<template>
  <div v-show="visible" class="element-renderer"
       :class="{
         'has-custom-styles': hasCustomStyles,
         'remove-text-field-border': removeTextFieldBorder,
         'element-renderer-fixed-width': isMessageTextWithRightActions
       }"
       :style="dynamicStyles"
       :data-field-name="elementApi.fieldName.value || elementApi.name.value">
    <label v-if="showLabel">{{ label }}</label>
    <div v-if="isSimpleLabel" class="element-renderer-label" :class="labelAlignClass">
      <span v-if="isHtmlLabel" v-html="labelHtmlContent"></span>
      <span v-else>{{ label }}</span>
    </div>
    <ElementStyleWrapper
      v-else-if="elementStyleWrapper.hasWrapper"
      :css-text="elementStyleWrapper.cssText"
      :before-html="elementStyleWrapper.beforeHtml"
      :after-html="elementStyleWrapper.afterHtml"
    >
      <component ref="componentRef" :is="currentComponent" v-bind="componentProps"
                 v-on="componentEvents" :key="tableKey"
                 @update:modelValue="handleInput" @enter="handleEnter" @change="handleChange"
                 @user-change="handleSelectionUserChange"
                 @update:selected="handleSelectionChange"
                 @blur="handleBlur" @focus="handleFocus"/>
    </ElementStyleWrapper>
    <component v-else ref="componentRef" :is="currentComponent" v-bind="componentProps"
               v-on="componentEvents" :key="tableKey"
               @update:modelValue="handleInput" @enter="handleEnter" @change="handleChange"
               @user-change="handleSelectionUserChange"
               @update:selected="handleSelectionChange"
               @blur="handleBlur" @focus="handleFocus"/>
    <AttachmentR7EditorDialog
      v-if="elementApi.type.value === 'FILEATTACHMENTS'"
      v-model:visible="r7EditorVisible"
      :title="r7EditorTitle"
      :placeholder-id="r7EditorPlaceholderId"
      :loading="r7EditorLoading"
      :error="r7EditorError"
      @shown="handleR7EditorDialogShown"
      @close="handleR7EditorDialogClose"
    />
  </div>
</template>

<script setup lang="ts">
import { getWebSocketService } from '@/services/websocket/websocket.service'
import {useActions} from '@/common/composables/useActions'
import {useElement} from '@/common/composables/useElement'
import {useListeners} from '@/common/composables/useListeners'
import {usePageData} from '@/common/composables/usePageData'
import {useFileAttachmentsElement} from '@/common/composables/useFileAttachmentsElement'
import {useAttachmentR7Editor} from '@/common/composables/useAttachmentR7Editor'
import type { FileItem } from '@/common/types/fileItem'
import { useR7InlinePreview } from '@/common/composables/useR7InlinePreview'
import {
  clearSessionsForElement,
  getEditingSession,
  markEditorClosedKeepStaged,
  syncEditingDocumentKeyForAttachmentItem,
} from '@/common/utils/r7AttachmentEditSession'
import { shouldDestroyR7EditorAfterFormSaveWait } from '@/common/utils/r7AttachmentFormSaveWait'
import {
  finalizeR7AttachmentEditorClose,
  markR7AttachmentDocumentDirty,
} from '@/common/utils/r7AttachmentEditorLifecycle'
import {
  type DsAttachmentPhase,
  getDsAttachmentStore,
  removeDsAttachmentStore,
  resolveAttachmentDocumentSessionId,
} from '@/common/composables/useDsAttachmentState'
import {
  collectFormActionsFromPageData,
  isAttachmentDownloadForbidden,
  isFileAttachmentsTapeMutationBlocked,
  parseFileAttachmentsElementMeta,
} from '@/common/utils/attachmentRpcPolicy'
import {
  isAttachmentTapeDebugEnabled,
  logAttachmentStoreDebug,
  summarizeAttachmentFieldEntry,
  summarizeAttachmentFileItems,
} from '@/common/utils/attachmentDebugLog'
import {
  markAttachmentStoreMutationCause,
  type AttachmentStoreMutationCause,
} from '@/common/utils/attachmentStoreMutationCause'
import { useNotification } from '@/common/composables/useNotification'
import { t } from '@/common/utils/translations'
import {useFormStates, usePageErrors, useAlerts} from '@/common/composables'
import {
  formLayoutHasFooter,
  resolveTableScrollHeight,
  resolveTreeTableFillAvailable,
} from '@/common/utils/resolveTableScrollHeight'
import {
  ElementContextKey,
  FormProviderKey,
  PlatformDialogLayoutKey,
  ReloadElementKey,
  GetFormKey,
  GetPageDataKey,
  RunCodeKey,
  OpenUrls,
  SwingTilesRowScopeKey,
} from '@/components/platform/injection-keys'
import {
  actionService,
  getActionDialogClosedScript,
  type ActionContext,
} from '@/services/action/action.service'
import {
  resolveActionLayoutLevelText,
  resolveActionLayoutPositionText,
  resolveActionLayoutTextId,
  shouldExecuteElementOnChangeOnClient,
} from '@/services/action/actionScriptRuntime'
import {
  resolveInlineActionCellConfig as resolveInlineActionCellConfigCore,
} from '@/common/utils/tableInlineActionCell'
import { parseListboxCatalogFromColumnMeta } from '@/common/utils/parseListboxCatalogFromColumnScript'
import { parseXmlElementColor } from '@/common/utils/parseXmlElementColor'
import { sanitizePlatformDisplayHtml } from '@/common/utils/html-sanitizer'
import { extractActionTextValue, normalizeActionImageName } from '@/services/action/normalizeActionTransfer'
import {
  getWebClientExecFromScriptNode,
} from '@/common/utils/webClientExec'
import { readRootFormFunctions } from '@/common/utils/formRootOnLoad'
import {
  clearDeferredFormVisibilityForActionIds,
  peekDeferredActionVisibility,
  restoreDeferredActionVisibility,
  snapshotDeferredActionVisibility,
} from '@/common/utils/deferredFormVisibility'
import {
  buildTableInlineActionVisibilityMap,
  collectInlineActionIds,
  resolveTableInlineActionVisible,
} from '@/common/utils/tableInlineMenuVisibility'
import { applyInlineMenuShowScriptVisibilities } from '@/common/utils/applyInlineMenuShowScriptVisibilities'
import {useAppStore} from '@/stores/app'
import { useActionRoleAccess } from '@/common/composables/useActionRoleAccess'
import { getPageIdentityAliases } from '@/common/utils/resolvePageIdentity'
import {
  getAttachmentUploadSessionOverride,
  getJsRequestSessionId,
  normalizePlatformSessionId,
} from '@/common/utils/jsRequestSessionContext'
import { findPageStoreEntryForForm } from '@/common/utils/resolvePageStoreEntry'
import {
  parseMultipleLimitFromXml,
  resolveElementMultipleLimit,
} from '@/common/utils/resolveElementMultipleLimit'
import { unwrapScriptValue } from '@/common/utils/wrapScriptValue'
import {
  shouldRunSelectionOnChangeAfterInput,
  shouldSkipDuplicateSelectionOnChange,
  shouldTreatOnChangeAsAlreadyRanViaInput,
} from '@/common/utils/elementOnChangeSelection'
import { collectTableLeafGetData, readElementWithCheckBoxes } from '@/common/utils/tableLeafGetData'
import { shouldCheckboxTableExportAllRowsForAction } from '@/services/action/checkboxTablePayloadPolicy'
import {
  isCollectingLiveFieldOverrides,
  isSelectionFormElementType,
  isTextLikeFormElementType,
  resolveTextInputStoreOptions,
} from '@/common/utils/liveFormFieldOverrides'
import { pickElementEventScript, pickElementEventScriptNode } from '@/common/utils/scriptNodeText'
import { resolveInvokeEventSelectedData } from '@/common/utils/resolveInvokeEventSelectedData'
import {
  fieldEntryHasPresentableValue,
  fieldEntryLooksEmptyIncoming,
} from '@/common/utils/changedDataFieldMerge'
import {
  shouldAllowTextFieldListenerEvent,
  shouldFireTextEditedOnInput,
} from '@/common/utils/textEditedListener'
import {computed, getCurrentInstance, inject, nextTick, onBeforeMount, onMounted, onUnmounted, ref, toRaw, watch} from 'vue'
import {storeToRefs} from 'pinia'
import {useDialog} from '@/common/composables/useDialog'
import {useRouter} from 'vue-router'
import api from '@/common/constants/api'
import {axios as axiosInstance} from '@/common/plugins/axios'
import {
  attachTreeTableNodeKey,
  canonicalTreeNodeId,
  TREE_TABLE_UNIQUE_EMPTY_SLOT,
  TREE_TABLE_NODE_KEY_FIELD,
  treeNodeIdsEqual,
} from '@/common/utils/treeTableIds'
import {
  resolveSwingReloadNodeTarget,
  buildFullTreeReloadPayload,
  buildTreeTableLazyExpandReloadPayload,
  buildTreeTableSearchStickyClearFields,
  extractTreeTableServerChildCount,
  selectionKeysIndicatesChecked,
  type FlatVisibleTreeRow,
} from '@/common/utils/treeTableReloadNodePayload'
import { buildTreeTableExpandParamsFields } from '@/common/utils/treeTableExpandParams'
import { isTreeTableSelectiveNode } from '@/common/utils/treeTableSelectiveNode'
import {
  buildFindParentRootReloadPayload,
  extractTreeTableParentRowsFromReload,
  isTreeTableGrowUpRootNode,
  wrapTreeTableBackingWithParent,
} from '@/common/utils/treeTableFindParent'
import {setType, setTypes} from '@/common/utils/typedValueSerializer'
import {extractUniDataTableRows} from '@/common/utils/extractUniDataTableRows'
import {
  buildTablePaginationEmptyPageFieldPatch,
  buildTablePaginationEmptyPageParamsPatch,
  buildTablePaginationReloadFields,
  isTablePaginationEmptyPageResponse,
  readReloadFieldScalar,
  resolveTablePaginationEmptyPageCorrection,
  resolveTablePaginationMetaFallback,
  tablePaginationFieldKeys,
  type TablePaginationEmptyPageClamp,
} from '@/common/utils/tablePaginationReload'
import {stripLeakedFormRows} from '@/common/utils/coalesceEmptyUniDataTableField'
import {
  normalizeMultiSelectFieldValue,
  toMultiSelectTypedValue,
  tryPromoteStringWrappedUniDataTable,
} from '@/common/utils/normalizeMultiselectTableValue'
import {formatItem, resolveUniDataRowKey, resolveUniDataRowLabel} from '@/components/ui/table/formatters'
import {
  ensureListboxSelectedOptionVisible,
  listboxOptionTextFromRow,
  listboxRowHasExplicitDescription,
} from '@/common/utils/listboxTableResolve'
import { reloadEmptyTableElementOnMount } from '@/common/utils/reloadEmptyTableElementOnMount'
import { parseApplyCurrentDateOnClick } from '@/common/utils/applyCurrentDateOnClick'
import { parsePageFieldToDate } from '@/common/utils/parsePageFieldToDate'
import { resolveDateTimePickerProps } from '@/common/utils/resolveDateTimePickerProps'
import { resolvePageFieldEntry } from '@/common/utils/pageFieldRegistryWrite'
import { resolveBooleanFieldValueFromPageFields } from '@/common/utils/resolveBooleanFieldValue'
import { coerceStateBoolean } from '@/common/utils/resolveActionPageState'
import {
  extractListboxScalarValue,
  isListboxEmptyValue,
  listboxFieldValuesEqual,
  normalizeListboxFieldValue,
  resolveListboxDisplayLabelFromPageFields,
  resolveListboxFieldValueFromPageFields,
} from '@/common/utils/resolveListboxFieldValue'
import {IKey} from '@/common/constants/ikey'
import { mergeTilesInlineSelectedData } from '@/common/utils/tilesInlineGetData'
import { resolveTilesCloneRowFieldRaw } from '@/common/utils/tilesRowPanelData'
import {
  applyPathMergeToTableField,
  buildTreeTableSearchCriteriaKey,
  buildTreeTableSearchWatchToken,
  commitTreeTableSearchSession,
  createTreeTableSearchSessionState,
  extractTreeTableSearchResult,
  isEmptyTreeTableSearchCriteriaKey,
  isTreeTableSearchExchange,
  reconcileTreeTableSearchResult,
  resetTreeTableSearchSessionState,
  resolveTreeTableSearchNotFoundMessage,
  shouldIgnoreTreeTableSearchResponse,
  signatureFromTreeTableSearchResult,
} from '@/common/utils/treeTableSearch'
import type { TreeTableSearchExtracted } from '@/common/utils/treeTableSearch'
import {
  buildTreeTableUniData,
  mergeTreeTableExportColumns,
  resolveTreeTableExpandByDefault,
} from '@/common/utils/treeTableGetData'
import { evaluateTableColumnCondition } from '@/common/utils/evaluateTableColumnCondition'
import {
  getTreeTableSearchSubmittedCriteria,
  hasPendingTreeTableSearchApply,
  markTreeTableSearchApplyConsumed,
  resetTreeTableSearchApplyState,
} from '@/common/utils/treeTableSearchSubmitted'
import {
  applyTreeTablePlatformRowFlags,
  mergePathRowsIntoBackingTree,
  pathRowsToNodeKeys,
  resolvePathKeysFromTreeItems,
} from '@/common/utils/treeTableRowKeys'
import {
  findTreeTableAncestorChain,
  resolveTreeTableContextItem as resolveTreeTableContextItemByIdentity,
  resolveTreeTableContextSourceRow as resolveTreeTableContextSourceRowByIdentity,
  resolveTreeTableParentIdValue,
} from '@/common/utils/treeTableContextResolve'
import {messagesService} from '@/services/messages/messages.service'
import {
  getSupportedEventNamesForType,
  resolveRegisteredEvents
} from '@/components/platform/events/event-registry'
import ElementStyleWrapper from '@/components/platform/elements/ElementStyleWrapper.vue'
import { resolveElementStyleWrapper, hasElementCustomStyle } from '@/common/utils/elementStyleWrapper'

// Импортируем UI компоненты
import MApproval from '@/components/ui/MApproval.vue'
import MMultiSelect from '@/components/ui/MMultiSelectAutoComplete.vue'
import MCheckbox from '@/components/ui/MCheckbox.vue'
import MDatePicker from '@/components/ui/MDatePicker.vue'
import MDateRange from '@/components/ui/MDateRange.vue'
import MDirectory from '@/components/ui/MDirectory.vue'
import MDownloadTask from '@/components/ui/MDownloadTask.vue'
import MFileDownload from '@/components/ui/MFileDownload.vue'
import MFileInput from '@/components/ui/MFileInput.vue'
import AttachmentR7EditorDialog from '@/components/ui/AttachmentR7EditorDialog.vue'
import {
  ATTACHMENT_DOCUMENT_SESSION_ID,
  buildPlatformDownloadPageFieldPatch,
  fileItemsToUniDataTable,
  findMatchingAttachmentItem,
  getAttachmentStorageUri,
  getScalarFromFields,
  mergeAttachmentsUniDataTables,
  rowAttachmentKey,
  rowHasServerCode,
  uniDataTableToFileItems,
  type PlatformDownloadMeta,
} from '@/common/utils/fileAttachmentsUniData'
import {
  getAttachmentNativeFileStore,
  hydrateAttachmentFileItemsFromStore,
  removeAttachmentNativeFileStore,
} from '@/common/utils/attachmentNativeFileRegistry'
import {
  getPlatformFileSessionContext,
  readPlatformSessionIdFromPageEntry,
} from '@/common/utils/platformFileSession'
import { applyPlatformDownloadMeta, fetchPlatformAttachmentDownload } from '@/common/utils/platformDownload'
import MFileUpload from '@/components/ui/MFileUpload.vue'
import MHTML from '@/components/ui/MHTML.vue'
import MIdentificator from '@/components/ui/MIdentificator.vue'
import MInput from '@/components/ui/MInput.vue'
import MMediaWall from '@/components/ui/MMediaWall.vue'
import MProgressBar from '@/components/ui/MProgressBar.vue'
import MSelector from '@/components/ui/MSelector.vue'
import MSlider from '@/components/ui/MSlider.vue'
import MStringRange from '@/components/ui/MStringRange.vue'
import MSVG from '@/components/ui/MSVG.vue'
import MTasks from '@/components/ui/MTasks.vue'
import MTextArea from '@/components/ui/MTextArea.vue'
import MTiles from '@/components/ui/MTiles.vue'
import MVideoPlayer from '@/components/ui/MVideoPlayer.vue'
import {MGroupTable, MTable, MTreeTable} from '@/components/ui/table'
import {useSettingsStore} from '@/stores'
import type {ElementSettingsParams} from '@/stores/settings'
import { profilePayload, startWorkflowMeasure } from '@/common/utils/performanceTrace'
import {
  countTreeTableNestedBranches,
  resolveTreeTableBackingRows,
} from '@/common/utils/resolveTreeTableBackingRows'

interface Props {
  config: any
  formPath: any
  readonly?: boolean
  selected?: any[]
  events?: any
  hideLabel?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  input: [data: any]
  change: [data: any]
  'update:selected': [selected: any[]]
  enter: [value: string | number]
}>()

interface RendererSetupStats {
  scheduled: boolean
  count: number
  byType: Record<string, number>
  durationByType: Record<string, number>
  firstAt: number
  lastAt: number
  lastType: string
}

function getRendererSetupStats(): RendererSetupStats {
  const key = '__workflowElementRendererSetupStats'
  const root = globalThis as typeof globalThis & Record<string, RendererSetupStats | undefined>
  if (!root[key]) {
    root[key] = {
      scheduled: false,
      count: 0,
      byType: {},
      durationByType: {},
      firstAt: 0,
      lastAt: 0,
      lastType: '',
    }
  }
  return root[key]!
}

function resetRendererSetupStats(stats: RendererSetupStats): void {
  stats.scheduled = false
  stats.count = 0
  stats.byType = {}
  stats.durationByType = {}
  stats.firstAt = 0
  stats.lastAt = 0
  stats.lastType = ''
}

const rendererSetupStats = getRendererSetupStats()

function recordRendererSetup(type: string): void {
  const now = typeof performance !== 'undefined' ? performance.now() : Date.now()
  if (rendererSetupStats.lastAt && rendererSetupStats.lastType) {
    rendererSetupStats.durationByType[rendererSetupStats.lastType] =
      (rendererSetupStats.durationByType[rendererSetupStats.lastType] ?? 0) +
      (now - rendererSetupStats.lastAt)
  }
  rendererSetupStats.count += 1
  rendererSetupStats.byType[type || 'UNKNOWN'] = (rendererSetupStats.byType[type || 'UNKNOWN'] ?? 0) + 1
  if (!rendererSetupStats.firstAt) {
    rendererSetupStats.firstAt = now
  }
  rendererSetupStats.lastAt = now
  rendererSetupStats.lastType = type || 'UNKNOWN'

  if (rendererSetupStats.scheduled) return
  rendererSetupStats.scheduled = true
  setTimeout(() => {
    const flushAt = typeof performance !== 'undefined' ? performance.now() : Date.now()
    const setupSpanMs = rendererSetupStats.lastAt - rendererSetupStats.firstAt
    const postSetupFlushDelayMs = flushAt - rendererSetupStats.lastAt
    startWorkflowMeasure('ElementRenderer.setup.batch', {
      count: rendererSetupStats.count,
      byType: { ...rendererSetupStats.byType },
      durationByType: Object.fromEntries(
        Object.entries(rendererSetupStats.durationByType).map(([key, value]) => [
          key,
          Math.round(value * 10) / 10,
        ]),
      ),
      setupSpanMs: Math.round(setupSpanMs * 10) / 10,
      postSetupFlushDelayMs: Math.round(postSetupFlushDelayMs * 10) / 10,
      spanMs: Math.round((flushAt - rendererSetupStats.firstAt) * 10) / 10,
    })()
    resetRendererSetupStats(rendererSetupStats)
  }, 0)
}

const elementApi = useElement(props.config, props.formPath, props.readonly)
recordRendererSetup(elementApi.type.value)
const {pageFields, pageData, pageDataSlot, pageRegistryFormId, pageWindowInstanceId, pageActions} = usePageData()
const {invokeListeners, hasListeners} = useListeners()
const actions = useActions()
const appStore = useAppStore()
const { hasActionRoleAccess } = useActionRoleAccess()
const {currentPlatform} = storeToRefs(appStore)
const elementContext = inject(ElementContextKey, null)
const dialog = useDialog()
const router = useRouter()
const reloadElement = inject(ReloadElementKey, null)
const formProvider = inject(FormProviderKey, null)
const platformDialogLayout = inject(PlatformDialogLayoutKey, null)
const formHasFooter = computed(() => formLayoutHasFooter((pageData.value as any)?.form))
const tableScrollHeight = computed(() =>
  resolveTableScrollHeight({
    element: props.config?.element,
    dialogLayout: platformDialogLayout,
    hasFormFooter: formHasFooter.value,
  }),
)
const treeTableFillAvailable = computed(() =>
  resolveTreeTableFillAvailable({
    element: props.config?.element,
    dialogLayout: platformDialogLayout,
    hasFormFooter: formHasFooter.value,
  }),
)
const swingTilesRowScope = inject(SwingTilesRowScopeKey, null)
const formStates = useFormStates()
const {getElementStyle, generateCSSProperties} = formStates
const pageErrors = usePageErrors()
const alerts = useAlerts()
const getForm = inject<({formId, panelId, elementId}: any) => any>(GetFormKey, () => ({}))
const getPageData = inject<({formId, pageId}: any) => any>(GetPageDataKey, () => ({}))
const openUrls = inject(OpenUrls, () => {
})
const runCode = inject<(cmd: string, selectedData?: any) => void>(RunCodeKey, () => {
})

onMounted(() => {
  const type = elementApi.type.value

  if (
    type === 'TABLE' ||
    type === 'EDITABLE' ||
    type === 'TREETABLE' ||
    type === 'GROUPTABLE' ||
    type === 'MULTISELECT' ||
    type === 'COMBOBOX' ||
    type === 'LISTBOX'
  ) {
    startWorkflowMeasure('ElementRenderer.onMounted', {
      fieldName: elementApi.fieldName.value,
      elementId: elementId.value,
      elementType: type,
      formId: (elementContext as any)?.formId ?? props.formPath?.formId,
      panelId: (elementContext as any)?.panelId ?? props.formPath?.panelId,
    })()
  }
})
const {getElementSetting, setElementSetting} = useSettingsStore()

const componentRef = ref<any>(null)
const lastUserInteractionAt = ref(0)
const USER_CHANGE_WINDOW_MS = 2000

const markUserInteraction = () => {
  lastUserInteractionAt.value = Date.now()
}

const hasRecentUserInteraction = () => Date.now() - lastUserInteractionAt.value <= USER_CHANGE_WINDOW_MS
const textLikeValueAtFocus = ref<unknown>(undefined)
const textLikeDirtySinceFocus = ref(false)
/** Локальный буфер на время ввода — не даёт controlled MInput сбросить символ при skipStore + рассинхроне forms slot. */
const textLikeEditingValue = ref<string | number | null>(null)

const isTruthyFlag = (value: any): boolean => {
  if (value === true || value === 1) return true
  if (value === false || value === 0) return false
  if (value == null) return false
  const text = String(value).trim().toLowerCase()
  return text === 'true' || text === '1' || text === 'yes' || text === 'y'
}

const focusElement = () => {
  if (!componentRef.value) return

  if (typeof componentRef.value.focus === 'function') {
    componentRef.value.focus()
    return
  }

  const el = componentRef.value.$el || componentRef.value
  if (el instanceof HTMLElement) {
    const focusable = el.querySelector('input, textarea, select') as HTMLElement
    if (focusable) {
      focusable.focus()
      return
    }
    if (el.focus) {
      el.focus()
    }
  }
}

const scrollIntoViewElement = () => {
  if (!componentRef.value) return

  const el = componentRef.value.$el || componentRef.value

  if (el instanceof HTMLElement) {
    el.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    })
  }
}

const elementId = computed(() => {
  const id = elementApi.element.value?._attributes?.id
  return id ? parseInt(id, 10) : null
})
const visible = computed(() => {
  const shellMap = formProvider?.elementShellVisibleByElementId?.value
  const shellVisible =
    elementId.value != null ? shellMap?.[String(elementId.value)] : undefined
  if (typeof shellVisible === 'boolean') {
    return shellVisible
  }
  return elementApi.isVisible.value
})
const focused = computed(() => elementApi.focused.value)

const label = computed(() => elementApi.label.value)

const elementStyleWrapper = computed(() =>
  resolveElementStyleWrapper(props.config.element),
)

const elementStyleData = computed(() => {
  if (!elementId.value) return undefined
  return getElementStyle('ELEMENT', elementId.value)
})

const staticElementFontSize = computed(() => {
  const rawFont = props.config.element?.font?._text
  if (rawFont == null || rawFont === '') return undefined

  const fontSize = Number(rawFont)
  if (!Number.isFinite(fontSize) || fontSize <= 0) return undefined

  return `${fontSize}px`
})

/** XML `<color>` when middle style store has no text_font color (e.g. form 2627 warnings). */
const staticElementColor = computed(() => parseXmlElementColor(props.config.element?.color))

const staticElementFontStyle = computed(() => {
  const rawFontStyle = String(props.config.element?.FontStyle?._text || '')
    .trim()
    .toUpperCase()

  if (!rawFontStyle || rawFontStyle === 'PLAIN') return {}

  const styles: Record<string, string> = {}

  if (rawFontStyle.includes('BOLD')) {
    styles.fontWeight = 'bold'
  }

  if (rawFontStyle.includes('ITALIC')) {
    styles.fontStyle = 'italic'
  }

  return styles
})

function parseXmlElementWidthPx(raw: unknown): number | null {
  const n = parseInt(String(raw ?? ''), 10)
  if (!Number.isFinite(n) || n <= 0) return null
  return n
}

const xmlTextFieldWidthPx = computed(() => {
  const t = elementApi.type.value
  if (t !== 'TEXT' && t !== 'PASSWORD') return null
  return parseXmlElementWidthPx(props.config.element?.width?._text)
})

const dynamicStyles = computed(() => {
  const styles: Record<string, string> = {}
  let textWidthPx = xmlTextFieldWidthPx.value
  const fieldName = String(elementApi.fieldName.value || elementApi.name.value || '')

  // Для поля messageText увеличиваем ширину в 2 раза
  if (fieldName === 'messageText' && textWidthPx) {
    textWidthPx = textWidthPx * 2
  }

  if (textWidthPx) {
    styles.width = '100%'
    styles.boxSizing = 'border-box'
    // Linked right-action (Swing): field fills remaining width so button attaches — skip XML maxWidth.
    // Для messageText maxWidth применяется на уровне MInput wrapper'а
    if (rightActions.value.length === 0 && fieldName !== 'messageText') {
      styles.maxWidth = `${textWidthPx}px`
    }
  }

  if (
    !elementStyleData.value &&
    !staticElementFontSize.value &&
    !staticElementColor.value &&
    Object.keys(staticElementFontStyle.value).length === 0
  ) {
    return styles
  }

  const cssProps = generateCSSProperties(elementStyleData.value)

  if (cssProps.fontFamily) styles['--element-font-family'] = cssProps.fontFamily
  if (cssProps.fontSize) {
    styles['--element-font-size'] = cssProps.fontSize
  } else if (staticElementFontSize.value) {
    styles['--element-font-size'] = staticElementFontSize.value
  }
  if (cssProps.fontWeight) {
    styles['--element-font-weight'] = cssProps.fontWeight
  } else if (staticElementFontStyle.value.fontWeight) {
    styles['--element-font-weight'] = staticElementFontStyle.value.fontWeight
  }
  if (cssProps.fontStyle) {
    styles['--element-font-style'] = cssProps.fontStyle
  } else if (staticElementFontStyle.value.fontStyle) {
    styles['--element-font-style'] = staticElementFontStyle.value.fontStyle
  }
  if (cssProps.color) {
    styles['--element-color'] = cssProps.color
  } else if (staticElementColor.value) {
    styles['--element-color'] = staticElementColor.value
  }
  if (cssProps.backgroundColor) styles['--element-bg-color'] = cssProps.backgroundColor

  return styles
})

const hasCustomStyles = computed(() => {
  return (
    elementStyleData.value !== undefined ||
    staticElementFontSize.value !== undefined ||
    staticElementColor.value !== undefined ||
    Object.keys(staticElementFontStyle.value).length > 0
  )
})

const removeTextFieldBorder = computed(() => {
  return elementApi.borderRemoved?.value === true
})

watch(focused, (newVal) => {
  if (!!newVal) {
    focusElement()
  }
})

watch(() => elementApi.cursorToEnd?.value, (newVal) => {
  if (!newVal || !componentRef.value) return
  const el = componentRef.value.$el || componentRef.value
  if (!(el instanceof HTMLElement)) return
  const input = el.querySelector('input, textarea') as HTMLInputElement | HTMLTextAreaElement | null
  if (input) {
    input.focus()
    const len = input.value?.length ?? 0
    input.setSelectionRange(len, len)
  }
  elementApi.cursorToEnd!.value = false
})

// Loading state for table reloads
const isTableReloading = ref(false)
/** После пустой страницы пагинации — удерживаем скорректированный total/page до смены maxOnPage. */
const tablePaginationEmptyPageClamp = ref<TablePaginationEmptyPageClamp | null>(null)
/** Инкремент при rollback пустой страницы — remount paginator/table meta. */
const tablePaginationUiEpoch = ref(0)
/** Защита от повторного reload при двойном клике по раскрытию TreeTable */
const treeTableExpandInFlightKey = ref<string | null>(null)
const lastTreeTableSearchSignature = ref('')
const treeTableSearchApplyInFlight = ref(false)
/** После found/refill/remount (tableKey) — повторно выделить строку поиска. */
const pendingTreeTableSearchTargetKey = ref<string | null>(null)
/** Path из последнего found — повторно вмержить после is_refill, который затирает registry. */
const lastTreeTableSearchPathRows = ref<unknown[]>([])
const treeTableSearchSession = createTreeTableSearchSessionState()
let treeTableSearchRefocusTicket = 0
let pendingTreeTableSearchClearTimer: ReturnType<typeof setTimeout> | null = null
const pendingTreeTableSearchTargetCriteriaKey = ref<string>('')

function schedulePendingTreeTableSearchFocusClear(delayMs = 450): void {
  if (pendingTreeTableSearchClearTimer) {
    clearTimeout(pendingTreeTableSearchClearTimer)
  }
  pendingTreeTableSearchClearTimer = setTimeout(() => {
    pendingTreeTableSearchClearTimer = null
    pendingTreeTableSearchTargetKey.value = null
  }, delayMs)
}

const columnConditionVisibility = ref<Record<string, boolean>>({})

// Reactive key for table components to force re-render on data changes
const tableKey = computed(() => {
  const elementType = elementApi.type.value
  if (elementType === 'DATE' || elementType === 'DATETIME') {
    const fieldName = elementApi.fieldName.value
    // Стабильный key: смена значения не должна remount'ить picker (иначе календарь закрывается).
    // Внешние обновления — через syncDateTimeValueFromPageFields.
    return `DATE-${fieldName}`
  }
  if (elementType === 'TILES') {
    const fieldName = elementApi.fieldName.value
    return `TILES-${fieldName}`
  }
  if (
    elementType === 'TABLE' ||
    elementType === 'EDITABLE' ||
    elementType === 'TREETABLE' ||
    elementType === 'GROUPTABLE'
  ) {
    // Access pageFields to track changes - this ensures reactivity
    const fields = pageFields.value
    const fieldName = elementApi.fieldName.value
    const tableData = (fields as any)?.[fieldName]

    const rowsData = tableData?.value?.rows || tableData?.rows
    const rowsCount = Array.isArray(rowsData) ? rowsData.length : 0

    const meta = getTableMeta()

    // Track a simple identifier from first row to detect content changes
    const firstRowId =
      rowsCount > 0 && rowsData?.[0]
        ? rowsData[0]._id?._text ||
        rowsData[0]._id?.value ||
        rowsData[0].id ||
        rowsData[0].fields?._id?.value ||
        'row0'
        : 'empty'

    const columnVisKey = Object.entries(columnConditionVisibility.value)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, visible]) => `${name}:${visible ? 1 : 0}`)
      .join(',')

    return `${elementType}-${fieldName}-${rowsCount}-${meta.page}-${meta.itemsPerPage}-${meta.pages}-${tablePaginationUiEpoch.value}-${firstRowId}-${columnVisKey}-${isTableReloading.value ? 'loading' : 'ready'}`
  }
  return `${elementApi.type.value}-${elementApi.fieldName.value}`
})

// Store selected row data for context menu actions
const selectedRowData = ref<any>(null)
// Store table context selectedIdx/column when context menu opens
const lastTableContextSelectedIdx = ref<number>(-1)
const lastTableContextColumn = ref<string>('')
type TableContextMenuSnapshot = {
  selectedIdx: number
  column: string
  rowData: Record<string, any>
}
const tableContextMenuSnapshot = ref<TableContextMenuSnapshot | null>(null)
/** Visibility inline-action из onInlineMenuShow (deferred / setVisible без mount MAction). */
const tableInlineActionVisibility = ref<Record<string, boolean>>({})

const resolveTableContextColumn = (event?: any) => {
  const column = event?.column
  return column !== undefined && column !== null && column !== '' ? column : lastTableContextColumn.value
}

// Список вариантов автодополнения (справочник) — получаем при фокусе на поле с autoComplete
const autoCompleteSuggestions = ref<any[]>([])
/** После openSuggester не перезапрашивать список в handleFocus (onClick + forced:false затирает подсказки). */
const openSuggesterSuppressFocusFetch = ref(false)
/** Открыть dropdown, когда suggestions придут после server openSuggester (onClick). */
const openSuggesterPending = ref(false)

const isOnClickAutocomplete = computed(() => {
  const mode = String(effectiveAutocompleteText.value ?? '').trim().toLowerCase()
  return mode === 'onclick' || mode.includes('onclick')
})

const autocompleteRowHasLabel = (row: unknown): boolean =>
  String(listboxOptionTextFromRow(row) ?? '').trim() !== ''

// Режим автодополнения
const effectiveAutocompleteText = computed(() => {
  const fromServer = elementApi.autocompleteModeOverride.value
  if (fromServer != null && String(fromServer).trim() !== '') {
    return String(fromServer)
  }
  return props.config.element?.autoComplete?._text != null
    ? String(props.config.element.autoComplete._text)
    : ''
})

const resolveAutocompleteForced = (filter: string): boolean => {
  const mode = String(effectiveAutocompleteText.value ?? '').trim().toLowerCase()
  if (mode === 'onclick' || mode.includes('onclick')) return true
  return !filter.trim()
}

// Поле с автодополнением
const isAutoCompleteField = computed(() => {
  if (elementApi.type.value !== 'TEXT') return false
  const autoComplete = effectiveAutocompleteText.value
  return autoComplete != null && String(autoComplete).trim() !== '' && String(autoComplete).toLowerCase() !== 'no'
})

// Internal state for table selection
const internalSelected = ref<any[]>(props.selected || [])

// Маппинг типов элементов на компоненты
const componentMap = {
  TEXT: MInput,
  PASSWORD: MInput,
  TEXTAREA: MTextArea,
  MULTILINETEXT: MTextArea,
  LISTBOX: MSelector,
  COMBOBOX: MSelector,
  BOOLEAN: MCheckbox,
  CHECKBOX: MCheckbox,
  DATETIME: MDatePicker,
  DATE: MDatePicker,
  DATERANGE: MDateRange,
  TABLE: MTable,
  EDITABLE: MTable,
  TREETABLE: MTreeTable,
  FILEATTACHMENTS: MFileInput,
  MULTISELECT: MMultiSelect,
  LINEDELIMITER: 'div',
  SLIDER: MSlider,
  PROGRESSBAR: MProgressBar,
  IDENTIFICATOR: MIdentificator,
  STRINGRANGE: MStringRange,
  HTML: MHTML,
  DIRECTORY: MDirectory,
  FILEUPLOAD: MFileUpload,
  FILEDOWNLOAD: MFileDownload,
  EXTFILEUPLOAD: MFileUpload,
  EXTFILEDOWNLOAD: MFileDownload,
  TILES: MTiles,
  SVG: MSVG,
  GROUPTABLE: MGroupTable,
  TASKS: MTasks,
  APPROVAL: MApproval,
  MEDIAWALL: MMediaWall,
  VIDEOPLAYER: MVideoPlayer,
  DOWNLOADTASK: MDownloadTask,
}

// Текущий компонент
const currentComponent = computed(() => {
  const type = elementApi.type.value
  return componentMap[type as keyof typeof componentMap] || 'div'
})

// исключить отображение подписи у самой подписи
const showLabel = computed(() => {
  if (props.hideLabel) return false
  return elementApi.type.value != 'LABEL' ? true : false
})

const isSimpleLabel = computed(() => elementApi.type.value === 'LABEL')

const labelAlign = computed(() => props.config.element?.align?._text || 'left')

const labelAlignClass = computed(() => `element-renderer-label--align-${labelAlign.value}`)

const isHtmlLabel = computed(() => {
  const value = label.value
  if (!value || typeof value !== 'string') return false
  const trimmed = value.trim()
  return trimmed.toLowerCase().startsWith('<html>') || trimmed.toLowerCase().startsWith('<html ')
})

const labelHtmlContent = computed(() => {
  const value = label.value
  if (!isHtmlLabel.value || typeof value !== 'string') return ''
  return sanitizePlatformDisplayHtml(value)
})

// Cache for transformed mediawall data to prevent infinite loops
const transformedMediaWallCache = ref<any[]>([])
let lastRawMediaWallValueJSON: string = ''

// Helper function to extract field value from UniData field structure
const getFieldValue = (field: any): any => {
  if (!field) return ''
  if (typeof field === 'object' && 'value' in field) {
    return field.value
  }
  return field
}

const getPageFieldValue = (fieldName: string): any => {
  const field = (pageFields.value as any)?.[fieldName]
  if (field && typeof field === 'object' && 'value' in field) {
    const extractedValue = field.value
    if (extractedValue && typeof extractedValue === 'object' && 'value' in extractedValue) {
      return extractedValue.value
    }
    return extractedValue
  }
  return field
}

const getFileDownloadMeta = () => {
  const fieldName = elementApi.fieldName.value
  const rawValue = getPageFieldValue(fieldName)
  const allPageFieldKeys = Object.keys(pageFields.value || {})

  const readBySuffix = (suffixes: string[]) => {
    for (const suffix of suffixes) {
      const value = getPageFieldValue(`${fieldName}${suffix}`)
      if (value !== undefined && value !== null && value !== '') return value
    }
    return undefined
  }

  const readByKeyMatch = (patterns: string[]) => {
    for (const key of allPageFieldKeys) {
      if (!key.startsWith(`${fieldName}__`)) continue
      const keyLower = key.toLowerCase()
      if (patterns.some((pattern) => keyLower.includes(pattern))) {
        const value = getPageFieldValue(key)
        if (value !== undefined && value !== null && value !== '') return value
      }
    }
    return undefined
  }

  let fileName = readBySuffix(['__name', '__fileName', '__filename'])
  let fileType = readBySuffix(['__type', '__fileType'])
  let size = readBySuffix(['__size'])
  let storageUri = readBySuffix(['__storage_uri', '__storageUri'])
  let documentSessionId = readBySuffix(['__documentSessionId'])
  let code = readBySuffix(['__key', '__code', '__id', '__fileId'])
  let version = readBySuffix([
    '__version',
    '__ver',
    '__fileVersion',
    '__fileVersionId',
    '__versionId',
  ])

  if (!code) {
    code = readByKeyMatch(['__key', 'code', 'fileid', 'file_id'])
  }
  if (!version) {
    version = readByKeyMatch(['version', 'ver', 'fileversion', 'file_version'])
  }
  if (!fileName) {
    fileName = readByKeyMatch(['name', 'filename', 'file_name'])
  }
  if (!storageUri) {
    storageUri = readByKeyMatch(['storage', 'storage_uri', 'storageuri'])
  }
  if (!documentSessionId) {
    documentSessionId = readByKeyMatch(['documentsessionid', 'document_session_id'])
  }

  if (rawValue && typeof rawValue === 'object' && !Array.isArray(rawValue)) {
    fileName = fileName ?? rawValue.fileName ?? rawValue.name
    fileType = fileType ?? rawValue.fileType ?? rawValue.type
    size = size ?? rawValue.size
    storageUri = storageUri ?? rawValue.storageUri ?? rawValue.storage_uri
    code = code ?? rawValue.code ?? rawValue.key ?? rawValue.id ?? rawValue._key
    version =
      version ?? rawValue.version ?? rawValue.ver ?? rawValue.fileVersionId ?? rawValue.versionId
    if (!code && (rawValue as any).fields) {
      const fields = (rawValue as any).fields
      code = fields.code?.value ?? fields._id?.value ?? fields.id?.value ?? code
      version = fields.version?.value ?? fields.ver?.value ?? version
      fileName = fields.name?.value ?? fields.fileName?.value ?? fileName
      storageUri = fields.storageUri?.value ?? fields.storage_uri?.value ?? storageUri
      documentSessionId =
        fields.documentSessionId?.value ?? fields.__documentSessionId?.value ?? documentSessionId
    }
  } else if (rawValue !== undefined && rawValue !== null && rawValue !== '') {
    code = code ?? rawValue
  }

  if (!fileName && rawValue !== undefined && rawValue !== null && rawValue !== '') {
    fileName = String(rawValue)
  }

  return {fileName, fileType, size, storageUri, code, version, documentSessionId}
}

const getFileDownloadModelValue = () => {
  const meta = getFileDownloadMeta()
  if (!meta.fileName && (meta.code === undefined || meta.code === null || meta.code === '')) {
    return null
  }
  return {fileName: meta.fileName || String(meta.code ?? '')}
}

const isValidSessionId = (value: any) => normalizePlatformSessionId(value) != null

const normalizeSessionId = (value: any) => normalizePlatformSessionId(value)

const unwrapRef = (value: any) => {
  if (value && typeof value === 'object' && 'value' in value) {
    return (value as any).value
  }
  return value
}

/** Как normalizeContext в ActionService: примитив или узел с _text из метаданных action. */
const pickXmlActionScalar = (v: unknown): string | undefined => {
  if (v === undefined || v === null) return undefined
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
    return String(v)
  }
  if (typeof v === 'object' && v !== null && '_text' in (v as object)) {
    const t = (v as { _text?: unknown })._text
    if (t === undefined || t === null) return undefined
    return String(t)
  }
  return undefined
}

const findStorePageDataByLogicalFormId = (logicalFormId: string | number | null | undefined) => {
  if (logicalFormId == null || logicalFormId === '') return null
  const targetFormId = String(logicalFormId)
  const pd = appStore.pagesData as Record<string, any>
  const direct = pd[targetFormId] ?? pd[logicalFormId as any]
  if (direct && typeof direct === 'object') {
    return direct
  }

  for (const [storeKey, entry] of Object.entries(pd)) {
    if (!entry || typeof entry !== 'object') continue
    const aliases = getPageIdentityAliases(entry as Record<string, any>, { slotId: storeKey })
    if (aliases.includes(targetFormId)) {
      return entry
    }
  }

  return null
}

const findPageDataByFormId = () => {
  const targetFormId = String(
    props.formPath?.formId ??
      pageRegistryFormId.value ??
      (elementContext as { formId?: string })?.formId ??
      '',
  ).trim()
  if (!targetFormId) return null
  return findStorePageDataByLogicalFormId(targetFormId)
}

/**
 * Получение sessionId (coreId)
 * TODO: нужно формализовать логику получения и хранения coreId-sessionId (док)
 */
const getSessionId = () => {
  const uploadOverride = getAttachmentUploadSessionOverride()
  if (uploadOverride) return uploadOverride

  const wsSession = normalizePlatformSessionId(getJsRequestSessionId())
  if (wsSession) return wsSession

  const registeredName = String(unwrapRef(elementApi.fieldName) ?? '').trim()
  let registeredFormId: string | undefined
  if (registeredName && formProvider?.getElements) {
    const reg = formProvider.getElements()?.[registeredName] as
      | { platformSessionId?: unknown; formId?: unknown }
      | undefined
    const fromRegistry = normalizePlatformSessionId(reg?.platformSessionId)
    if (fromRegistry) return fromRegistry
    const fid = String(unwrapRef(reg?.formId) ?? '').trim()
    if (fid) registeredFormId = fid
  }

  const formIdForLookup =
    registeredFormId ||
    String(unwrapRef(props.formPath?.formId) ?? '').trim() ||
    String(unwrapRef(pageRegistryFormId.value) ?? '').trim()
  if (formIdForLookup && appStore.sessionIdByFormId) {
    const fromFormId = normalizePlatformSessionId(appStore.sessionIdByFormId(formIdForLookup))
    if (fromFormId) return fromFormId
  }

  const candidateStoreKeys = [
    props.formPath?.formId,
    pageDataSlot.value,
    pageRegistryFormId.value,
    (elementContext as any)?.formId,
  ]
    .map((value) => String(unwrapRef(value) ?? '').trim())
    .filter(Boolean)

  const readSessionFromPageEntry = (entry: unknown): string | null => {
    const fromHelper = readPlatformSessionIdFromPageEntry(entry)
    if (fromHelper) return fromHelper
    if (!entry || typeof entry !== 'object') return null
    const sid = unwrapRef((entry as any).sessionId ?? (entry as any).$sessionId)
    if (isValidSessionId(sid)) return normalizeSessionId(sid)
    const clone = unwrapRef((entry as any).cloneOf ?? (entry as any).$cloneOf)
    if (isValidSessionId(clone)) return normalizeSessionId(clone)
    return null
  }

  // Сначала — store формы элемента (важно при WS getFullData во время executeScript на middleware).
  const pageByForm = findPageDataByFormId()
  const sessionFromForm = readSessionFromPageEntry(pageByForm)
  if (sessionFromForm) return sessionFromForm

  for (const key of candidateStoreKeys) {
    const sessionFromAlias = readSessionFromPageEntry(findStorePageDataByLogicalFormId(key))
    if (sessionFromAlias) return sessionFromAlias
  }

  const primary = unwrapRef((pageData.value as any)?.sessionId)
  if (isValidSessionId(primary)) return normalizeSessionId(primary)

  const fallbackCloneOf = unwrapRef((pageData.value as any)?.cloneOf)
  if (isValidSessionId(fallbackCloneOf)) return normalizeSessionId(fallbackCloneOf)

  const currentStoreSession = unwrapRef((appStore as any).currentSessionId)
  if (isValidSessionId(currentStoreSession)) return normalizeSessionId(currentStoreSession)

  for (const key of candidateStoreKeys) {
    const formSession = appStore.sessionIdByFormId?.(key)
    if (isValidSessionId(formSession)) return normalizeSessionId(formSession)

    for (const [, entry] of Object.entries(appStore.pagesData as Record<string, unknown>)) {
      if (!entry || typeof entry !== 'object') continue
      const aliases = getPageIdentityAliases(entry as Record<string, unknown>, {})
      if (!aliases.includes(key)) continue
      const sessionFromScan = readSessionFromPageEntry(entry)
      if (sessionFromScan) return sessionFromScan
    }

    const storeEntry = (appStore.pagesData as any)?.[key]
    const appSessionId = unwrapRef(storeEntry?.sessionId)
    if (isValidSessionId(appSessionId)) return normalizeSessionId(appSessionId)

    const appCloneOf = unwrapRef(storeEntry?.cloneOf)
    if (isValidSessionId(appCloneOf)) return normalizeSessionId(appCloneOf)

    const dialogEntry = (appStore.dialogs as Record<string, any> | undefined)?.[key]
    const dialogSessionId = unwrapRef(dialogEntry?.sessionId)
    if (isValidSessionId(dialogSessionId)) return normalizeSessionId(dialogSessionId)
  }

  const currentPageId = unwrapRef((appStore as any).currentPageId)
  const currentPageSession = unwrapRef((appStore.pagesData[currentPageId] as any)?.sessionId)
  if (isValidSessionId(currentPageSession)) return normalizeSessionId(currentPageSession)

  for (const key of candidateStoreKeys) {
    const pageDataByKey = findStorePageDataByLogicalFormId(key)
    const sessionFromPageData = unwrapRef((pageDataByKey as any)?.sessionId)
    if (isValidSessionId(sessionFromPageData)) return normalizeSessionId(sessionFromPageData)

    const cloneOfFromPageData = unwrapRef((pageDataByKey as any)?.cloneOf)
    if (isValidSessionId(cloneOfFromPageData)) return normalizeSessionId(cloneOfFromPageData)
  }

  const routeIdRaw = router.currentRoute.value?.params?.id
  const storeEntry = findPageStoreEntryForForm(appStore.pagesData as Record<string, any>, {
    routeParamId: Array.isArray(routeIdRaw) ? routeIdRaw[0] : routeIdRaw,
    currentPageId: (appStore as { currentPageId?: string | number }).currentPageId,
    slotId: pageDataSlot.value,
    registryFormId: pageRegistryFormId.value,
    formId: props.formPath?.formId,
    windowInstanceId: pageWindowInstanceId.value,
  })
  if (storeEntry?.page) {
    const sessionFromStore = unwrapRef(storeEntry.page.sessionId)
    if (isValidSessionId(sessionFromStore)) return normalizeSessionId(sessionFromStore)

    const cloneFromStore = unwrapRef(storeEntry.page.cloneOf)
    if (isValidSessionId(cloneFromStore)) return normalizeSessionId(cloneFromStore)
  }

  return null
}

/**
 * Проверка на незагруженные в платформу файлы
 *
 * @param items
 */
function hasPendingAttachmentUploads(items: FileItem[]): boolean {
  return items.some(
    (item) =>
      item.file instanceof File &&
      !rowHasServerCode((item.fields ?? {}) as Record<string, unknown>),
  )
}

// Helper function to transform UniData into MediaWallModel
const transformMediaWallData = (data: any): any => {
  // Unwrap Vue Proxy and serialize for comparison
  const rawData = toRaw(data)
  const currentJSON = JSON.stringify(rawData)

  // Check if data is the same as last time (prevent unnecessary transforms)
  if (currentJSON === lastRawMediaWallValueJSON) {
    return transformedMediaWallCache.value
  }

  lastRawMediaWallValueJSON = currentJSON

  // Default model
  let result: any = {
    gridWidth: 10,
    gridHeight: 8,
    widgets: [],
  }

  // Get field name for this element
  const fieldName = elementApi.fieldName.value

  const possibleWidthFields = [
    `${fieldName}__width`,
    `${fieldName}__WIDTH`,
    `${fieldName}_WIDTH`,
    `${fieldName}_width`,
  ]

  const possibleHeightFields = [
    `${fieldName}__height`,
    `${fieldName}__HEIGHT`,
    `${fieldName}_HEIGHT`,
    `${fieldName}_height`,
  ]

  let widthValue = null
  let heightValue = null

  for (const field of possibleWidthFields) {
    const value = (pageFields.value as any)?.[field]?.value
    if (value !== undefined && value !== null) {
      widthValue = value
      break
    }
  }

  for (const field of possibleHeightFields) {
    const value = (pageFields.value as any)?.[field]?.value
    if (value !== undefined && value !== null) {
      heightValue = value
      break
    }
  }

  if (widthValue) result.gridWidth = Number(widthValue) || 10
  if (heightValue) result.gridHeight = Number(heightValue) || 8

  // Transform widgets from UniDataTable
  if (data && typeof data === 'object' && 'rows' in data) {
    const rows = data.rows || []
    result.widgets = rows
      .filter((row: any) => row != null && typeof row === 'object')
      .map((row: any, index: number) => {
        // Extract fields from UniData row format
        const fields = row.fields || {}

        // Transform row data into MediaWallWidget format
        return {
          id: getFieldValue(fields.id) || getFieldValue(fields._id) || index,
          color: getFieldValue(fields.COLOR) || getFieldValue(fields.color) || '#CCFFCC',
          title: getFieldValue(fields.TITLE) || getFieldValue(fields.title) || '',
          x: Number(getFieldValue(fields.X) || getFieldValue(fields.x) || 0),
          y: Number(getFieldValue(fields.Y) || getFieldValue(fields.y) || 0),
          width: Number(getFieldValue(fields.WIDTH) || getFieldValue(fields.width) || 1),
          height: Number(getFieldValue(fields.HEIGHT) || getFieldValue(fields.height) || 1),
          image: getFieldValue(fields.IMAGE) || getFieldValue(fields.image),
          payload: row,
        }
      })
  } else if (data && typeof data === 'object' && data.gridWidth !== undefined) {
    // Already in MediaWallModel format
    result = data
  }

  transformedMediaWallCache.value = result
  return result
}

// Get model value with proper type handling
const resolveCommittedTextLikeModelValue = (fieldName: string, rawValue: unknown): unknown => {
  const toTextLikeScalar = (value: unknown): unknown => unwrapScriptValue(value)
  if (rawValue !== undefined && rawValue !== null && String(rawValue).trim() !== '') {
    return toTextLikeScalar(rawValue)
  }
  const pageEntry = (pageFields.value as Record<string, unknown> | undefined)?.[fieldName]
  if (pageEntry && typeof pageEntry === 'object' && !Array.isArray(pageEntry) && 'value' in pageEntry) {
    const fromPage = (pageEntry as { value?: unknown }).value
    if (fromPage !== undefined && fromPage !== null) {
      return toTextLikeScalar(fromPage)
    }
  } else if (fieldEntryHasPresentableValue(pageEntry)) {
    return toTextLikeScalar(pageEntry)
  }
  const formId = (elementContext as { formId?: string | number })?.formId ?? props.formPath?.formId
  if (!isCollectingLiveFieldOverrides()) {
    const live = (formProvider as { collectLiveFieldOverrides?: (id?: string | number) => Record<string, { value?: unknown }> } | null)
      ?.collectLiveFieldOverrides?.(formId)?.[fieldName]
    if (live && typeof live === 'object' && 'value' in live && live.value !== undefined) {
      return toTextLikeScalar(live.value ?? '')
    }
    if (fieldEntryHasPresentableValue(live)) {
      return toTextLikeScalar(live?.value ?? '')
    }
  }
  return rawValue ?? ''
}

const toListboxScalarModelValue = (entry: unknown): string | number | boolean | '' => {
  const scalar = extractListboxScalarValue(entry)
  if (typeof scalar === 'string' || typeof scalar === 'number' || typeof scalar === 'boolean') {
    return scalar
  }
  return ''
}

const getModelValue = () => {
  const type = elementApi.type.value
  const rawValue = elementApi.value.value
  const fieldName = elementApi.fieldName.value

  switch (type) {
    case 'DATERANGE':
      if (typeof rawValue === 'string' && rawValue === '') {
        return {from: null, to: null}
      }
      if (typeof rawValue === 'object' && rawValue !== null) {
        return rawValue
      }
      return {from: null, to: null}
    case 'MEDIAWALL':
      return transformMediaWallData(rawValue)
    case 'APPROVAL':
      if (Array.isArray(rawValue)) {
        return rawValue
      }
      if (rawValue && typeof rawValue === 'object') {
        // Check if it's a UniDataTable structure with rows
        if (rawValue.rows && Array.isArray(rawValue.rows)) {
          // Transform rows to ApprovalGroup format
          return rawValue.rows.map((row: any) => {
            const fields = row.fields || row
            return {
              id: fields.id?.value || fields._id?.value || fields.id || fields._id,
              name:
                fields.name?.value ||
                fields.description?.value ||
                fields.name ||
                fields.description,
              signer: fields.signer?.value || fields.user?.value || fields.signer || fields.user,
              signDate:
                fields.signDate?.value || fields.date?.value || fields.signDate || fields.date,
              status: fields.status?.value || fields.status,
              comment: fields.comment?.value || fields.comment,
            }
          })
        }
        // wrap in an array
        if (rawValue.id || rawValue._id) {
          return [rawValue]
        }
      }
      // If approval data not loaded yet
      if (typeof rawValue === 'number') {
        return []
      }
      return []
    case 'TASKS':
      if (Array.isArray(rawValue)) {
        return rawValue
      }
      if (rawValue && typeof rawValue === 'object') {
        if (rawValue.rows && Array.isArray(rawValue.rows)) {
          return rawValue.rows
        }
        if (rawValue.items && Array.isArray(rawValue.items)) {
          return rawValue.items
        }
      }
      return []
    case 'FILEATTACHMENTS':
      return uniDataTableToFileItems(rawValue)
    case 'TILES': {
      const pageField = (pageFields.value as Record<string, unknown> | undefined)?.[fieldName]
      if (fieldName && pageField !== undefined && pageField !== null) {
        return { rows: extractUniDataTableRows(pageField) }
      }
      const registryRows = extractUniDataTableRows(rawValue)
      return { rows: registryRows }
    }
    case 'FILEDOWNLOAD':
    case 'EXTFILEDOWNLOAD':
      return getFileDownloadModelValue()
    case 'LISTBOX':
    case 'COMBOBOX': {
      const pf = (pageFields.value ?? {}) as Record<string, any>
      const pageEntry = (pageFields.value as Record<string, any> | undefined)?.[fieldName]
      const fromPage = toListboxScalarModelValue(pageEntry)
      if (!isListboxEmptyValue(fromPage)) return fromPage
      const fromRaw = toListboxScalarModelValue(rawValue)
      return normalizeListboxFieldValue(fromRaw, pf, fieldName)
    }
    case 'MULTISELECT':
      if (rawValue !== undefined && rawValue !== null) {
        return createMultiSelectValue(rawValue)
      }
      return createMultiSelectValue(
        pageFields.value?.[fieldName]?.value ??
        pageFields.value?.[fieldName],
      )
    case 'BOOLEAN': {
      const coerced = coerceStateBoolean(rawValue)
      return coerced === undefined ? rawValue : coerced
    }
    case 'CHECKBOX':
      if (rawValue && typeof rawValue === 'object') {
        return rawValue
      }
      if (Array.isArray(rawValue)) {
        return rawValue
      }
      {
        const coerced = coerceStateBoolean(rawValue)
        return coerced === undefined ? rawValue : coerced
      }
    case 'HTML':
      if (
        rawValue &&
        typeof rawValue === 'object' &&
        !Array.isArray(rawValue) &&
        'value' in rawValue
      ) {
        return rawValue.value ?? ''
      }
      return rawValue ?? ''
    case 'TEXT':
    case 'PASSWORD':
    case 'TEXTAREA':
    case 'MULTILINETEXT':
      return resolveCommittedTextLikeModelValue(fieldName, rawValue)
    case 'DATE':
    case 'DATETIME':
      return resolveDateTimeModelValue(fieldName, rawValue)
    default:
      return rawValue
  }
}

const r7EditorPlaceholderId = `r7-att-ed-${getCurrentInstance()?.uid ?? Date.now()}`
const r7PreviewHostId = `r7-att-preview-${getCurrentInstance()?.uid ?? Date.now()}`
const attachmentR7Editor = useAttachmentR7Editor(r7EditorPlaceholderId)
const attachmentR7Preview = useAttachmentR7Editor(r7PreviewHostId)
const wsService = getWebSocketService()
const r7EditorVisible = ref(false)
const r7EditorTitle = ref(t('attachments.r7EditorTitle'))
const r7EditorTargetFile = ref<FileItem | null>(null)
const r7EditorAwaitingShown = ref(false)
const r7ActiveDocumentKey = ref<string | null>(null)
const r7EditorNotification = useNotification()
const r7EditorLoading = computed(() => attachmentR7Editor.isLoading.value)
const r7EditorError = computed(() => attachmentR7Editor.error.value)

let handleR7AttachmentStagingUpdated: (
  payload: Parameters<ReturnType<typeof useR7InlinePreview>['handleStagingUpdated']>[0],
) => void = () => {}
let onBeforeGetFullDataUpload: () => void = () => {}
let onAfterGetFullDataUpload: () => void = () => {}
let prepareFileAttachmentsForFormSave: () => Promise<void> = async () => {}
let fileAttachmentsFormSavePrepared = false

const fileAttachmentsElement = useFileAttachmentsElement({
  elementType: elementApi.type,
  fieldName: elementApi.fieldName,
  verificationError: elementApi.verificationError,
  elementConfig: computed(() => props.config?.element),
  formPath: computed(() => props.formPath),
  elementId,
  componentRef,
  pageActions,
  pageData,
  pageRegistryFormId,
  pageDataSlot,
  currentPlatform,
  appStorePagesData: appStore.pagesData as Record<string, unknown>,
  formProvider: formProvider ?? undefined,
  getSessionId,
  getModelValue,
  writeToElement: (table) => elementApi._input(table, { skipOnElement: true }),
  emitInput: (items) => emit('input', items),
  onR7AttachmentStagingUpdated: (payload) => handleR7AttachmentStagingUpdated(payload),
  onBeforeGetFullDataUpload: () => onBeforeGetFullDataUpload(),
  onAfterGetFullDataUpload: () => onAfterGetFullDataUpload(),
})

const r7EditorOpenContext = ref<ReturnType<
  typeof fileAttachmentsElement.resolveR7EditorOpenContext
> | null>(null)

const r7InlinePreview = useR7InlinePreview({
  previewHostId: r7PreviewHostId,
  attachmentR7Preview,
  fileAttachmentsElement,
  wsService,
  isEditorVisible: r7EditorVisible,
})
handleR7AttachmentStagingUpdated = r7InlinePreview.handleStagingUpdated

/** Store rows + hydrate `File`/`changed` из реестра (фаза E, SSOT). */
const fileAttachmentsModelValue = computed((): FileItem[] => {
  if (elementApi.type.value !== 'FILEATTACHMENTS') return []
  const rows = uniDataTableToFileItems(elementApi.value.value)
  return hydrateAttachmentFileItemsFromStore(elementId.value, rows)
})

watch(
  () =>
    elementApi.type.value === 'FILEATTACHMENTS'
      ? {
          items: fileAttachmentsModelValue.value,
          raw: elementApi.value.value,
          fieldName: elementApi.fieldName.value,
          elementId: elementId.value,
        }
      : null,
  (snapshot) => {
    if (!snapshot) return
    logAttachmentStoreDebug('model-value', {
      fieldName: snapshot.fieldName,
      elementId: snapshot.elementId,
      ...summarizeAttachmentFileItems(snapshot.items),
      storeRaw: summarizeAttachmentFieldEntry(snapshot.raw),
    })
  },
  { deep: true, immediate: true },
)

onUnmounted(() => {
  if (elementApi.type.value !== 'FILEATTACHMENTS') return
  if (r7EditorVisible.value) {
    attachmentR7Editor.finalizeEditorSession()
  }
  const eid = Number(elementId.value)
  if (!Number.isFinite(eid) || eid <= 0) return
  // Диалог/форма закрыты — temp documentSessionId на middle больше не контрактен.
  removeDsAttachmentStore(eid)
  removeAttachmentNativeFileStore(eid)
  clearSessionsForElement(eid)
})

// Get inline actions for this element (memoized as computed)
const inlineActions = computed(() => {
  const elementId = elementApi.id.value?.toString()
  if (!elementId || !pageActions.value) return []

  const filtered = pageActions.value
    .filter((action) => {
      const levelMatch = resolveActionLayoutLevelText(action.layout) === 'element'
      const idMatch = resolveActionLayoutTextId(action.layout) === elementId
      const positionMatch = resolveActionLayoutPositionText(action.layout) === 'inline'

      const roleMatch = hasActionRoleAccess(action.roles)

      return levelMatch && idMatch && positionMatch && roleMatch
    })
    .sort((a, b) => {
      const sortA = parseInt(a._attributes?.sort) || 0
      const sortB = parseInt(b._attributes?.sort) || 0
      return sortA - sortB
    })

  return filtered
})

const hasRequiredElementActionRole = (action: any): boolean => hasActionRoleAccess(action.roles)

const getDropDownName = (action: any): string => {
  const dropDown = action.dropDownActionName
  if (Array.isArray(dropDown)) {
    return dropDown[0]?._text?.trim() || ''
  }
  return dropDown?._text?.trim() || ''
}

const isDropDownMain = (action: any): boolean => {
  const dropDown = action.dropDownMain
  const value = Array.isArray(dropDown) ? dropDown[0]?._text : dropDown?._text
  return value === 'true' || value === true
}

const rightActions = computed(() => {
  const currentElementId = elementApi.id.value?.toString()
  if (!currentElementId || !pageActions.value) return []

  return pageActions.value
    .filter((action) => {
      const levelMatch = resolveActionLayoutLevelText(action.layout) === 'element'
      const idMatch = resolveActionLayoutTextId(action.layout) === currentElementId
      const actionPosition = resolveActionLayoutPositionText(action.layout)
      const positionMatch = actionPosition === 'right' || actionPosition.includes('right')
      const roleMatch = hasRequiredElementActionRole(action)
      const dropDownName = getDropDownName(action)

      if (dropDownName && !isDropDownMain(action)) {
        return false
      }

      return levelMatch && idMatch && positionMatch && roleMatch
    })
    .sort((a, b) => {
      const sortA = parseInt(a._attributes?.sort) || 0
      const sortB = parseInt(b._attributes?.sort) || 0
      return sortA - sortB
    })
})

const primaryMultiselectRightAction = computed(() =>
  elementApi.type.value === 'MULTISELECT' ? rightActions.value[0] ?? null : null,
)

const isMessageTextWithRightActions = computed(() => {
  const fieldName = String(elementApi.fieldName.value || elementApi.name.value || '')
  return fieldName === 'messageText' && rightActions.value.length > 0
})

const resolveDateTimeVisibilityProps = (rawFormat: unknown): Record<string, any> =>
  resolveDateTimePickerProps(rawFormat)

const resolveApplyCurrentDateOnClickProps = (): Record<string, unknown> => ({
  applyCurrentDateOnClick: parseApplyCurrentDateOnClick(
    props.config?.element?.applyCurrentDateOnClick,
  ),
  platformFormat: String(elementApi.format.value ?? ''),
})

const resolveDateTimeModelValue = (fieldName: string, rawValue: unknown): Date | null => {
  const dateProps = resolveDateTimeVisibilityProps(elementApi.format.value)
  const timeOnly = dateProps.timeOnly === true
  const parseOpts = { timeOnly }

  // Явный clear в registry / refill payload — не поднимать stale date из pageFields.
  if (
    rawValue === null ||
    rawValue === '' ||
    (rawValue &&
      typeof rawValue === 'object' &&
      !Array.isArray(rawValue) &&
      fieldEntryLooksEmptyIncoming(rawValue))
  ) {
    return null
  }

  const fromRaw =
    rawValue instanceof Date && !Number.isNaN(rawValue.getTime())
      ? parsePageFieldToDate(rawValue, parseOpts)
      : parsePageFieldToDate(rawValue, parseOpts)

  const pageEntry = resolvePageFieldEntry(
    (pageFields.value ?? {}) as Record<string, unknown>,
    fieldName,
  )
  if (pageEntry !== undefined) {
    if (fieldEntryLooksEmptyIncoming(pageEntry)) {
      return null
    }
    const fromPage = parsePageFieldToDate(pageEntry, parseOpts)
    if (fromPage && fromRaw && fromRaw.getTime() !== fromPage.getTime()) {
      return fromRaw
    }
    if (fromPage) return fromPage
  }

  if (fromRaw) return fromRaw

  return null
}

const getTypeSpecificProps = (elementType: string): Record<string, any> => {
  switch (elementType) {
    case 'TEXT':
      return { type: 'text', plain: true, platformFormat: String(elementApi.format.value ?? '') }
    case 'PASSWORD':
      return { type: 'password', plain: true }
    case 'TEXTAREA':
    case 'MULTILINETEXT':
      return {
        height: props.config.element?.height?._text,
        autoResize: elementApi?.element?.value?.grabVertical?._text === 'true',
        platformFormat: String(elementApi.format.value ?? ''),
      }
    case 'LISTBOX':
      return {
        items: getListboxItems(),
        itemValue: 'value',
        itemText: 'text',
        elementId: elementApi.id.value,
        resolveItemLabel: resolveListboxSelectorItemLabel,
      }
    case 'COMBOBOX':
      return {
        items: getComboboxItems(),
        itemValue: 'value',
        itemText: 'text',
        elementId: elementApi.id.value,
        resolveItemLabel: resolveListboxSelectorItemLabel,
      }
    case 'BOOLEAN':
      return { binary: true }
    case 'CHECKBOX':
      return { options: getCheckboxOptions() }
    case 'DATETIME':
      return {
        ...resolveDateTimeVisibilityProps(elementApi.format.value),
        ...resolveApplyCurrentDateOnClickProps(),
      }
    case 'DATE':
      return {
        showTime: false,
        ...resolveApplyCurrentDateOnClickProps(),
      }
    case 'DATERANGE':
      return {
        dateFormat: 'dd.mm.yy',
        fromPlaceholder: t('ui.from'),
        toPlaceholder: t('ui.to'),
        ...resolveApplyCurrentDateOnClickProps(),
      }
    case 'TABLE':
      return {
        withCheckbox: isXmlTrue(props?.config?.element?.withCheckBoxes?._text, false),
        selected: internalSelected.value.length > 0 ? internalSelected.value : props.selected || [],
        showControlPanel: isXmlTrue(props.config.element?.showControlPanel?._text, true),
        showHeader: isXmlTrue(props.config.element?.showHeader?._text, true),
        showTableHeader: isXmlTrue(props.config.element?.showTableHeader?._text, true),
        tableName: elementApi.fieldName.value,
        scrollHeight: tableScrollHeight.value,
        selectionMode: isXmlTrue(props?.config?.element?.withCheckBoxes?._text, false)
          ? 'multiple'
          : 'single',
        elementId: elementApi.id.value,
        beforeContextMenuShow: handleContextMenu,
        previewInlineMenu: Boolean(String(props.config.element?.onInlineMenuShow?._text ?? '').trim()),
      }
    case 'EDITABLE': {
      const withCheckBoxes = isXmlTrue(props?.config?.element?.withCheckBoxes?._text, false)
      return {
        cellEditable: props.config.element?.isReadOnly?._text !== '1',
        sortable: true,
        withCheckbox: withCheckBoxes,
        selected: withCheckBoxes
          ? (internalSelected.value.length > 0 ? internalSelected.value : props.selected || [])
          : props.selected || [],
        selectionMode: withCheckBoxes ? 'multiple' : 'single',
        onUpdateMeta: (meta: any) => handleUpdateMeta(meta),
        elementId: elementApi.id.value,
        showControlPanel: isXmlTrue(props.config.element?.showControlPanel?._text, true),
        tableName: elementApi.fieldName.value,
        showHeader: isXmlTrue(props.config.element?.showHeader?._text, true),
        showTableHeader: isXmlTrue(props.config.element?.showTableHeader?._text, true),
        scrollHeight: tableScrollHeight.value,
        beforeContextMenuShow: handleContextMenu,
        previewInlineMenu: Boolean(String(props.config.element?.onInlineMenuShow?._text ?? '').trim()),
      }
    }
    case 'TREETABLE':
      return {
        sortable: true,
        onUpdateMeta: (meta: any) => handleUpdateMeta(meta),
        withCheckbox: isXmlTrue(props?.config?.element?.withCheckBoxes?._text, false),
        showControlPanel: isXmlTrue(props.config.element?.[IKey.SHOW_CONTROL_PANEL]?._text, false),
        showHeader: isXmlTrue(props.config.element?.showHeader?._text, true),
        showTableHeader: isXmlTrue(props.config.element?.showTableHeader?._text, true),
        tableName: elementApi.fieldName.value,
        fillAvailable: treeTableFillAvailable.value,
        scrollHeight: tableScrollHeight.value,
        expandByDefault: isXmlTrue(props.config.element?.[IKey.EXPAND_BY_DEFAULT]?._text, false),
        showRootNode: isXmlTrue(props.config.element?.[IKey.TT_VISIBLE_ROOT_NODE]?._text, false),
        checkingMode:
          props.config.element?.[IKey.TT_CHECKING_MODE]?._text || 'PROPAGATE_PRESERVING_UNCHECK',
        forbidSelectNode: isXmlTrue(props.config.element?.[IKey.TT_FORBID_SELECT_NODE]?._text, false),
        hideCheckBoxOnAllLevel: isXmlTrue(
          props.config.element?.[IKey.TT_HIDE_CHECKBOX_ON_ALL_LEVEL]?._text,
          false,
        ),
        hideCheckBoxLevel: props.config.element?.[IKey.TT_HIDE_CHECKBOX_LEVEL]?._text || '',
        isMultiSelectEnabled: isXmlTrue(props.config.element?.isMultiSelectEnabled?._text, false),
        descRoot: props.config.element?.[IKey.TT_DESC_ROOT]?._text || '',
        hideBorder: isXmlTrue(props.config.element?.[IKey.HIDE_BORDER]?._text, false),
        tableStyle: props.config.element?.[IKey.TABLE_STYLE]?._text || '',
        maxOnPageValues: props.config.element?.[IKey.MAX_ON_PAGE_VALUES]?._text || '',
        showFormStateOptions: isXmlTrue(
          props.config.element?.[IKey.TT_FORMSTATE_OPTIONS]?._text,
          false,
        ),
        savedExpandedKeys: getSavedSetting('expandedKeys'),
        dragEnabled:
          props.config.element?.isDraggable?._text === 'true' ||
          props.config.element?.isDraggable?._text === '1',
        beforeContextMenuShow: handleContextMenu,
        previewInlineMenu: Boolean(String(props.config.element?.onInlineMenuShow?._text ?? '').trim()),
      }
    case 'LABEL':
      return {
        isLink: false,
        align: props.config.element?.align?._text || 'left',
      }
    case 'MULTISELECT':
      return {
        items: getMultiselectItems(),
        selectedRowsSource: getModelValue(),
        itemValue: 'value',
        itemText: 'text',
        multiple: true,
        format: elementApi.format.value,
        inlineActions: inlineActions.value,
        elementId: elementApi.id.value?.toString(),
        panelId: props.formPath?.panelId,
        contextMenuItems: getTableContextMenuItems(),
        hasPrimaryAction: Boolean(primaryMultiselectRightAction.value),
        hasEditor: elementApi.hasEditor.value,
        suggestOnFocusGained: elementApi.suggestOnFocusGained.value,
        linked: elementApi.linked.value,
        getSuggestions: (opts: { filter?: string; cursor?: number; forced?: boolean }) => {
          const liveRows = (
            componentRef.value as { getLiveSelectedRows?: () => unknown } | null
          )?.getLiveSelectedRows?.()
          const rows = Array.isArray(liveRows) ? liveRows : []
          const fieldName = elementApi.fieldName.value
          return (
            elementApi.getAutoCompleteList?.({
              ...opts,
              fieldOverlay: fieldName
                ? { [fieldName]: toMultiSelectTypedValue(rows) }
                : undefined,
            }) ?? Promise.resolve([])
          )
        },
      }
    case 'SLIDER':
      return {
        min: parseInt(props.config.element?.minValue?._text || '0'),
        max: parseInt(props.config.element?.maxValue?._text || '100'),
        step: parseInt(props.config.element?.step?._text || '1'),
      }
    case 'PROGRESSBAR':
      return {
        max: parseInt(props.config.element?.maxValue?._text || '100'),
        showLabel: true,
      }
    case 'IDENTIFICATOR':
      return { name: elementApi.name.value }
    case 'STRINGRANGE':
      return {
        fromPlaceholder: t('ui.from'),
        toPlaceholder: t('ui.to'),
        separatorText: t('ui.and'),
      }
    case 'HTML':
      return {
        editable: props.config.element?.isReadOnly?._text === '0',
        rows: parseInt(props.config.element?.height?._text || '10'),
        preferDisplayMode: true,
      }
    case 'DIRECTORY':
      return { placeholder: t('ui.selectFolder') }
    case 'FILEUPLOAD':
      return { multiple: false }
    case 'EXTFILEUPLOAD':
      return { multiple: true }
    case 'FILEDOWNLOAD':
    case 'EXTFILEDOWNLOAD':
      return {
        canOpen: true,
        downloadLabel: t('ui.download'),
      }
    case 'TILES':
      return {
        expandByDefault:
          props.config.element?.expandByDefault?._text === 'true' ||
          props.config.element?.expandByDefault?._text === '1',
        contextMenuItems: tableContextMenuItemsLive.value,
      }
    case 'SVG':
      return {
        width: props.config.element?.width?._text || '100%',
        height: props.config.element?.height?._text || 'auto',
      }
    case 'GROUPTABLE':
      return {
        groupBy: props.config.element?.groupBy?._text || '_group',
        sortable: true,
        withCheckbox: isXmlTrue(props?.config?.element?.withCheckBoxes?._text, false),
        showControlPanel: isXmlTrue(props.config.element?.showControlPanel?._text, true),
        showHeader: isXmlTrue(props.config.element?.showHeader?._text, true),
        showTableHeader: isXmlTrue(props.config.element?.showTableHeader?._text, true),
        tableName: elementApi.fieldName.value,
      }
    case 'TASKS':
      return {
        editable: props.config.element?.isReadOnly?._text !== '1',
        canAddTasks: true,
      }
    case 'APPROVAL':
      return {
        editable: props.config.element?.isReadOnly?._text !== '1',
        isNested: false,
        canAddSteps: true,
      }
    case 'MEDIAWALL':
      return {
        dragEnabled: props.config.element?.isReadOnly?._text !== '1',
        replaceable:
          props.config.element?.canBeReplaced?._text === 'true' ||
          props.config.element?.canBeReplaced?._text === '1',
        inlineActions: inlineActions.value,
        elementId: elementApi.id.value?.toString() || '',
        panelId: elementContext?.panelId || props.formPath?.panelId || '',
      }
    case 'VIDEOPLAYER':
      return {
        controls: true,
        autoplay: false,
        width: props.config.element?.width?._text || '100%',
        height: props.config.element?.height?._text || '400px',
      }
    case 'DOWNLOADTASK':
      return { disabled: props.config.element?.isReadOnly?._text === '1' }
    case 'FILEATTACHMENTS':
      return {
        tableFieldName: elementApi.fieldName.value,
      }
    default:
      return {}
  }
}

// Пропсы для компонента
const componentProps = computed(() => {
  const isPanelReadOnly = elementContext?.isReadOnly || false
  const elementType = elementApi.type.value
  const textLikeModelValue =
    isTextLikeFormElementType(elementType) && textLikeEditingValue.value !== null
      ? textLikeEditingValue.value
      : getModelValue()
  const baseProps = {
    modelValue: textLikeModelValue,
    label: elementApi.label.value,
    placeholder: elementApi.label.value,
    disabled: props.readonly || !elementApi.editable.value || isPanelReadOnly,
    readonly: props.readonly || !elementApi.editable.value || isPanelReadOnly,
  }

  // Explicitly access pageFields to ensure reactivity tracking
  const _ = pageFields.value

  const typeProps = getTypeSpecificProps(elementType)

  if (elementType === 'TEXT' && isAutoCompleteField.value) {
    const suggestFromServer = elementApi.enableSuggestEditOverride.value
    const delimiterFromServer = elementApi.multipleDelimiterOverride.value
    return {
      ...baseProps,
      ...typeProps,
      autoComplete: effectiveAutocompleteText.value || '',
      enableSuggestEdit:
        suggestFromServer != null
          ? suggestFromServer
          : props.config.element?.enableSuggestEdit?._text || '',
      multipleDelimiter:
        delimiterFromServer != null
          ? delimiterFromServer
          : props.config.element?.multipleDelimiter?._text || ', ',
      suggestions: autoCompleteSuggestions.value,
      multipleLimit: resolveElementMultipleLimit({
        xmlLimit: props.config.element?.multipleLimit,
        runtimeLimit: elementApi.multipleLimit.value,
      }),
    }
  }

  if (elementType === 'FILEATTACHMENTS') {
    return {
      ...baseProps,
      modelValue: fileAttachmentsModelValue.value,
      ...typeProps,
      tableFieldName: elementApi.fieldName.value,
      previewOnly: isXmlTrue(props.config.element?.previewOnly?._text, false),
      tapeWithFilesOnly: isXmlTrue(props.config.element?.tapeWithFilesOnly?._text, false),
      autoShowFirstElement: isXmlTrue(
        props.config.element?.auto_show_first_element?._text ??
          (props.config.element as { autoShowFirstElement?: { _text?: string } })?.autoShowFirstElement
            ?._text,
        false,
      ),
      disabled: isFileAttachmentsTapeMutationBlocked({
        elementMeta: parseFileAttachmentsElementMeta(props.config.element),
        elementEditable: elementApi.editable.value,
        panelReadOnly: isPanelReadOnly,
        formReadOnly: props.readonly,
      }),
      fetchAttachmentBlob: (file: FileItem) =>
        fileAttachmentsElement.fetchAttachmentBlobForFile(file),
      uploadInProgress: false,
      showR7EditorButton: true,
      canOpenR7Editor: fileAttachmentsElement.canOpenR7ForFile,
      r7InlinePreviewHostId: r7PreviewHostId,
      r7InlinePreviewOverlay: r7InlinePreview.inlinePreviewOverlay.value,
      r7InlinePreviewError: r7InlinePreview.previewError.value,
      canPreviewR7Inline: fileAttachmentsElement.canPreviewR7InlineForFile,
      resolveAttachmentTapeR7Debug: isAttachmentTapeDebugEnabled()
        ? fileAttachmentsElement.resolveAttachmentTapeR7Debug
        : undefined,
      resolveAttachmentTapeBadge: (file: FileItem) =>
        fileAttachmentsElement.resolveAttachmentTapeBadgeUi(file),
      isR7ModalOpenForFile: (file: FileItem) => {
        if (!r7EditorVisible.value || !r7EditorTargetFile.value) return false
        return findMatchingAttachmentItem([r7EditorTargetFile.value], file) !== undefined
      },
      onRemoveAttachmentSideEffects: (file: FileItem) => {
        const eid = elementId.value
        if (eid == null) return
        try {
          getDsAttachmentStore(Number(eid)).clearSnapshot(file)
          getAttachmentNativeFileStore(Number(eid)).clearForItem(file)
        } catch {
          // ignore invalid elementId
        }
      },
      errorMessages: elementApi.verificationError.value ? [elementApi.verificationError.value] : [],
    }
  }

  if (elementType === 'TILES') {
    const tp = props.config.element?.panel?._text || (props.config.element?.panel as any)?.[0]?._text
    const tilePanelId = tp ? parseInt(String(tp), 10) : 0
    const tilesExpandByDefault = isXmlTrue(
      props.config.element?.expandByDefault?._text ??
      (props.config.element as any)?._attributes?.expandByDefault,
      true,
    )
    return {
      ...baseProps,
      ...typeProps,
      tilePanelId,
      tilesElementId: elementId.value ?? 0,
      parentPanelId: elementContext?.panelId ?? props.formPath?.panelId ?? '',
      pageId: elementContext?.pageId ?? '',
      formId: elementContext?.formId ?? props.formPath?.formId ?? '',
      tableFieldName: elementApi.fieldName.value,
      inlineAfterLoad: props.config.element?.inlineAfterLoad ?? '',
      gapPx: 5,
      expandByDefault: tilesExpandByDefault,
      errorMessages: elementApi.verificationError.value ? [elementApi.verificationError.value] : [],
    }
  }

  if (
    elementType === 'TABLE' ||
    elementType === 'EDITABLE' ||
    elementType === 'TREETABLE' ||
    elementType === 'GROUPTABLE'
  ) {
    // Force reactivity by accessing pageFields and computing fresh values
    void appStore.pagesDataRevision
    const endMeasure = startWorkflowMeasure('ElementRenderer.componentProps.table', {
      fieldName: elementApi.fieldName.value,
      elementId: elementId.value,
      elementType,
    })
    try {
      const tableItems = elementType === 'TREETABLE' ? getTreeTableItems() : getTableItems()
      const tableHeaders = getTableHeaders()
      const tableMeta = getTableMeta(tableItems.length)
      void tableContextMenuItemsLive.value
      const selectedColumns = getSavedSetting('selectedColumns')
      const columnWidths = getSavedSetting('columnWidths')
      const hasCustomStyle = hasElementCustomStyle(props.config.element)

      return {
        ...baseProps,
        ...typeProps,
        items: tableItems,
        headers: tableHeaders,
        meta: tableMeta,
        contextMenuItems: tableContextMenuItemsLive.value,
        loading: isTableReloading.value,
        errorMessages: elementApi.verificationError.value ? [elementApi.verificationError.value] : [],
        selectedColumns: selectedColumns,
        columnWidths: columnWidths,
        skipDefaultStyles: hasCustomStyle,
      }
    } finally {
      endMeasure()
    }
  }

  return {
    ...baseProps,
    ...typeProps,
    errorMessages: elementApi.verificationError.value ? [elementApi.verificationError.value] : [],
  }
})

// Выполнить onChange для текстового поля (Enter или выбор из автодополнения).
// committedValue — явное значение из эмита change (важно: до обновления props после update:modelValue).
const runOnChangeForText = async (committedValue?: unknown) => {
  const value = committedValue !== undefined ? committedValue : getModelValue()
  await executeEventAction('onChange', {value, selectedData: value})
  if (elementId.value) {
    await invokeListeners('element', elementId.value, 'change', {value})
  }
}

// События для компонента
const componentEvents = computed(() => {
  const elType = elementApi.type.value
  const usesBeforeContextMenuShow =
    elType === 'TABLE' || elType === 'EDITABLE' || elType === 'TREETABLE'

  const baseEvents: Record<string, (...args: any[]) => any> = {
    'update:modelValue': handleInput,
    'enter': handleEnter,
    'change': handleChange,
    'user-change': handleSelectionUserChange,
    'update:selected': handleSelectionChange,
    'update:meta': handleUpdateMeta,
    'dblclick': handleTableDoubleClick,
    'blur': handleBlur,
    'focus': handleFocus,
    'click': handleClick,
    'hover': handleHover,
    'keypress': handleKeyPress,
    'keyup': handleKeyRelease,
    'file-change': handleFileChange,
    // MediaWall specific events
    'widget-click': handleWidgetClick,
    'widget-dblclick': handleWidgetDoubleClick,
    'context-menu': handleWidgetContextMenu,
    'replace': handleWidgetReplace,
    'drag-start': handleDragStart,
    'drag-end': handleDragEnd,
    'import': handleImport,
    'action': handleAction,
    'chip-action': handleChipAction,
    'primary-click': handlePrimaryMultiSelectClick,
    'saveSetting': handleSaveSetting
  }

  // TABLE/TREETABLE: beforeContextMenuShow prop (await) вместо emit contextmenu.
  if (!usesBeforeContextMenuShow) {
    baseEvents.contextmenu = handleContextMenu
  }

  if (elementApi.type.value === 'FILEDOWNLOAD' || elementApi.type.value === 'EXTFILEDOWNLOAD') {
    return {
      ...baseEvents,
      'download': handleFileDownload,
      'open': handleFileOpen
    }
  }

  if (elementApi.type.value === 'FILEATTACHMENTS') {
    // update:modelValue уже на <component> в template — не дублировать (двойной handleInput).
    const { 'update:modelValue': _modelValueHandler, ...fileEvents } = baseEvents
    return {
      ...fileEvents,
      'download-file': handleFileAttachmentDownload,
      'open-r7-editor': handleFileAttachmentOpenR7Editor,
      'tape-preview-focus': r7InlinePreview.handleTapePreviewFocus,
    }
  }

  if (elementApi.type.value === 'TREETABLE') {
    return {
      ...baseEvents,
      'load-children': handleTreeTableLoadChildren,
      'node-drop': handleTreeTableNodeDrop,
      'find-parent': handleTreeTableFindParent,
    }
  }

  return baseEvents
})

// Методы для получения данных элементов
const getExplicitListboxOptionText = (item: any) => listboxOptionTextFromRow(item)

const getListboxOptionText = (item: any) => {
  const explicit = getExplicitListboxOptionText(item)
  if (listboxRowHasExplicitDescription(item)) {
    return explicit
  }
  return explicit || resolveUniDataRowLabel(item)
}

const hasVisibleListboxOptionText = (item: any) => String(getListboxOptionText(item) ?? '').trim() !== ''

const getMultiSelectDisplayText = (item: any) => {
  const format = String(elementApi.format.value ?? '').trim()
  if (format) {
    const formatted = formatItem(item, format)
    if (formatted) return formatted
  }
  return getExplicitListboxOptionText(item) || resolveUniDataRowLabel(item)
}

const getListboxOptionValue = (item: any) =>
  item?.fields?.code?.value ??
  item?.fields?.kind?.value ??
  item?.fields?.value?.value ??
  item?.fields?._id?.value ??
  item?.code?._text ??
  item?.kind?._text ??
  item?.value?._text ??
  item?._id?._text ??
  item?.id ??
  ''

const resolveListboxSelectorItemLabel = (value: unknown): string | undefined => {
  const fieldName = elementApi.fieldName.value
  if (!fieldName) return undefined
  const pf = (pageFields.value ?? {}) as Record<string, any>
  return resolveListboxDisplayLabelFromPageFields(pf, fieldName, value)
}

const createMultiSelectValue = (value: any) => normalizeMultiSelectFieldValue(value)

const getListboxItems = () => {
  const fieldName = elementApi.fieldName.value || props.config.element?.name?._text || ''
  const pf = pageFields.value as Record<string, any> | undefined
  const finish = (items: any[]) => ensureListboxSelectedOptionVisible(pf, fieldName, items)
  const elementData = elementApi.value.value

  if (pf && fieldName) {
    const possibleFieldNames = [`${fieldName}__listbox`, `${fieldName}__items`, fieldName]

    for (const key of possibleFieldNames) {
      const fieldData = pf[key]
      if (fieldData?.value?.rows && Array.isArray(fieldData.value.rows)) {
        return finish(
          fieldData.value.rows
            .map((item: any) => {
              const value = getListboxOptionValue(item)
              const text = getListboxOptionText(item)
              return { value, text, label: text, _rowData: item }
            })
            .filter(
              (item: any) =>
                item.value !== undefined &&
                item.value !== null &&
                hasVisibleListboxOptionText(item._rowData),
            ),
        )
      }
    }
  }

  if (elementData?.rows && Array.isArray(elementData.rows)) {
    return finish(
      elementData.rows
        .map((item: any) => ({
          value: getListboxOptionValue(item),
          text: getListboxOptionText(item),
          label: getListboxOptionText(item),
          _rowData: item,
        }))
        .filter((item: any) => item.value != null && hasVisibleListboxOptionText(item._rowData)),
    )
  }

  if (Array.isArray(elementData)) {
    return finish(
      elementData
        .map((item: any) => ({
          value: getListboxOptionValue(item),
          text: getListboxOptionText(item),
          label: getListboxOptionText(item),
          _rowData: item,
        }))
        .filter((item: any) => item.value != null && hasVisibleListboxOptionText(item._rowData)),
    )
  }

  const rawItems = props.config.element?.items?.item || props.config.element?.items || []
  const items = Array.isArray(rawItems) ? rawItems : rawItems ? [rawItems] : []
  return finish(
    items
      .map((item: any) => ({
        value: item.code?._text || item.value?._text || '',
        text: item.description?._text || item.name?._text || '',
        _rowData: item,
      }))
      .filter((item: any) => item.value && hasVisibleListboxOptionText(item._rowData)),
  )
}

const getMultiselectItems = () => {
  const elementName = props.config.element?.name?._text || 'unknown'
  const elementData = elementApi.value.value

  if (pageFields.value && elementName) {
    const possibleFieldNames = [`${elementName}__listbox`, `${elementName}__items`, elementName]

    for (const fieldName of possibleFieldNames) {
      const fieldData = pageFields.value[fieldName]
      if (fieldData?.value?.rows && Array.isArray(fieldData.value.rows)) {
        return fieldData.value.rows
          .map((item: any) => {
            const text = getMultiSelectDisplayText(item)
            return {value: item, text, label: text, _rowData: item}
          })
          .filter((item: any) => item.value != null)
      }
    }
  }

  if (elementData?.rows && Array.isArray(elementData.rows)) {
    return elementData.rows
      .map((item: any) => {
        const text = getMultiSelectDisplayText(item)
        return {value: item, text, label: text, _rowData: item}
      })
      .filter((item: any) => item.value != null)
  }

  if (Array.isArray(elementData)) {
    return elementData
      .map((item: any) => {
        const text = getMultiSelectDisplayText(item)
        return {value: item, text, label: text, _rowData: item}
      })
      .filter((item: any) => item.value != null)
  }

  return getListboxItems().map((item: any) => ({
    ...item,
    text: item?.text ?? item?.label ?? '',
    label: item?.text ?? item?.label ?? '',
  }))
}

const getComboboxItems = () => getListboxItems()

const getCheckboxOptions = () => {
  const rawItems = props.config.element?.items?.item || props.config.element?.items || []
  const items = Array.isArray(rawItems) ? rawItems : rawItems ? [rawItems] : []
  return items
    .map((item: any) => ({
      id: item.id?._text || item.value?._text || '',
      value: item.value?._text || item.id?._text || '',
      label: item.description?._text || item.name?._text || item.value?._text || item.id?._text || '',
    }))
    .filter((item: any) => item.id && item.label)
}

const isXmlTrue = (value: any, defaultValue = false) => {
  if (value == null) return defaultValue
  if (typeof value === 'boolean') return value
  const normalized = String(value).trim().toLowerCase()
  if (normalized === 'true' || normalized === '1') return true
  if (normalized === 'false' || normalized === '0') return false
  return defaultValue
}

const getTableFieldKeys = () => {
  const candidates = [elementApi.fieldName.value, elementApi.name.value]
    .map((value) => String(value || '').trim())
    .filter(Boolean)

  return [...new Set(candidates)]
}

const getRuntimeTableColumns = () => {
  const rawColumns = getTableFieldKeys()
    .map((tableKey) =>
      (pageFields.value as any)?.[`${tableKey}__columns`]?.value?.rows ??
      (pageFields.value as any)?.[`${tableKey}__columns`]?.rows,
    )
    .find((columns) => Array.isArray(columns) && columns.length > 0)

  if (!Array.isArray(rawColumns) || rawColumns.length === 0) {
    return []
  }

  return rawColumns
    .map((column: any) => {
      const fields = column?.fields || {}
      const formatType = fields.formatType?.value
      const formatName = fields.formatName?.value
      const rawFormat = fields.format?.value ?? fields.format?._text

      const colElementId = fields.id?.value ?? fields._id?.value
      return {
        description: fields.title?.value || fields.description?.value || fields.name?.value,
        order: fields.order?.value != null ? Number(fields.order.value) : undefined,
        type: fields.type?.value,
        name: fields.name?.value,
        elementId: colElementId != null && colElementId !== '' ? String(colElementId) : undefined,
        format:
          rawFormat || formatType || formatName
            ? rawFormat || {
                type: formatType,
                name: formatName,
              }
            : undefined,
        isReadOnly: isXmlTrue(fields.isReadOnly?.value, false),
        isSort: isXmlTrue(fields.isSort?.value, false),
        width: fields.width?.value,
        align: fields.align?.value,
        defVisible: fields.defVisible?.value == null ? true : isXmlTrue(fields.defVisible?.value, true),
        isPrimaryKey: isXmlTrue(fields.isPrimaryKey?.value, false),
        isMain: isXmlTrue(fields.isMain?.value, false),
        likeValueParentId: isXmlTrue(fields.likeValueParentId?.value, false),
        parentColumn:
          fields.parentColumn?.value != null && String(fields.parentColumn.value).trim() !== ''
            ? String(fields.parentColumn.value).trim()
            : undefined,
        uniqueValues: isXmlTrue(fields.uniqueValues?.value, false),
        uniqueNodeValues: isXmlTrue(fields.uniqueNodeValues?.value, false),
      }
    })
    .filter((column: any) => !!column.name)
}

const findPanelById = (root: any, panelId: string): any | null => {
  if (!root || !panelId) return null
  if (Array.isArray(root)) {
    for (const item of root) {
      const found = findPanelById(item, panelId)
      if (found) return found
    }
    return null
  }
  if (typeof root !== 'object') return null

  const currentId = String((root as any)?.id ?? (root as any)?._attributes?.id ?? '').trim()
  if (currentId !== '' && currentId === panelId) {
    return root
  }

  const keys = ['main', 'header', 'footer', 'rows']
  for (const k of keys) {
    const found = findPanelById((root as any)?.[k], panelId)
    if (found) return found
  }
  return null
}

const resolveFormPanelDefinition = (formRoot: any, panelId: string): any | null => {
  if (!formRoot || !panelId) return null
  const pid = String(panelId).trim()
  if (!pid) return null
  const panels = (formRoot as any)?.panels
  if (panels && typeof panels === 'object' && !Array.isArray(panels)) {
    const fromMap =
      (panels as Record<string, unknown>)[pid] ??
      (panels as Record<string, unknown>)[String(pid)] ??
      (Number.isFinite(Number(pid)) ? (panels as Record<number, unknown>)[Number(pid)] : undefined)
    if (fromMap && typeof fromMap === 'object') {
      return fromMap
    }
  }
  return findPanelById(formRoot, pid)
}

const collectActionTargetIdsFromPanel = (panel: any): Set<string> => {
  const ids = new Set<string>()
  const visit = (node: any): void => {
    if (!node) return
    if (Array.isArray(node)) {
      node.forEach(visit)
      return
    }
    if (typeof node !== 'object') return
    const panelId = String((node as any)?.panelId ?? (node as any)?.id ?? (node as any)?._attributes?.id ?? '').trim()
    if (panelId) ids.add(panelId)
    const element = (node as any).element
    if (element && typeof element === 'object') {
      const eid = String(element?._attributes?.id ?? element?.id ?? '').trim()
      if (eid) ids.add(eid)
    }
    if ((node as any).rows) visit((node as any).rows)
    if ((node as any).main) visit((node as any).main)
    if ((node as any).header) visit((node as any).header)
    if ((node as any).footer) visit((node as any).footer)
  }
  const identificators = panel?.identificators
  if (identificators != null) {
    visit(Array.isArray(identificators) ? identificators : [identificators])
  }
  visit(panel?.rows)
  visit(panel?.main)
  visit(panel?.header)
  visit(panel?.footer)
  return ids
}

const getTableInlineActions = () => {
  const elementId =
    elementApi.id.value?.toString() ||
    String(props.config.element?._attributes?.id ?? '').trim()
  const tilePanelRef =
    props.config.element?.panel?._text ||
    (props.config.element?.panel as any)?.[0]?._text ||
    ''
  const tilePanelId = tilePanelRef != null ? String(tilePanelRef).trim() : ''
  const isTiles = elementApi.type.value === 'TILES'
  const tilePanelElementIds = (() => {
    if (!isTiles || !tilePanelId) return new Set<string>()
    const formRoot = (pageData.value as any)?.form
    const panel = resolveFormPanelDefinition(formRoot, tilePanelId)
    return collectActionTargetIdsFromPanel(panel)
  })()

  const formActions = collectFormActionsFromPageData(pageData.value) as any[]
  const sourceActions =
    pageActions.value && pageActions.value.length > 0
      ? pageActions.value
      : formActions

  return sourceActions
    .filter((action: any) => {
      const actionLevel = resolveActionLayoutLevelText(action.layout)
      const actionId = resolveActionLayoutTextId(action.layout)
      const actionPosition = resolveActionLayoutPositionText(action.layout)
      const idMatchesCurrent = actionId === elementId
      const idMatchesTilesPanel = isTiles && tilePanelId !== '' && actionId === tilePanelId
      const idMatchesTilesPanelElement =
        isTiles && tilePanelElementIds.size > 0 && tilePanelElementIds.has(actionId)

      return (
        actionLevel === 'element' &&
        (idMatchesCurrent || idMatchesTilesPanel || idMatchesTilesPanelElement) &&
        actionPosition === 'inline'
      )
    })
    .slice()
    .sort((a: any, b: any) => {
      const sortA = parseInt(a._attributes?.sort || '0')
      const sortB = parseInt(b._attributes?.sort || '0')
      return sortA - sortB
    })
}

const resolveInlineActionCellConfig = (column: any, row: any) =>
  resolveInlineActionCellConfigCore(getTableInlineActions(), column, row, getFieldValueFromRow)

const buildTableInlineActionCellPayload = (
  row: any,
  rowIndex: number,
  column: any,
  allColumns: Array<{ name: string; type: string }>,
  tableName: string,
) => {
  const resolved = resolveInlineActionCellConfig(column, row)
  if (!resolved) {
    return null
  }

  const selectedData = buildPlainRowForTableContext(row, allColumns, rowIndex, column.name, tableName)
  const boundAction = {
    ...resolved.action,
    selected: {fields: selectedData},
    command: async (event?: any) => {
      await executeTableInlineAction(resolved.action, {
        ...event,
        data: row,
        rowData: row,
        item: row,
        selectedIdx: rowIndex,
        column: column.name,
      })
    },
  }

  return {
    inlineAction: true,
    title: resolved.title,
    icon: normalizeActionImageName(resolved.action.image),
    action: boundAction,
  }
}

const flattenTreeTableBackingRowsForAction = (rows: any[]): any[] => {
  const result: any[] = []
  const walk = (list: any[]) => {
    for (const row of list) {
      result.push(row)
      const nested = extractUniDataTableRows(row?.fields?.items ?? row?.items)
      if (nested.length > 0) {
        walk(nested)
      }
    }
  }
  walk(rows)
  return result
}

const getTreeTableActionByColumnName = (rowIndexRaw: unknown, columnNameRaw: unknown) => {
  const rowIndex = Number(rowIndexRaw)
  const columnName = String(columnNameRaw ?? '')
  if (!Number.isFinite(rowIndex) || rowIndex < 0 || !columnName) {
    return null
  }

  const columns = getAllElementColumns()
  const column = columns.find((col: any) => String(col?.name ?? '') === columnName)
  if (String(column?.type || '').toUpperCase() !== 'INLINEACTION') {
    return null
  }

  const rows = flattenTreeTableBackingRowsForAction(getTreeTableBackingRows())
  const row = rows[rowIndex]
  if (!row) {
    return null
  }

  const columnList = columns
    .filter((col: any) => !!col?.name)
    .map((col: any) => ({ name: col.name, type: col.type }))
  const payload = buildTableInlineActionCellPayload(
    row,
    rowIndex,
    column,
    columnList,
    elementApi.fieldName.value,
  )
  const command = payload?.action?.command
  if (typeof command !== 'function') {
    return null
  }

  return {
    getAction: () => ({
      doAction: async (rowUniData?: any, newWindow?: boolean) => {
        void rowUniData
        const rowData = row
        await command({
          data: rowData,
          rowData,
          item: rowData,
          selectedIdx: rowIndex,
          column: columnName,
          originalEvent: newWindow ? { altKey: true } : undefined,
        })
      },
    }),
  }
}

const getTableItems = () => {
  const endMeasure = startWorkflowMeasure('ElementRenderer.getTableItems', {
    fieldName: elementApi.fieldName.value,
    elementId: elementId.value,
    elementType: elementApi.type.value,
  })
  const fieldName = elementApi.fieldName.value
  try {
    const preferRegistryFirst = elementApi.type.value === 'EDITABLE'

    // Editable should prefer local registry so in-progress edits are not overwritten by stale pageFields.
    const backendField = (pageFields.value as any)?.[fieldName]
    const registryField = elementApi.value.value
    const backendRows = extractUniDataTableRows(backendField)
    const registryRows = extractUniDataTableRows(registryField)
    const readTableEmptyFlag = (field: unknown): boolean | undefined => {
      if (!field || typeof field !== 'object' || Array.isArray(field)) return undefined
      const typed = field as { type?: string; value?: { empty?: unknown }; empty?: unknown }
      const emptyFlag =
        typed.value && typeof typed.value === 'object' && !Array.isArray(typed.value)
          ? typed.value.empty
          : typed.empty
      return typeof emptyFlag === 'boolean' ? emptyFlag : undefined
    }
    const registryEmptyFlag = readTableEmptyFlag(registryField)
    const backendEmptyFlag = readTableEmptyFlag(backendField)

    let rows: any[] = []

    // После en.reloadElement pageFields (backend) с empty:true|false — источник истины.
    // Иначе registryEmptyFlag===false оставлял stale rows и игнорировал свежий backend.
    if (backendEmptyFlag === true) {
      rows = []
    } else if (backendEmptyFlag === false) {
      rows = backendRows
    } else if (registryEmptyFlag === true) {
      rows = registryRows
    } else if (registryEmptyFlag === false) {
      rows = registryRows
    } else if (preferRegistryFirst && registryRows.length > 0) {
      rows = registryRows
    } else if (backendRows.length > 0) {
      rows = backendRows
    } else if (registryRows.length > 0) {
      rows = registryRows
    }

    // Get ALL columns (including Identificator) for data extraction
    // Headers are filtered separately for display, but data should include everything
    const allColumns = getAllElementColumns()
    if (!allColumns.length) return rows

    return rows.map((row: any, rowIndex: number) => {
      const result: Record<string, any> = {}
      // Preserve original row for hidden/system fields
      result._rowData = row
      result._rowIndex = rowIndex
      // Include all fields from the original row first
      if (row?.fields) {
        Object.keys(row.fields).forEach((fieldName: string) => {
          const fieldData = row.fields[fieldName]
          result[fieldName] = toPlainVal(extractCellValueAndType(fieldData).value)
        })
      }
      // Also include direct fields
      if (row && typeof row === 'object') {
        Object.keys(row).forEach((key: string) => {
          if (key !== 'fields' && !result.hasOwnProperty(key)) {
            result[key] = row[key]
          }
        })
      }
      allColumns.forEach((col: any) => {
        const sys = col.name
        const normalizedType = String(col?.type || '').toUpperCase()

        if (normalizedType === 'INLINEACTION') {
          result[sys] = buildTableInlineActionCellPayload(
            row,
            rowIndex,
            col,
            allColumns
              .filter((column: any) => !!column?.name)
              .map((column: any) => ({name: column.name, type: column.type})),
            fieldName,
          )
          return
        }

        if (!result.hasOwnProperty(sys)) {
          const cellFromFields = row?.fields?.[sys]
          const cellDirect = row?.[sys]

          const value =
            (cellFromFields !== undefined ? toPlainVal(extractCellValueAndType(cellFromFields).value) : undefined) ??
            (cellDirect !== undefined ? toPlainVal(extractCellValueAndType(cellDirect).value) : undefined) ??
            null
          result[sys] = value
        }
      })
      return result
    })
  } finally {
    endMeasure()
  }
}

// Helper to extract field value from UniData row structure
const getFieldValueFromRow = (row: any, fieldName: string): any => {
  if (!row) return undefined

  const fromFields = row.fields?.[fieldName]
  if (fromFields !== undefined) {
    return fromFields?.value !== undefined ? fromFields.value : fromFields
  }

  if (row[fieldName] !== undefined) {
    return row[fieldName]?.value !== undefined ? row[fieldName].value : row[fieldName]
  }

  return undefined
}

// Transform UniData row to flat object for tree node data
const applyInlineActionColumnsToFlatRow = (
  result: Record<string, any>,
  row: any,
  rowIndex: number,
  columns: any[],
  tableName: string,
) => {
  result._rowIndex = rowIndex
  result._rowData = row
  const columnList = columns
    .filter((column: any) => !!column?.name)
    .map((column: any) => ({name: column.name, type: column.type}))

  columns.forEach((col: any) => {
    if (String(col?.type || '').toUpperCase() !== 'INLINEACTION') return
    result[col.name] = buildTableInlineActionCellPayload(
      row,
      rowIndex,
      col,
      columnList,
      tableName,
    )
  })
}

const flattenRowData = (
  row: any,
  columns: any[],
  rowIndex: number,
  tableName: string,
): Record<string, any> => {
  const result: Record<string, any> = {}

  // Store original row for reference
  result._originalRow = row

  // First, include ALL fields from the original row
  if (row?.fields) {
    Object.keys(row.fields).forEach((fieldName: string) => {
      const fieldData = row.fields[fieldName]
      result[fieldName] = toPlainVal(extractCellValueAndType(fieldData).value)
    })
  }
  // Also include direct fields
  if (row && typeof row === 'object') {
    Object.keys(row).forEach((key: string) => {
      if (key !== 'fields' && !result.hasOwnProperty(key)) {
        result[key] = row[key]
      }
    })
  }

  columns.forEach((col: any) => {
    const fieldName = col.name
    if (!result.hasOwnProperty(fieldName)) {
      const value = getFieldValueFromRow(row, fieldName)
      result[fieldName] = value
    }
  })

  applyTreeTablePlatformRowFlags(row, result)

  // Extract items (children) - this is a UniDataTable
  const itemsField = getFieldValueFromRow(row, IKey.ITEMS)
  if (itemsField) {
    // items can be: { value: { rows: [...] } } or { rows: [...] } or just rows array
    const itemsRows =
      itemsField?.value?.rows || itemsField?.rows || (Array.isArray(itemsField) ? itemsField : [])
    result._items = itemsRows
  }

  applyInlineActionColumnsToFlatRow(result, row, rowIndex, columns, tableName)

  return result
}

const TREE_TABLE_KEY_JOINER = '|'

function buildTreeTableRowKey(
  row: any,
  columns: any[],
  isAllowChildren: unknown,
  parentKey: string,
  level: number,
  rowIndex: number,
): string {
  const sorted = [...columns].sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0))
  const nodeCols = sorted
    .filter((c: any) => c.uniqueNodeValues === true)
    .map((c: any) => c.name)
    .filter(Boolean)
  const leafCols = sorted
    .filter((c: any) => c.uniqueValues === true)
    .map((c: any) => c.name)
    .filter(Boolean)

  const useNode = isAllowChildren === true || isAllowChildren === 'true'
  const names: string[] = useNode ? nodeCols : leafCols

  if (names.length > 0) {
    const parts = names.map((n) => {
      const v = canonicalTreeNodeId(getFieldValueFromRow(row, n))
      return v === '' ? TREE_TABLE_UNIQUE_EMPTY_SLOT : v
    })
    return parts.join(TREE_TABLE_KEY_JOINER)
  }

  const rowId = canonicalTreeNodeId(
    getFieldValueFromRow(row, '_id') ?? getFieldValueFromRow(row, 'id'),
  )
  if (rowId !== '') {
    const base = parentKey !== '' ? parentKey : 'tt-root'
    return `${base}${TREE_TABLE_KEY_JOINER}${rowId}`
  }

  const base = parentKey !== '' ? parentKey : 'tt-root'
  return `${base}${TREE_TABLE_KEY_JOINER}L${level}${TREE_TABLE_KEY_JOINER}i${rowIndex}`
}

const transformTreeRows = (rows: any[], columns: any[], level: number = 0, parentKey: string = ''): any[] => {
  if (!Array.isArray(rows) || rows.length === 0) return []

  const tableName = elementApi.fieldName.value

  return rows.map((row: any, rowIndex: number) => {
    const data = flattenRowData(row, columns, rowIndex, tableName)
    const key = buildTreeTableRowKey(row, columns, data.isAllowChildren, parentKey, level, rowIndex)
    attachTreeTableNodeKey(data, key)

    const rawId = getFieldValueFromRow(row, '_id') ?? getFieldValueFromRow(row, 'id')

    // Get label (for tree display)
    const label =
      getFieldValueFromRow(row, 'subject') ||
      getFieldValueFromRow(row, 'name') ||
      getFieldValueFromRow(row, 'description') ||
      String(rawId ?? key)

    // Process children if items field exists
    let children: any[] | undefined = undefined
    if (data._items && Array.isArray(data._items) && data._items.length > 0) {
      children = transformTreeRows(data._items, columns, level + 1, key)
    }

    // Only include children if isAllowChildren is true OR children exist
    const node: any = {
      key,
      label: String(label),
      data: data,
      isAllowChildren: data.isAllowChildren,
      expandByDefault: data.expandByDefault,
      hideCheckbox: data.hideCheckbox,
      [IKey.TT_FORBID_TO_SELECT_NODE]: data[IKey.TT_FORBID_TO_SELECT_NODE],
      [IKey.TT_IS_DROP_TARGET]: data[IKey.TT_IS_DROP_TARGET],
    }

    if (children && children.length > 0) {
      node.children = children
    }

    return node
  })
}

/** Сырой корень UniDataTable: тот же выбор, что у getTreeTableItems (реестр vs pageFields). */
const getTreeTableBackingRows = (): any[] => {
  const fieldName = elementApi.fieldName.value
  let pageRows: any[] = []
  const backendRows = (pageFields.value as any)?.[fieldName]?.value?.rows
  if (Array.isArray(backendRows)) {
    pageRows = backendRows
  } else {
    const rawField = (pageFields.value as any)?.[fieldName]
    if (rawField && typeof rawField === 'object') {
      const nested = (rawField.value as any)?.rows ?? (rawField.rows as any)
      if (Array.isArray(nested)) {
        pageRows = nested
      }
    }
  }

  const regVal = elementApi.value.value
  const registryRows = extractUniDataTableRows(regVal)
  let registryEmptyFlag: boolean | undefined
  if (regVal && typeof regVal === 'object' && !Array.isArray(regVal)) {
    const direct = (regVal as { empty?: unknown }).empty
    const nested = (regVal as { value?: { empty?: unknown } }).value?.empty
    if (typeof direct === 'boolean') registryEmptyFlag = direct
    else if (typeof nested === 'boolean') registryEmptyFlag = nested
  }

  const pageField = (pageFields.value as any)?.[fieldName]
  let pageEmptyFlag: boolean | undefined
  if (pageField && typeof pageField === 'object' && !Array.isArray(pageField)) {
    const nested = (pageField as { value?: { empty?: unknown } }).value?.empty
    const direct = (pageField as { empty?: unknown }).empty
    if (typeof nested === 'boolean') pageEmptyFlag = nested
    else if (typeof direct === 'boolean') pageEmptyFlag = direct
  }
  // Reload: authoritative page empty важнее stale registry (в т.ч. empty:false).
  if (pageEmptyFlag === true) return []
  if (pageEmptyFlag === false && pageRows.length > 0) return pageRows

  return resolveTreeTableBackingRows(pageRows, registryRows, {
    registryEmptyFlag,
  }) as any[]
}

const getTreeTableItems = () => {
  // Use all columns for data extraction (including Identificator)
  const columns = getAllElementColumns()
  const rows = getTreeTableBackingRows()
  // Transform rows to TreeNode format
  if (rows.length === 0) return []

  return transformTreeRows(rows, columns)
}

const getTableHeaders = () => {
  const columns = getElementColumns()
  if (columns.length) {
    return columns
      .filter((col: any) => {
        // Hide columns with type = Identificator
        if (col.type === 'Identificator') return false
        if (col.defVisible === false) return false
        const condition = String(col.condition ?? '').trim()
        if (condition && columnConditionVisibility.value[col.name] !== true) {
          return false
        }
        return true
      })
      .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0))
      .map((col: any) => {
        const normalizedType = (col.type || '').toUpperCase()
        const isInlineAction = normalizedType === 'INLINEACTION'

        return {
          name: isInlineAction ? '' : col.description || col.name,
          value: col.name,
          isSort: isInlineAction ? false : !!col.isSort,
          width: col.width || undefined,
          constantColumnWidth: col.constantColumnWidth,
          align: col.align || undefined,
          type: col.type || undefined,
          format: col.format || undefined,
          isReadOnly: !!col.isReadOnly,
          defVisible: col.defVisible,
          isPrimaryKey: col.isPrimaryKey,
          isMain: col.isMain,
          isInlineAction: isInlineAction,
          columnElementId: col.elementId,
          listboxCatalog: col.listboxCatalog,
        }
      })
  }

  // Fallback to first row keys
  const items = getTableItems()
  const first = items && items.length > 0 ? items[0] : null
  if (!first || typeof first !== 'object') return []
  return Object.keys(first).map((key) => ({name: key, value: key}))
}

const parseElementColumnsFromConfig = () => {
  const parseColumnOrder = (value: unknown): number | undefined => {
    if (value == null || value === '') return undefined
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }

  const columns = props.config.element?.columns
  if (!columns?.column) return []
  const arr = Array.isArray(columns.column) ? columns.column : [columns.column]
  return arr
    .filter((c: any) => c != null)
    .map((c: any, index: number) => ({
      swingOrderIndex: index,
      description: c.description?._text,
      order: parseColumnOrder(c.order?._text ?? c._attributes?.order),
      type: c.type?._text,
      name: c.name?._text,
      elementId: c._attributes?.id != null ? String(c._attributes.id) : c.id?._text != null ? String(c.id._text) : undefined,
      format: c.format?._text,
      isReadOnly: c.isReadOnly?._text === 'true' || c.isReadOnly?._text === '1',
      isSort: c.isSort?._text === 'true' || c.isSort?._text === '1',
      width: c.width?._text,
      constantColumnWidth:
        c.constant_column_width?._text === 'true' ||
        c.constant_column_width?._text === '1',
      align: c.align?._text,
      defVisible: c.defVisible?._text === '1' || c.defVisible?._text === 'true',
      condition: String(c.condition?._text ?? c.condition ?? '').trim() || undefined,
      isPrimaryKey: c.isPrimaryKey?._text === 'true' || c.isPrimaryKey?._text === '1',
      isMain: c.isMain?._text === 'true' || c.isMain?._text === '1',
      likeValueParentId:
        c.likeValueParentId?._text === '1' || c.likeValueParentId?._text === 'true',
      parentColumn: c.parentColumn?._text,
      uniqueValues: isXmlTrue(c.uniqueValues?._text, false),
      uniqueNodeValues: isXmlTrue(c.uniqueNodeValues?._text, false),
      listboxCatalog: parseListboxCatalogFromColumnMeta(c),
    }))
    .filter((c: any) => !!c.name)
}

// Get ALL columns - used for data extraction
const buildFallbackColumnsFromFirstRow = () => {
  const tableName = elementApi.fieldName.value
  const backendRows = (pageFields.value as any)?.[tableName]?.value?.rows
  const first = Array.isArray(backendRows) && backendRows.length ? backendRows[0] : null
  const fieldKeys = first?.fields ? Object.keys(first.fields) : []
  return fieldKeys.map((key: string) => ({
    name: key,
    description: key,
    order: 0,
    type: 'Text',
    format: undefined,
    isReadOnly: false,
    isSort: false,
    width: undefined,
    align: undefined,
    defVisible: true,
    isPrimaryKey: false,
    isMain: false,
    likeValueParentId: false,
    parentColumn: undefined,
    uniqueValues: false,
    uniqueNodeValues: false,
  }))
}

const resolveMergedElementColumns = () => {
  const parsed = parseElementColumnsFromConfig()
  const runtimeColumns = getRuntimeTableColumns()
  if (parsed.length && runtimeColumns.length) {
    return mergeTreeTableExportColumns(parsed, runtimeColumns)
  }
  if (runtimeColumns.length) return runtimeColumns
  if (parsed.length) return parsed
  return buildFallbackColumnsFromFirstRow()
}

const getAllElementColumns = () => resolveMergedElementColumns()

/** Merged XML + runtime columns для expand/reloadNode/getData */
const getTreeTableContractColumns = () =>
  mergeTreeTableExportColumns(parseElementColumnsFromConfig(), getRuntimeTableColumns())

// Get columns filtered for display (excludes Identificator and hidden columns) - used for headers
const getElementColumns = () => resolveMergedElementColumns()

let lastTableColumnConditionEvaluation = 0

const tableColumnsWithConditions = computed(() => {
  const elementType = elementApi.type.value
  if (
    elementType !== 'TABLE' &&
    elementType !== 'EDITABLE' &&
    elementType !== 'TREETABLE' &&
    elementType !== 'GROUPTABLE'
  ) {
    return []
  }
  return parseElementColumnsFromConfig().filter(
    (col: any) => String(col.condition ?? '').trim() !== '',
  )
})

const hasTableColumnConditions = computed(() => tableColumnsWithConditions.value.length > 0)

const refreshTableColumnConditions = async () => {
  const columns = tableColumnsWithConditions.value
  if (!columns.length) {
    columnConditionVisibility.value = {}
    return
  }

  const evaluationId = ++lastTableColumnConditionEvaluation
  const next: Record<string, boolean> = {}

  await Promise.all(
    columns.map(async (col: any) => {
      next[col.name] = await evaluateTableColumnCondition({
        condition: col.condition,
        pageData: pageData.value,
        formProvider: formProvider as Parameters<typeof evaluateTableColumnCondition>[0]['formProvider'],
        formId: elementContext?.formId ?? props.formPath?.formId,
        panelId: elementContext?.panelId ?? props.formPath?.panelId,
        elementId: elementId.value ?? undefined,
        measureLabel: 'ElementRenderer.tableColumn.condition.evaluate',
      })
    }),
  )

  if (evaluationId !== lastTableColumnConditionEvaluation) {
    return
  }
  columnConditionVisibility.value = next
}

watch(
  [
    hasTableColumnConditions,
    () => pageData.value,
    () => elementId.value,
    () => elementContext?.formId ?? props.formPath?.formId,
    () => elementContext?.panelId ?? props.formPath?.panelId,
  ],
  () => {
    if (!hasTableColumnConditions.value) {
      columnConditionVisibility.value = {}
      return
    }
    void refreshTableColumnConditions()
  },
  { immediate: true, flush: 'post' },
)

watch(
  () => (hasTableColumnConditions.value ? appStore.pagesDataRevision : -1),
  () => {
    if (!hasTableColumnConditions.value) return
    void refreshTableColumnConditions()
  },
  { flush: 'post' },
)

const getTableMeta = (preparedItemsCount?: number) => {
  const tableName = elementApi.fieldName.value
  const params = (pageData.value as any)?.uniData?.clientData?.params || {}
  const paginationKeys = tablePaginationFieldKeys(tableName)
  const readPaginationScalar = (key: string): number => {
    const fromFields = Number(readReloadFieldScalar((pageFields.value as any)?.[key])) || 0
    const fromParams = Number(params?.[key]) || 0
    // pageFields — актуальнее после client-side rollback (params могут отставать).
    return fromFields || fromParams || 0
  }
  let page = readPaginationScalar(paginationKeys.page) || 1

  const savedItemsPerPage = getSavedSetting('itemsPerPage')
  const maxOnPage = readPaginationScalar(paginationKeys.maxOnPage) || savedItemsPerPage || 50

  const activeClamp = tablePaginationEmptyPageClamp.value
  if (activeClamp && activeClamp.maxOnPage === maxOnPage) {
    page = Math.min(page, activeClamp.page)
  }

  // Get total items count from backend (tableName + "__size")
  // This is the total count across all pages, not just current page
  // Check pageFields first (where reloadElement response stores it)
  const sizeFieldName = paginationKeys.size
  const sizeFieldFromFields = (pageFields.value as any)?.[sizeFieldName]

  // Also check params as fallback
  const sizeFieldFromParams = params?.[sizeFieldName]

  // Try to extract the value from different possible structures
  let totalItems: number | null = null

  if (sizeFieldFromFields) {
    if (typeof sizeFieldFromFields === 'number') {
      totalItems = sizeFieldFromFields
    } else if (sizeFieldFromFields?.value !== undefined && sizeFieldFromFields?.value !== null) {
      totalItems = Number(sizeFieldFromFields.value)
    } else if (typeof sizeFieldFromFields === 'string') {
      const parsed = Number(sizeFieldFromFields)
      if (!isNaN(parsed)) totalItems = parsed
    }
  }

  if (totalItems === null && sizeFieldFromParams !== undefined && sizeFieldFromParams !== null) {
    if (typeof sizeFieldFromParams === 'number') {
      totalItems = sizeFieldFromParams
    } else if (typeof sizeFieldFromParams === 'string') {
      const parsed = Number(sizeFieldFromParams)
      if (!isNaN(parsed)) totalItems = parsed
    }
  }

  // Also check if size might be in the table data structure itself
  if (totalItems === null) {
    const tableData = (pageFields.value as any)?.[tableName]
    if (tableData?.value?.size !== undefined) {
      totalItems = Number(tableData.value.size)
    } else if (tableData?.size !== undefined) {
      totalItems = Number(tableData.size)
    } else if (tableData?.value && typeof tableData.value === 'object') {
      // Check if size is a property of the value object
      const valueObj = tableData.value
      if (valueObj.size !== undefined) {
        totalItems = Number(valueObj.size)
      } else if (valueObj._size !== undefined) {
        totalItems = Number(valueObj._size)
      } else if (valueObj.totalSize !== undefined) {
        totalItems = Number(valueObj.totalSize)
      } else if (valueObj.total !== undefined) {
        totalItems = Number(valueObj.total)
      }
    }
  }

  // If still not found, check if backend sends it in a different format
  // Some backends might send total count in the rows array metadata
  if (totalItems === null) {
    const tableData = (pageFields.value as any)?.[tableName]
    const rows = tableData?.value?.rows || tableData?.rows
    if (Array.isArray(rows) && rows.length > 0) {
      // Check if first row has metadata with total
      const firstRow = rows[0]
      if (firstRow?._total !== undefined) {
        totalItems = Number(firstRow._total)
      } else if (firstRow?.total !== undefined) {
        totalItems = Number(firstRow.total)
      }
    }
  }

  // If we don't have totalItems from backend, use raw row count. Pagination does not need
  // the fully transformed table items, and transforming them here duplicates render work.
  const currentItemsCount =
    preparedItemsCount ??
    (() => {
      const backendCount = extractUniDataTableRows((pageFields.value as any)?.[tableName]).length
      if (backendCount > 0) return backendCount
      return extractUniDataTableRows(elementApi.value.value).length
    })()

  const paginationFallback = resolveTablePaginationMetaFallback({
    page,
    maxOnPage,
    currentItemsCount,
    totalItemsFromBackend:
      activeClamp && activeClamp.maxOnPage === maxOnPage ? activeClamp.totalItems : totalItems,
  })
  const effectiveTotalItems = paginationFallback.totalItems
  const calculatedPages =
    activeClamp && activeClamp.maxOnPage === maxOnPage ? activeClamp.pages : paginationFallback.pages

  return {
    page,
    pages: calculatedPages,
    itemsPerPage: maxOnPage,
    sort: {column: '', direction: 'asc'},
    totalItems: effectiveTotalItems,
    totalItemsBackEnd:
      activeClamp && activeClamp.maxOnPage === maxOnPage ? activeClamp.totalItems : totalItems,
  }
}

/**
 * Unwrap Vue ref to plain value (one level).
 * TODO: перенести в утилиты
 */
function toPlainVal(val: unknown): unknown {
  const peeled = unwrapScriptValue(val)
  if (peeled != null && typeof peeled === 'object' && (peeled as any).__v_isRef === true)
    return toPlainVal((peeled as any).value)
  if (
    peeled != null &&
    typeof peeled === 'object' &&
    !('value' in (peeled as any)) &&
    ('_value' in (peeled as any) || '_rawValue' in (peeled as any))
  ) {
    const innerValue = (peeled as any)._value ?? (peeled as any)._rawValue
    if (innerValue === null || ['string', 'number', 'boolean'].includes(typeof innerValue)) {
      return innerValue
    }
  }
  if (peeled != null && typeof peeled === 'object' && 'value' in peeled && Object.keys(peeled as object).length <= 2)
    return (peeled as any).value
  return peeled
}

function hasDisplayValue(val: unknown) {
  return val !== undefined && val !== null && !(typeof val === 'string' && val === '')
}

function extractCellValueAndType(cell: unknown): { value: unknown; type?: string } {
  const peeled = unwrapScriptValue(cell)
  if (peeled != null && typeof peeled === 'object' && (peeled as any).__v_isRef === true) {
    return extractCellValueAndType((peeled as any).value)
  }

  if (peeled == null || typeof peeled !== 'object') {
    return {value: peeled}
  }

  const typedCell = peeled as any
  const nestedType = typeof typedCell.type === 'string' ? typedCell.type : undefined

  if ('value' in typedCell) {
    const nested = extractCellValueAndType(typedCell.value)
    const fallbackText =
      typeof typedCell._text === 'string' && typedCell._text !== ''
        ? typedCell._text
        : typeof typedCell.text === 'string' && typedCell.text !== ''
          ? typedCell.text
          : undefined
    return {
      value: hasDisplayValue(nested.value) ? nested.value : fallbackText,
      type: nested.type ?? nestedType,
    }
  }

  if (typeof typedCell._text === 'string') {
    return {value: typedCell._text, type: nestedType}
  }

  if (typeof typedCell.text === 'string') {
    return {value: typedCell.text, type: nestedType}
  }

  const internalValue = typedCell._value ?? typedCell._rawValue
  if (internalValue === null || ['string', 'number', 'boolean'].includes(typeof internalValue)) {
    return {value: internalValue, type: nestedType}
  }

  return {value: peeled, type: nestedType}
}

function resolveRowCell(row: Record<string, any>, name: string): unknown {
  const rowData = row._rowData
  const candidates = [
    row[name],
    row.fields?.[name],
    rowData?.fields?.[name],
    rowData?.[name],
  ]

  for (const candidate of candidates) {
    if (candidate !== undefined) return candidate
  }

  const rowPath = name.includes('.') ? row[name] ?? name.split('.').reduce((acc: any, key: string) => acc?.[key], row) : undefined
  if (rowPath !== undefined) return rowPath

  if (rowData && name.includes('.')) {
    return name.split('.').reduce((acc: any, key: string) => acc?.[key], rowData)
  }

  return undefined
}

/** Resolve value for a column from row */
function getColumnValue(row: Record<string, any>, name: string): { value: unknown; type?: string } {
  return extractCellValueAndType(resolveRowCell(row, name))
}

function normalizeIdentityPart(value: unknown): string {
  const plain = toPlainVal(value)
  return plain === undefined || plain === null ? '' : String(plain)
}

function getRowIdentityValue(row: Record<string, any> | null | undefined): unknown {
  if (!row || typeof row !== 'object') return undefined

  const id =
    row._rowData?.fields?._id?.value ??
    row._rowData?._id ??
    row.fields?._id?.value ??
    row._id ??
    row.requestId ??
    row.id

  const typeId =
    row._rowData?.fields?._typeId?.value ??
    row._rowData?._typeId ??
    row.fields?._typeId?.value ??
    row._typeId ??
    ''

  const versionId =
    row._rowData?.fields?._versionId?.value ??
    row._rowData?._versionId ??
    row.fields?._versionId?.value ??
    row._versionId ??
    ''

  const normalizedId = normalizeIdentityPart(id)
  if (normalizedId === '') return undefined

  return `${normalizedId}|${normalizeIdentityPart(typeId)}|${normalizeIdentityPart(versionId)}`
}

function flattenTableContextItems(items: Record<string, any>[] | null | undefined): Record<string, any>[] {
  if (!Array.isArray(items) || items.length === 0) return []

  const flat: Record<string, any>[] = []
  const visit = (list: Record<string, any>[]) => {
    for (const item of list) {
      flat.push(item)
      if (Array.isArray((item as any).children) && (item as any).children.length > 0) {
        visit((item as any).children)
      }
    }
  }

  visit(items)
  return flat
}

function getTableContextItemData(item: Record<string, any> | null | undefined): Record<string, any> | null | undefined {
  if (!item || typeof item !== 'object') return item
  return item.data && typeof item.data === 'object' ? item.data : item
}

function findTableContextRowIndex(
  items: Record<string, any>[] | null | undefined,
  rowData: Record<string, any> | null | undefined,
): number {
  if (!Array.isArray(items) || !items.length || !rowData) return -1

  const flatItems = flattenTableContextItems(items)
  const rowIdentity = getRowIdentityValue(rowData)
  const matchedIdx = flatItems.findIndex((item) => {
    const itemData = getTableContextItemData(item)
    const itemIdentity = getRowIdentityValue(itemData)
    if (itemData === rowData) return true

    const itemRowData = itemData._rowData
    const targetRowData = rowData._rowData
    if (itemRowData != null && targetRowData != null && itemRowData === targetRowData) {
      return true
    }

    const itemOriginalRow = itemData._originalRow
    if (itemOriginalRow != null) {
      if (itemOriginalRow === rowData) return true
      const targetOriginalRow = rowData._originalRow
      if (targetOriginalRow != null && itemOriginalRow === targetOriginalRow) {
        return true
      }
    }

    return rowIdentity != null && itemIdentity != null && String(itemIdentity) === String(rowIdentity)
  })

  return matchedIdx >= 0 ? matchedIdx : -1
}

function resolveTableContextSourceRow(
  items: Record<string, any>[],
  rowData: Record<string, any> | null,
  selectedIdx: number,
) {
  const flatItems = flattenTableContextItems(items)
  const isTreeTable = elementApi.type.value === 'TREETABLE'

  if (isTreeTable) {
    return resolveTreeTableContextSourceRowByIdentity(items, rowData, selectedIdx)
  }

  if (selectedIdx >= 0 && selectedIdx < flatItems.length) {
    return {row: getTableContextItemData(flatItems[selectedIdx]), selectedIdx}
  }

  if (!flatItems.length || !rowData) {
    return {row: rowData, selectedIdx}
  }

  const matchedIdx = findTableContextRowIndex(items, rowData)

  if (matchedIdx >= 0) {
    return {row: getTableContextItemData(flatItems[matchedIdx]), selectedIdx: matchedIdx}
  }

  return {row: rowData, selectedIdx}
}

/** Build plain row for table context action */
function normalizeClientFieldType(valueType: string | undefined, value: unknown): string {
  const normalized = String(valueType || '').trim()
  if (normalized === 'Checkbox') return 'Boolean'
  if (
    normalized &&
    normalized !== 'Text' &&
    normalized !== 'Identificator'
  ) {
    return normalized
  }
  if (
    value != null &&
    typeof value === 'object' &&
    (Array.isArray((value as any).rows) || Array.isArray((value as any).value?.rows))
  ) {
    return 'UniDataTable'
  }
  if (typeof value === 'boolean') return 'Boolean'
  if (typeof value === 'number') return Number.isInteger(value) ? 'Long' : 'Double'
  return 'String'
}

function toClientFieldValue(value: unknown, valueType?: string, fieldName?: string): unknown {
  const v = toPlainVal(value)

  const normalizedType = String(valueType || '').trim()
  const isTreeBooleanField =
    fieldName === IKey.TT_HIDE_CHECKBOX ||
    fieldName === IKey.TT_IS_ALLOW_CHILDREN ||
    fieldName === IKey.EXPAND_BY_DEFAULT ||
    fieldName === IKey.TT_FORBID_TO_SELECT_NODE ||
    fieldName === IKey.TT_IS_DROP_TARGET
  const isUniDataTableValue =
    normalizedType === 'UniDataTable' ||
    normalizedType === 'UniDataTableFields' ||
    (v != null &&
      typeof v === 'object' &&
      (Array.isArray((v as any).rows) || Array.isArray((v as any).value?.rows)))

  if (v === null || v === undefined) {
    if (normalizedType === 'Boolean' || normalizedType === 'Checkbox' || isTreeBooleanField) {
      return false
    }
    if (isUniDataTableValue) {
      return {rows: []}
    }
    return ''
  }

  if (v === 'undefined') {
    return ''
  }

  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return v

  if (isUniDataTableValue) {
    return v
  }

  return String(v)
}

function buildPlainRowForTableContext(
  row: Record<string, any> | null,
  columns: { name: string; type?: string }[],
  selectedIdx: number,
  columnName: string,
  tableName: string,
): Record<string, unknown> {
  const plain: Record<string, unknown> = {}
  if (!row || typeof row !== 'object') return plain
  const fields: Record<string, { value: unknown; type: string }> = {}

  for (const column of columns) {
    const name = column.name
    const raw = getColumnValue(row, name)
    const effectiveType = raw.type ?? column.type
    const plainValue = toClientFieldValue(raw.value, effectiveType, name)
    plain[name] = plainValue
    fields[name] = {value: plainValue, type: normalizeClientFieldType(effectiveType, plainValue)}
  }

  const rd = row._rowData
  const idCell = extractCellValueAndType(
    rd?.fields?._id ?? rd?._id ?? row.fields?._id ?? row._id ?? row.requestId ?? row.id,
  )
  const typeIdCell = extractCellValueAndType(
    rd?.fields?._typeId ?? rd?._typeId ?? row.fields?._typeId ?? row._typeId,
  )
  const versionIdCell = extractCellValueAndType(
    rd?.fields?._versionId ?? rd?._versionId ?? row.fields?._versionId ?? row._versionId ?? null,
  )
  const idVal = toPlainVal(idCell.value)
  const typeIdVal = toPlainVal(typeIdCell.value)
  const versionIdVal = toPlainVal(versionIdCell.value)
  const asPrim = (v: unknown) =>
    v == null ? v : typeof v === 'object' && 'value' in (v as object) ? (v as any).value : (typeof v === 'object' ? String(v) : v)
  if (idVal !== undefined && idVal !== null) {
    plain._id = asPrim(idVal)
    fields._id = {value: plain._id, type: normalizeClientFieldType(idCell.type, plain._id)}
  }
  if (typeIdVal !== undefined && typeIdVal !== null) {
    plain._typeId = asPrim(typeIdVal)
    fields._typeId = {
      value: plain._typeId,
      type: normalizeClientFieldType(typeIdCell.type, plain._typeId)
    }
  }
  plain._versionId = versionIdVal === undefined || versionIdVal === null ? null : asPrim(versionIdVal)
  fields._versionId = {
    value: plain._versionId,
    type: normalizeClientFieldType(versionIdCell.type, plain._versionId)
  }

  if (tableName) {
    plain[`${tableName}__${IKey.ROW_NUM}`] = selectedIdx
    plain[`${tableName}__${IKey.COLUMN}`] = columnName || ''
    fields[`${tableName}__${IKey.ROW_NUM}`] = {
      value: plain[`${tableName}__${IKey.ROW_NUM}`],
      type: 'Long'
    }
    fields[`${tableName}__${IKey.COLUMN}`] = {
      value: plain[`${tableName}__${IKey.COLUMN}`],
      type: 'String'
    }
  }
  plain.fields = fields
  return plain
}

function buildTreeTableRootRow(columns: { name: string; type?: string }[]): Record<string, unknown> {
  const root: Record<string, unknown> = {
    row__checked: '0',
    [IKey.TT_IS_DROP_TARGET]: '1',
    name: 'rootNode',
    _id: '',
    parentId: '',
    group: '',
  }

  for (const column of columns) {
    const name = column?.name
    if (!name || name in root) continue
    root[name] = ''
  }

  return root
}

function toTreeTableParentUniDataRow(
  row: Record<string, unknown>,
  columns: { name: string; type?: string }[],
): { fields: Record<string, { value: unknown; type: string }>; errors: Record<string, never>; attributes: Record<string, never> } {
  const normalizedRow = row && typeof row === 'object' ? row : {}
  const rowFields: Record<string, { value: unknown; type: string }> = {}
  const typedRowFields =
    normalizedRow.fields && typeof normalizedRow.fields === 'object'
      ? (normalizedRow.fields as Record<string, { value: unknown; type: string }>)
      : null

  if (typedRowFields) {
    Object.keys(typedRowFields).forEach((fieldName) => {
      const field = typedRowFields[fieldName]
      if (!field || typeof field !== 'object') return
      rowFields[fieldName] = {
        value: field.value,
        type: field.type || 'String',
      }
    })
  } else {
    Object.keys(normalizedRow).forEach((fieldName) => {
      if (fieldName === 'fields') return
      const value = (normalizedRow as Record<string, unknown>)[fieldName]
      const columnType = columns.find((column) => column.name === fieldName)?.type
      rowFields[fieldName] = {
        value,
        type: normalizeClientFieldType(columnType, value),
      }
    })
  }

  if (!('row__checked' in rowFields)) {
    rowFields.row__checked = { value: '0', type: 'String' }
  }

  return {
    fields: rowFields,
    errors: {},
    attributes: {},
  }
}

function buildTreeTableParentsPayload(
  items: Record<string, any>[],
  targetRow: Record<string, any> | null,
  columns: { name: string; type?: string }[],
): Record<string, unknown> {
  if (!Array.isArray(items) || items.length === 0 || !targetRow) {
    const rootRow = toTreeTableParentUniDataRow(buildTreeTableRootRow(columns), columns)
    return {
      attributes: {},
      columns: [],
      rows: [rootRow],
      empty: false,
    }
  }

  const targetIdentity = getRowIdentityValue(targetRow)
  if (targetIdentity == null) {
    const rootRow = toTreeTableParentUniDataRow(buildTreeTableRootRow(columns), columns)
    return {
      attributes: {},
      columns: [],
      rows: [rootRow],
      empty: false,
    }
  }

  const ancestors = findTreeTableAncestorChain(items, targetRow)
  const parentRows = [...ancestors]
    .reverse()
    .map((ancestor) => {
      const plainParent = buildPlainRowForTableContext(ancestor, columns, -1, '', '') as Record<string, unknown>
      return toTreeTableParentUniDataRow(plainParent, columns)
    })

  parentRows.push(toTreeTableParentUniDataRow(buildTreeTableRootRow(columns), columns))
  return {
    attributes: {},
    columns: [],
    rows: parentRows,
    empty: parentRows.length === 0,
  }
}

const TREE_TABLE_INLINE_META_FIELDS = new Set([
  IKey.TT_IS_ALLOW_CHILDREN,
  IKey.EXPAND_BY_DEFAULT,
  IKey.TT_HIDE_CHECKBOX,
  IKey.TT_FORBID_TO_SELECT_NODE,
  IKey.TT_IS_DROP_TARGET,
])

function findTreeTableContextItem(
  items: Record<string, any>[],
  row: Record<string, any> | null,
  selectedIdx: number,
): Record<string, any> | null {
  return resolveTreeTableContextItemByIdentity(items, row, selectedIdx) as Record<string, any> | null
}

/** Как Swing convertNode: node.getAllowsChildren(), не сырой столбец backing. */
function resolveTreeTableInlineIsAllowChildren(
  row: Record<string, any>,
  treeItem: Record<string, any> | null,
  items: Record<string, any>[],
): boolean {
  if (treeItem) {
    const fromItem = (treeItem as { isAllowChildren?: unknown }).isAllowChildren
    if (fromItem === true || fromItem === 'true' || fromItem === 1 || fromItem === '1') {
      return true
    }
    if (fromItem === false || fromItem === 'false' || fromItem === 0 || fromItem === '0') {
      return false
    }
    const children = Array.isArray((treeItem as { children?: unknown[] }).children)
      ? (treeItem as { children: unknown[] }).children
      : []
    if (children.length > 0) return true
  }

  const explicitCountRaw =
    getColumnValue(row, '_countChildren').value ?? getColumnValue(row, 'countChildren').value
  if (explicitCountRaw !== undefined && explicitCountRaw !== null && String(explicitCountRaw) !== '') {
    const explicitCount = Number(explicitCountRaw)
    if (Number.isFinite(explicitCount) && explicitCount > 0) return true
  }

  const rowFlag = getColumnValue(row, 'isAllowChildren').value
  return rowFlag === true || rowFlag === 'true' || rowFlag === 1 || rowFlag === '1'
}

function buildTreeTableActionContextRow(
  row: Record<string, any> | null,
  items: Record<string, any>[],
  columns: { name: string; type?: string }[],
  selectedIdx: number,
  columnName: string,
  tableName: string,
): Record<string, unknown> {
  const treeItem = findTreeTableContextItem(items, row, selectedIdx)
  const exportColumns = columns.filter((c) => !TREE_TABLE_INLINE_META_FIELDS.has(c.name))
  const plain = buildPlainRowForTableContext(
    row,
    exportColumns,
    selectedIdx,
    columnName,
    tableName,
  ) as Record<string, unknown>
  if (!row || typeof row !== 'object') {
    return plain
  }

  const fields =
    plain.fields && typeof plain.fields === 'object'
      ? {...(plain.fields as Record<string, { value: unknown; type: string }>)}
      : {}

  const rowCheckedRaw = getColumnValue(row, 'row__checked').value
  const rowChecked = rowCheckedRaw === undefined || rowCheckedRaw === null || rowCheckedRaw === '' ? '0' : rowCheckedRaw
  plain.row__checked = rowChecked
  fields.row__checked = {
    value: rowChecked,
    type: typeof rowChecked === 'boolean' ? 'Boolean' : 'String',
  }

  const allowsChildren = resolveTreeTableInlineIsAllowChildren(row, treeItem, items)
  plain.isAllowChildren = allowsChildren
  fields.isAllowChildren = {
    value: allowsChildren,
    type: 'Boolean',
  }

  const resolveTreeTableActionChildCount = (
    treeItem: Record<string, any> | null,
    treeRow: Record<string, any>,
    nodeAllowsChildren: boolean,
  ): number => {
    // Swing convertNode: leaf → 0, loaded → getChildCount(), иначе -1 (не backing _countChildren).
    if (treeItem) {
      const children = Array.isArray((treeItem as { children?: unknown[] }).children)
        ? (treeItem as { children: unknown[] }).children
        : []
      if (children.length > 0) {
        return children.length
      }
      if ((treeItem as { leaf?: boolean }).leaf === true || !nodeAllowsChildren) {
        return 0
      }
      if (nodeAllowsChildren) {
        return -1
      }
    }

    const rowItems = (treeRow as any)._items
    if (Array.isArray(rowItems)) {
      return rowItems.length
    }

    if (!nodeAllowsChildren) {
      return 0
    }

    return -1
  }

  const countChildren = resolveTreeTableActionChildCount(treeItem, row, allowsChildren)
  plain._countChildren = countChildren
  fields._countChildren = {
    value: countChildren,
    type: 'Long',
  }

  plain.rowNum = selectedIdx
  fields.rowNum = {
    value: selectedIdx,
    type: 'Long',
  }

  const parentIdValue = resolveTreeTableParentIdValue(items, row)
  const parentIdType = columns.find((column) => column.name === '_id')?.type
  const rowParentIdValue =
    getColumnValue(row, 'parentId').value ??
    (plain as { parentId?: unknown }).parentId
  const effectiveParentIdValue =
    parentIdValue !== '' && parentIdValue != null ? parentIdValue : rowParentIdValue
  if (effectiveParentIdValue !== '' && effectiveParentIdValue != null) {
    plain.parentId = effectiveParentIdValue
    fields.parentId = {
      value: effectiveParentIdValue,
      type: normalizeClientFieldType(parentIdType, effectiveParentIdValue),
    }
  }

  const parents = buildTreeTableParentsPayload(items, row, columns)
  plain.parents = parents
  fields.parents = {
    value: parents,
    type: 'UniDataTable',
  }

  const exportColumnNames = new Set(exportColumns.map((c) => c.name))
  if (!exportColumnNames.has('_typeId')) {
    delete plain._typeId
    delete fields._typeId
  }
  if (!exportColumnNames.has('_versionId')) {
    delete plain._versionId
    delete fields._versionId
  }

  plain.fields = fields
  return plain
}

function unwrapTypedFieldMap(src: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(src)) {
    if (v && typeof v === 'object' && 'value' in (v as object) && 'type' in (v as object)) {
      out[k] = (v as { value: unknown }).value
    } else {
      out[k] = v
    }
  }
  return out
}

function unwrapSelectedNodeDataFields(raw: unknown): Record<string, unknown> | null {
  if (raw == null || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  if (r.fields && typeof r.fields === 'object') {
    return unwrapTypedFieldMap(r.fields as Record<string, unknown>)
  }
  return unwrapTypedFieldMap(r)
}

function rowsMatchTreeReloadSelection(
  selected: Record<string, unknown>,
  treeRow: Record<string, unknown>,
  columns: any[],
): boolean {
  const sorted = [...columns].sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0))
  const nodeCols = sorted
    .filter((c: any) => c.uniqueNodeValues === true)
    .map((c: any) => c.name)
    .filter(Boolean)
  const leafCols = sorted
    .filter((c: any) => c.uniqueValues === true)
    .map((c: any) => c.name)
    .filter(Boolean)
  const useNode = treeRow.isAllowChildren === true || treeRow.isAllowChildren === 'true'
  const names = useNode ? nodeCols : leafCols
  const keysToCompare = names.length > 0 ? names : ['_id']
  for (const name of keysToCompare) {
    const va = canonicalTreeNodeId(getColumnValue(selected as any, name).value)
    const vb = canonicalTreeNodeId(getColumnValue(treeRow as any, name).value)
    if (va === '' && vb === '') continue
    if (va !== vb) return false
  }
  return keysToCompare.length > 0
}

function buildReloadNodePayloadFields(rowNum: number, needReloadParent: boolean): Record<string, any> {
  const tableName = elementApi.fieldName.value
  if (!tableName) return {}
  const treeInst = componentRef.value as any
  const flat: FlatVisibleTreeRow[] = treeInst?.getFlattenVisibleRowsForReload?.() ?? []
  const resolved = resolveSwingReloadNodeTarget(flat, rowNum, needReloadParent)
  if (resolved.kind === 'full') {
    return buildFullTreeReloadPayload(tableName)
  }
  const rowData = resolved.row
  const contractColumns = getTreeTableContractColumns()
  const expandFields = buildTreeTableExpandParamsFields(rowData, contractColumns)
  const childCount = extractTreeTableServerChildCount(rowData)
  const nk = String((rowData as any)?.[TREE_TABLE_NODE_KEY_FIELD] ?? '').trim()
  const selKeys = (treeInst?.getCheckboxSelectionKeysSnapshot?.() ?? {}) as Record<string, unknown>
  const withCb = isXmlTrue(props?.config?.element?.withCheckBoxes?._text, false)
  const rowChecked = withCb && nk ? selectionKeysIndicatesChecked(selKeys, nk) : false
  return buildTreeTableLazyExpandReloadPayload(tableName, resolved.rowNum, expandFields, {
    childCount,
    rowChecked,
  })
}

/** Снимок поля для merge: метаданные из pageFields, rows — те же, что у MTreeTable (реестр при nestedReg > nestedPage). */
function resolveTreeTableMergeSnapshotField(): any | null {
  const name = elementApi.fieldName.value
  if (!name) return null
  const pageField = (pageFields.value as any)?.[name]
  const winningRows = getTreeTableBackingRows()

  if (!pageField || typeof pageField !== 'object') {
    const regInner = elementApi.value.value as any
    if (!regInner || typeof regInner !== 'object') return null
    try {
      const clonedInner = JSON.parse(JSON.stringify(toRaw({ ...regInner, rows: winningRows })))
      return {type: 'UniDataTable', value: clonedInner}
    } catch {
      return null
    }
  }

  const snapshot = deepCloneTreeTableField(pageField)
  const inner = resolveUniDataTableInner(snapshot)
  if (!inner) return snapshot
  try {
    inner.rows = JSON.parse(JSON.stringify(toRaw(winningRows)))
  } catch {
    inner.rows = winningRows
  }
  return snapshot
}

async function reloadNodeHook(
  rowNum?: unknown,
  needReloadParent?: unknown,
  selectedNodeData?: unknown,
): Promise<any> {
  const rn = Number(rowNum ?? -1)
  const nrp = Boolean(needReloadParent)
  const finiteRn = Number.isFinite(rn) ? rn : -1
  const treeInst = componentRef.value as any
  const flat: FlatVisibleTreeRow[] = treeInst?.getFlattenVisibleRowsForReload?.() ?? []
  const resolved = resolveSwingReloadNodeTarget(flat, finiteRn, nrp)
  const payload = buildReloadNodePayloadFields(finiteRn, nrp)
  const tableName = elementApi.fieldName.value

  const runSelectedData = async (res: any) => {
    if (selectedNodeData !== undefined && selectedNodeData !== null) {
      await applyReloadNodeSelectedNodeData(selectedNodeData)
    }
    return res
  }

  // Полная перезагрузка дерева (__initTreeTable / rowNum -1) — ответ целиком подставляем как раньше.
  if (resolved.kind === 'full' || !tableName) {
    const result = await elementApi.reload?.(payload, {
      applyResult: true,
      source: 'TreeTable.reloadNode',
    })
    return runSelectedData(result)
  }

  const snapshot = resolveTreeTableMergeSnapshotField()

  // Без снимка — старое поведение (целиком ответ в форму).
  if (!snapshot) {
    const result = await elementApi.reload?.(payload, {
      applyResult: true,
      source: 'TreeTable.reloadNode',
    })
    return runSelectedData(result)
  }

  // Частичный reload строки: сервер часто отдаёт только дочерний срез в корне UniDataTable (как при lazy expand).
  // Иначе replace затирает всё дерево одной строкой.
  const result = await elementApi.reload?.(payload, {
    applyResult: false,
    source: 'TreeTable.reloadNode',
  })
  const serverTable = result?.fields?.fields?.[tableName]
  if (!serverTable) {
    return runSelectedData(result)
  }

  const columnsForMerge = getTreeTableContractColumns()
  const rowData = resolved.row
  const idForParent =
    getColumnValue(rowData as Record<string, any>, '_id').value ??
    (rowData as any)?._id ??
    (rowData as any)?.id
  const parentNodeIdCanon = canonicalTreeNodeId(idForParent)
  const parentTreeKey =
    flat[resolved.rowNum]?.key != null ? String(flat[resolved.rowNum].key).trim() : ''
  const expandedSnapshot =
    typeof treeInst?.getExpandedSnapshot === 'function' ? treeInst.getExpandedSnapshot() : undefined

  const tableFieldToApply = mergeTreeTableLazyExpandResponse(
    snapshot,
    serverTable,
    parentNodeIdCanon,
    columnsForMerge,
    parentTreeKey || undefined,
    resolved.rowNum,
    expandedSnapshot,
  )

  const patchedResult = {
    ...result,
    fields: {
      ...result.fields,
      fields: {
        ...(result.fields?.fields || {}),
        [tableName]: tableFieldToApply,
      },
    },
  }

  elementApi.onReloaded?.(patchedResult, {
    fieldNames: [tableName],
    skipClientData: true,
    skipEnvelopeSetValue: true,
    skipOnElement: true,
    source: 'TreeTable.reloadNode',
  })
  elementApi.refill?.({ [tableName]: tableFieldToApply })

  return runSelectedData(patchedResult)
}

async function applyReloadNodeSelectedNodeData(selectedNodeData: unknown): Promise<void> {
  const rawFields = unwrapSelectedNodeDataFields(selectedNodeData)
  if (!rawFields) {
    console.warn('[TreeTable.reloadNode] selectedNodeData is empty')
    return
  }
  await nextTick()
  await nextTick()
  const items = getTreeTableItems()
  const columns = getAllElementColumns()
  const flat = flattenTableContextItems(items)
  let matchKey: string | null = null
  for (const item of flat) {
    const data = getTableContextItemData(item)
    if (!data || typeof data !== 'object') continue
    const flatData = flattenRowData(data, columns)
    if (rowsMatchTreeReloadSelection(rawFields, flatData as Record<string, unknown>, columns)) {
      matchKey = String((flatData as any)[TREE_TABLE_NODE_KEY_FIELD] ?? '').trim() || null
      break
    }
  }
  if (!matchKey) {
    console.warn('[TreeTable.reloadNode] selectedNodeData not found after reload')
    return
  }
  const treeInst = componentRef.value as any
  treeInst?.expandToShowKey?.(matchKey)
  const withCb = isXmlTrue(props?.config?.element?.withCheckBoxes?._text, false)
  if (withCb) {
    treeInst?.ensureCheckboxSelectedForKey?.(matchKey, true)
  }
}

function deepCloneTreeTableField(field: any): any {
  if (field == null) return field
  try {
    return JSON.parse(JSON.stringify(toRaw(field)))
  } catch {
    return toRaw(field)
  }
}

/** Тело UniDataTable: rows могут быть в value.rows или на верхнем уровне поля */
function resolveUniDataTableInner(field: any): {
  rows: any[]
  columns?: any[]
  attributes?: Record<string, unknown>
  empty?: boolean
} | null {
  if (!field || typeof field !== 'object') return null
  const wrapped =
    field.value != null && typeof field.value === 'object' && Array.isArray((field.value as any).rows)
      ? field.value
      : null
  if (wrapped) {
    return {
      rows: wrapped.rows,
      columns: wrapped.columns,
      attributes: wrapped.attributes,
      empty: wrapped.empty,
    }
  }
  if (Array.isArray((field as any).rows)) {
    return {
      rows: (field as any).rows,
      columns: (field as any).columns,
      attributes: (field as any).attributes,
      empty: (field as any).empty,
    }
  }
  return null
}

/** Поиск строки UniDataTable по _id (рекурсивно, включая fields.items). */
function findTreeTableRowByNodeId(rows: any[] | undefined, parentNodeIdCanon: string): any | null {
  if (!Array.isArray(rows) || !parentNodeIdCanon) return null
  for (const row of rows) {
    const rid = getFieldValueFromRow(row, '_id') ?? getFieldValueFromRow(row, 'id')
    if (treeNodeIdsEqual(rid, parentNodeIdCanon)) return row
    const itemsField = row?.fields?.items ?? row?.items
    const nested =
      itemsField?.value?.rows ??
      itemsField?.rows ??
      (Array.isArray(itemsField) ? itemsField : null)
    if (Array.isArray(nested) && nested.length > 0) {
      const found = findTreeTableRowByNodeId(nested, parentNodeIdCanon)
      if (found) return found
    }
  }
  return null
}

function getTreeTableNestedRows(row: any): any[] | null {
  const itemsField = row?.fields?.items ?? row?.items
  const nested =
    itemsField?.value?.rows ??
    itemsField?.rows ??
    (Array.isArray(itemsField) ? itemsField : null)
  return Array.isArray(nested) && nested.length > 0 ? nested : null
}

/**
 * Поиск строки по ключу дерева
 */
function findTreeTableRowByTreeKey(
  rows: any[] | undefined,
  columns: any[],
  targetKey: string,
  parentKey: string = '',
  level: number = 0,
): any | null {
  if (!Array.isArray(rows) || !targetKey) return null
  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    const row = rows[rowIndex]
    const flat = flattenRowData(row, columns)
    const rowKey = buildTreeTableRowKey(row, columns, flat.isAllowChildren, parentKey, level, rowIndex)
    if (rowKey === targetKey) return row

    const nested = getTreeTableNestedRows(row)
    if (nested) {
      const found = findTreeTableRowByTreeKey(nested, columns, targetKey, rowKey, level + 1)
      if (found) return found
    }
  }
  return null
}

/**
 * Порядок строк UniDataTable как у MTreeTable.flattenVisibleNodes: родитель, затем дети, если узел «раскрыт».
 */
function listTreeTableRowsInVisibleOrder(
  rows: any[] | undefined,
  columns: any[],
  expandedSnapshot: Record<string, boolean>,
  parentKey: string = '',
  level: number = 0,
): any[] {
  if (!Array.isArray(rows) || rows.length === 0) return []
  const out: any[] = []
  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    const row = rows[rowIndex]
    const flat = flattenRowData(row, columns)
    const rowKey = buildTreeTableRowKey(row, columns, flat.isAllowChildren, parentKey, level, rowIndex)
    out.push(row)
    const nested = getTreeTableNestedRows(row)
    if (Array.isArray(nested) && nested.length > 0 && expandedSnapshot[rowKey] === true) {
      out.push(...listTreeTableRowsInVisibleOrder(nested, columns, expandedSnapshot, rowKey, level + 1))
    }
  }
  return out
}

/**
 * Сервер при lazy expand отдаёт только строки детей в поле таблицы
 */
function mergeTreeTableLazyExpandResponse(
  snapshotField: any,
  serverChildTableField: any,
  parentNodeIdCanon: string,
  columns: any[],
  parentTreeKey?: string,
  rowNum?: number,
  expandedSnapshot?: Record<string, boolean>,
): any {
  const merged = deepCloneTreeTableField(snapshotField)
  const rootRows = merged?.value?.rows
  const srvInner = resolveUniDataTableInner(serverChildTableField)
  const canFindParent =
    parentTreeKey && String(parentTreeKey).trim() !== ''
      ? true
      : !!parentNodeIdCanon ||
        (rowNum != null &&
          rowNum >= 0 &&
          expandedSnapshot &&
          Object.keys(expandedSnapshot).length > 0)
  if (!Array.isArray(rootRows) || !srvInner?.rows || !canFindParent) {
    return merged ?? serverChildTableField
  }
  let parentRow: any | null = null
  if (parentTreeKey && String(parentTreeKey).trim() !== '') {
    parentRow = findTreeTableRowByTreeKey(rootRows, columns, String(parentTreeKey).trim())
  }
  if (!parentRow && parentNodeIdCanon) {
    parentRow = findTreeTableRowByNodeId(rootRows, parentNodeIdCanon)
  }
  if (
    !parentRow &&
    rowNum != null &&
    rowNum >= 0 &&
    expandedSnapshot &&
    Object.keys(expandedSnapshot).length > 0
  ) {
    const visibleRows = listTreeTableRowsInVisibleOrder(rootRows, columns, expandedSnapshot)
    parentRow = visibleRows[rowNum] ?? null
  }
  if (!parentRow || typeof parentRow !== 'object') {
    return merged
  }
  if (!parentRow.fields) parentRow.fields = {}
  parentRow.fields.items = {
    type: 'UniDataTable',
    value: {
      attributes: srvInner.attributes ?? {},
      columns: Array.isArray(srvInner.columns) ? srvInner.columns : [],
      rows: srvInner.rows,
      empty: srvInner.empty ?? srvInner.rows.length === 0,
    },
  }
  const childLen = Array.isArray(srvInner.rows) ? srvInner.rows.length : 0
  // Только сброс в 0: полная замена _countChildren на len(rows) ломала обновление ветки-источника
  // при несовпадении семантики счётчика с ответом сервера; для «последний ребёнок ушёл» этого достаточно.
  if (childLen === 0) {
    parentRow.fields._countChildren = setType(0)
  }
  return merged
}

/**
 * parentId из payload DnP (cloneRowDataForDrop от node.data).
 */
function readTreeDropSideParentIdCanon(side: Record<string, unknown> | undefined): string {
  if (!side || typeof side !== 'object') return ''
  const asCanon = (v: unknown): string => {
    if (v === undefined || v === null) return ''
    if (typeof v === 'object' && v !== null && 'value' in (v as object)) {
      return canonicalTreeNodeId((v as { value: unknown }).value)
    }
    return canonicalTreeNodeId(v)
  }
  let p = asCanon((side as { parentId?: unknown }).parentId)
  if (p) return p
  const fields = (side as { fields?: Record<string, unknown> }).fields
  if (fields && typeof fields === 'object') {
    p = asCanon(fields.parentId as unknown)
  }
  return p
}

/**
 * TreeTable row DnD (Swing isDraggable + onDrop / metadata action _treeDrop).
 */
const handleTreeTableNodeDrop = async (payload: Record<string, unknown>) => {
  // Swing itemCanImport returns canImport/changeParent from script; ActionService.executeAction
  // does not surface a sync predicate — optional blocking deferred to platform support.
  await executeEventAction('onDrop', {selectedData: payload})

  // Цепочка _treeDrop перезагружает ветку назначения; старый родитель источника остаётся с устаревшими детьми.
  try {
    const source = payload?.source as Record<string, unknown> | undefined
    if (!source) {
      return
    }
    await nextTick()
    await nextTick()
    const treeInst = componentRef.value as any
    const flat: FlatVisibleTreeRow[] = treeInst?.getFlattenVisibleRowsForReload?.() ?? []
    if (!flat.length) {
      return
    }
    const parentCanon = readTreeDropSideParentIdCanon(source)
    if (!parentCanon) {
      await reloadNodeHook(-1, false)
      return
    }
    const parentRowIdx = flat.findIndex((r) => {
      const id =
        canonicalTreeNodeId(
          getColumnValue(r.data as Record<string, any>, '_id').value ??
            (r.data as any)?._id ??
            (r.data as any)?.id,
        )
      return id !== '' && id === parentCanon
    })
    if (parentRowIdx >= 0) {
      await reloadNodeHook(parentRowIdx, false)
    } else {
      await reloadNodeHook(-1, false)
    }
  } catch (e) {
    console.warn('[TreeTable DnD] reload source parent branch failed', e)
  }
}

function parseTreeTableRowChildCount(data: Record<string, unknown> | null | undefined): number | null {
  if (!data || typeof data !== 'object') return null
  const cc = data._countChildren ?? (data as { countChildren?: unknown }).countChildren
  if (cc != null && typeof cc === 'object' && 'value' in (cc as object)) {
    const n = Number((cc as { value: unknown }).value)
    return Number.isFinite(n) ? n : null
  }
  if (cc != null) {
    const n = Number(cc)
    return Number.isFinite(n) ? n : null
  }
  return null
}

function findTreeTableItemByKey(
  items: Record<string, any>[],
  targetKey: string,
): Record<string, any> | null {
  const tk = String(targetKey).trim()
  if (!tk) return null
  for (const item of flattenTableContextItems(items)) {
    const key = String((item as { key?: unknown }).key ?? '').trim()
    if (key === tk) return item
  }
  return null
}

function treeTableItemHasChildWithKey(item: Record<string, any>, childKey: string): boolean {
  const children = (item as { children?: Record<string, any>[] }).children
  if (!Array.isArray(children) || children.length === 0) return false
  const ck = String(childKey).trim()
  return children.some((c) => String(c?.key ?? '').trim() === ck)
}

async function ensureTreeTableNodeChildrenLoadedForSearch(
  parentKey: string,
  childPathRow: unknown,
  columns: ReturnType<typeof getAllElementColumns>,
): Promise<void> {
  const items = getTreeTableItems()
  const parentItem = findTreeTableItemByKey(items, parentKey)
  if (!parentItem) return

  const parentChildren = (parentItem as { children?: unknown[] }).children ?? []
  let childKey = resolvePathKeysFromTreeItems([childPathRow], parentChildren, columns)[0] ?? ''
  if (!childKey) {
    childKey = pathRowsToNodeKeys([childPathRow], columns)[0] ?? ''
  }
  if (!childKey) return
  if (treeTableItemHasChildWithKey(parentItem, childKey)) return

  const parentData = getTableContextItemData(parentItem) as Record<string, unknown> | null
  const childCount = parseTreeTableRowChildCount(parentData)
  if (childCount === null || childCount <= 0) return

  const treeInst = componentRef.value as any
  const flat: FlatVisibleTreeRow[] = treeInst?.getFlattenVisibleRowsForReload?.() ?? []
  const rowNum = flat.findIndex((r) => String(r.key).trim() === String(parentKey).trim())
  if (rowNum < 0) return

  await reloadNodeHook(rowNum, false)
  await nextTick()
  await nextTick()
}

function resolveSearchPathKeys(
  pathRows: unknown[],
  columns: ReturnType<typeof getAllElementColumns>,
): string[] {
  const fromItems = resolvePathKeysFromTreeItems(pathRows, getTreeTableItems(), columns)
  if (fromItems.length === pathRows.length && fromItems.length > 0) {
    return fromItems
  }
  return pathRowsToNodeKeys(pathRows, columns)
}

function getTreeTableSearchLiveCriteriaOptions(): {
  liveOverrides?: Record<string, { type?: string; value?: unknown } | undefined>
} {
  const formId = (elementContext as any)?.formId ?? props.formPath?.formId
  const liveOverrides = formProvider?.collectLiveFieldOverrides?.(formId)
  if (liveOverrides && Object.keys(liveOverrides).length > 0) {
    return { liveOverrides }
  }
  return {}
}

function getTreeTableLiveCriteriaKey(
  pageFieldsMap: Record<string, unknown>,
  tableName: string,
): string {
  return buildTreeTableSearchCriteriaKey(
    pageFieldsMap,
    tableName,
    getTreeTableSearchLiveCriteriaOptions(),
  )
}

function resetLocalTreeTableSearchState(): void {
  resetTreeTableSearchSessionState(treeTableSearchSession)
  pendingTreeTableSearchTargetKey.value = null
  lastTreeTableSearchPathRows.value = []
}

function resolveTreeTableSearchCriteriaKey(
  pageFieldsMap: Record<string, unknown>,
  tableName: string,
): string {
  const liveKey = getTreeTableLiveCriteriaKey(pageFieldsMap, tableName)
  if (!isEmptyTreeTableSearchCriteriaKey(liveKey)) {
    return liveKey
  }
  return buildTreeTableSearchCriteriaKey(pageFieldsMap, tableName)
}

function evaluateTreeTableSearchForApply(
  pageFieldsMap: Record<string, unknown>,
  tableName: string,
  options?: { forceApply?: boolean },
): { result: TreeTableSearchExtracted; signature: string } | null {
  const formId = (elementContext as any)?.formId ?? props.formPath?.formId
  const liveKey = getTreeTableLiveCriteriaKey(pageFieldsMap, tableName)
  const submittedKey = getTreeTableSearchSubmittedCriteria(formId)
  const liveOverrides = getTreeTableSearchLiveCriteriaOptions().liveOverrides

  if (!options?.forceApply && !hasPendingTreeTableSearchApply(formId)) {
    return null
  }

  if (
    !options?.forceApply &&
    shouldIgnoreTreeTableSearchResponse(liveKey, pageFieldsMap, tableName, submittedKey, liveOverrides)
  ) {
    resetLocalTreeTableSearchState()
    if (formId != null) {
      resetTreeTableSearchApplyState(formId)
    }
    return null
  }

  if (isEmptyTreeTableSearchCriteriaKey(liveKey)) {
    resetLocalTreeTableSearchState()
  }

  const criteriaKey = resolveTreeTableSearchCriteriaKey(pageFieldsMap, tableName)
  const raw = extractTreeTableSearchResult(pageFieldsMap, tableName)
  if (raw.kind === 'none') {
    return null
  }

  const { result, session: next } = reconcileTreeTableSearchResult(
    raw,
    treeTableSearchSession,
    criteriaKey,
  )
  commitTreeTableSearchSession(treeTableSearchSession, next)
  const signature = signatureFromTreeTableSearchResult(
    result,
    pageFieldsMap,
    tableName,
    criteriaKey,
  )
  return { result, signature }
}

function treeTableSearchDataFingerprint(): string {
  const tableName = elementApi.fieldName.value
  if (!tableName) return ''
  const rows = getTreeTableBackingRows()
  return `${rows.length}:${countTreeTableNestedBranches(rows)}:${pendingTreeTableSearchTargetKey.value ?? ''}`
}

async function reassertTreeTableSearchPathInData(): Promise<boolean> {
  const tableName = elementApi.fieldName.value
  const pathRows = lastTreeTableSearchPathRows.value
  if (!tableName || pathRows.length === 0) return false

  const columns = getAllElementColumns()
  if (isSearchPathFullyInCurrentTree(pathRows, columns)) {
    return false
  }

  const backing = getTreeTableBackingRows()
  const merged = mergePathRowsIntoBackingTree(backing, pathRows, columns)
  const existingField = (pageFields.value as any)?.[tableName]
  const tableField = applyPathMergeToTableField(existingField, merged)
  elementApi.refill?.({ [tableName]: tableField })
  await nextTick()
  await nextTick()
  return true
}

async function focusTreeTableSearchTarget(
  pathRows: unknown[],
  columns: ReturnType<typeof getAllElementColumns>,
): Promise<void> {
  const treeInst = componentRef.value as {
    expandToShowKey?: (k: string) => boolean
    focusAndSelectKeyWhenReady?: (
      k: string,
      options?: { stealFocus?: boolean },
    ) => Promise<boolean>
    getFlattenVisibleRowsForReload?: () => FlatVisibleTreeRow[]
  } | null
  if (!treeInst) return

  let keys = resolveSearchPathKeys(pathRows, columns)
  if (keys.length === 0) return

  if (keys.length > 1) {
    treeInst.expandToShowKey?.(keys[keys.length - 1])
    await nextTick()
  }

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i]
    if (i > 0) {
      treeInst.expandToShowKey?.(key)
      await nextTick()
    }
    if (i < pathRows.length - 1) {
      await ensureTreeTableNodeChildrenLoadedForSearch(key, pathRows[i + 1], columns)
      await nextTick()
      await nextTick()
      keys = resolveSearchPathKeys(pathRows, columns)
    }
  }

  // Как Swing markFinedNode: выделяем последний узел пути _selected, не __selectedRow
  // (индекс видимой строки на сервере может не совпасть с flatten webui).
  const targetKey = keys.length > 0 ? keys[keys.length - 1] : ''
  if (!targetKey) return

  pendingTreeTableSearchTargetKey.value = targetKey
  pendingTreeTableSearchTargetCriteriaKey.value = getTreeTableLiveCriteriaKey(
    (pageFields.value || {}) as Record<string, unknown>,
    elementApi.fieldName.value ?? '',
  )

  if (typeof treeInst.focusAndSelectKeyWhenReady === 'function') {
    const ok = await treeInst.focusAndSelectKeyWhenReady(targetKey, { stealFocus: false })
    if (ok) {
      schedulePendingTreeTableSearchFocusClear()
    }
  }
}

async function reapplyPendingTreeTableSearchFocus(reason: string): Promise<void> {
  const targetKey = pendingTreeTableSearchTargetKey.value
  if (!targetKey || elementApi.type.value !== 'TREETABLE') return
  const treeInst = componentRef.value as {
    focusAndSelectKeyWhenReady?: (
      k: string,
      options?: { stealFocus?: boolean },
    ) => Promise<boolean>
    getCheckboxSelectionKeysSnapshot?: () => Record<string, unknown>
  } | null
  if (!treeInst?.focusAndSelectKeyWhenReady) return

  const ok = await treeInst.focusAndSelectKeyWhenReady(targetKey, { stealFocus: false })
  if (ok) {
    schedulePendingTreeTableSearchFocusClear()
  }
}

function isSearchPathFullyInCurrentTree(
  pathRows: unknown[],
  columns: ReturnType<typeof getAllElementColumns>,
): boolean {
  if (!pathRows.length) return false
  const keys = resolvePathKeysFromTreeItems(pathRows, getTreeTableItems(), columns)
  return keys.length === pathRows.length
}

async function applyTreeTableSearchFromPageFields(options?: {
  skipRefill?: boolean
  forceApply?: boolean
  pageFieldsOverride?: Record<string, unknown>
  evaluated?: { result: TreeTableSearchExtracted; signature: string }
}): Promise<void> {
  if (elementApi.type.value !== 'TREETABLE') return
  const tableName = elementApi.fieldName.value
  if (!tableName || treeTableSearchApplyInFlight.value) {
    return
  }

  await nextTick()
  const pageFieldsMap = (options?.pageFieldsOverride ?? pageFields.value ?? {}) as Record<string, unknown>
  const evaluation =
    options?.evaluated ??
    evaluateTreeTableSearchForApply(pageFieldsMap, tableName, {
      forceApply: options?.forceApply,
    })
  if (!evaluation) {
    return
  }
  const { result: extracted, signature } = evaluation
  if (extracted.kind === 'none') return

  const formId = (elementContext as any)?.formId ?? props.formPath?.formId
  treeTableSearchApplyInFlight.value = true
  let didApplySearch = false
  try {
    if (extracted.kind === 'notFound') {
      lastTreeTableSearchPathRows.value = []
      // Сброс stale-указателя на последнюю найденную строку: без него
      // боковой watch(tableKey) при следующем поиске пытается зафокусить
      // прошлый ключ и гоняется с основным focus → "поиск не работает".
      pendingTreeTableSearchTargetKey.value = null
      // Визуальный фокус MTreeTable (clickedRowKey/selectedKeys) от прошлого
      // успешного результата иначе остаётся подсвеченным — выглядит как
      // «поиск нашёл прошлую строку», хотя серверный ответ — notFound.
      const treeInstForClear = componentRef.value as { clearSearchFocus?: () => void } | null
      treeInstForClear?.clearSearchFocus?.()
      if (extracted.showMessage) {
        await messagesService.showMessage(
          resolveTreeTableSearchNotFoundMessage(
            extracted.nextSearchPosition,
            pageFieldsMap,
            tableName,
          ),
          '',
        )
      }
      lastTreeTableSearchSignature.value = signature
      didApplySearch = true
      if (formId != null) {
        markTreeTableSearchApplyConsumed(formId)
      }
      return
    }

    const columns = getAllElementColumns()
    lastTreeTableSearchPathRows.value = extracted.pathRows
    const skipRefill =
      options?.skipRefill === true ||
      (!options?.skipRefill && isSearchPathFullyInCurrentTree(extracted.pathRows, columns))

    if (!skipRefill) {
      await reassertTreeTableSearchPathInData()
    }

    await focusTreeTableSearchTarget(extracted.pathRows, columns)

    lastTreeTableSearchSignature.value = signature
    didApplySearch = true
    if (formId != null) {
      markTreeTableSearchApplyConsumed(formId)
    }
  } finally {
    treeTableSearchApplyInFlight.value = false
    if (didApplySearch && tableName) {
      elementApi.refill?.(buildTreeTableSearchStickyClearFields(tableName))
    }
  }
}

function treeTableSearchWatchToken(): string {
  if (elementApi.type.value !== 'TREETABLE') return ''
  const tableName = elementApi.fieldName.value
  if (!tableName) return ''
  const pf = (pageFields.value || {}) as Record<string, unknown>
  return buildTreeTableSearchWatchToken(pf, tableName)
}

watch(
  treeTableSearchWatchToken,
  async (token, prevToken) => {
    if (!token) return
    await nextTick()
    await nextTick()
    const tableName = elementApi.fieldName.value
    if (!tableName) return
    const pageFieldsMap = (pageFields.value || {}) as Record<string, unknown>
    const evaluation = evaluateTreeTableSearchForApply(pageFieldsMap, tableName)
    if (!evaluation) return

    const { result: extracted, signature } = evaluation

    const isNotFound = extracted.kind === 'notFound'
    if (isNotFound) {
      if (signature === lastTreeTableSearchSignature.value) {
        return
      }
      await applyTreeTableSearchFromPageFields({ evaluated: evaluation })
      return
    }
    if (signature === lastTreeTableSearchSignature.value) {
      await applyTreeTableSearchFromPageFields({ skipRefill: true, evaluated: evaluation })
      return
    }
    await applyTreeTableSearchFromPageFields({ evaluated: evaluation })
  },
  { flush: 'post' },
)

const handleTreeTableLoadChildren = async (payload: {
  node: any
  rowNum: number
  expandedSnapshot?: Record<string, boolean>
}) => {
  const node = payload?.node
  const rowNum = payload?.rowNum
  const expandedSnapshot = payload?.expandedSnapshot
  if (!node?.data || rowNum == null || rowNum < 0) return

  const tableName = elementApi.fieldName.value
  if (!tableName) return

  const key = node.key != null ? String(node.key) : ''
  if (!key) return
  if (treeTableExpandInFlightKey.value === key) return

  const contractColumns = getTreeTableContractColumns()
  const paramFields = buildTreeTableExpandParamsFields(node.data, contractColumns)
  if (Object.keys(paramFields).length === 0) return

  const nk = String((node.data as any)?.[TREE_TABLE_NODE_KEY_FIELD] ?? key).trim()
  const treeInst = componentRef.value as any
  const selKeys = (treeInst?.getCheckboxSelectionKeysSnapshot?.() ?? {}) as Record<string, unknown>
  const withCb = isXmlTrue(props?.config?.element?.withCheckBoxes?._text, false)
  const rowChecked = withCb && nk ? selectionKeysIndicatesChecked(selKeys, nk) : false
  const childCount = extractTreeTableServerChildCount(node.data)

  const out = buildTreeTableLazyExpandReloadPayload(tableName, rowNum, paramFields, {
    childCount,
    rowChecked,
  })

  const idForParent =
    getColumnValue(node.data as Record<string, any>, '_id').value ??
    (node.data as any)?._id ??
    (node.data as any)?.id
  const parentNodeIdCanon = canonicalTreeNodeId(idForParent)
  const snapshot = resolveTreeTableMergeSnapshotField()

  treeTableExpandInFlightKey.value = key
  try {
    const result = await elementApi.reload?.(out, {
      applyResult: false,
      source: 'ElementRenderer.treeLazyExpand',
    })
    if (!result) return

    const serverTable = result.fields?.fields?.[tableName]
    let tableFieldToApply = serverTable

    const columnsForMerge = getTreeTableContractColumns()
    if (snapshot && serverTable && key) {
      tableFieldToApply = mergeTreeTableLazyExpandResponse(
        snapshot,
        serverTable,
        parentNodeIdCanon,
        columnsForMerge,
        key,
        rowNum,
        expandedSnapshot,
      )
    }

    const patchedResult = {
      ...result,
      fields: {
        ...result.fields,
        fields: {
          ...(result.fields?.fields || {}),
          [tableName]: tableFieldToApply,
        },
      },
    }
    const didMergeLazyChildren = !!(snapshot && serverTable && key)
    elementApi.onReloaded?.(patchedResult, {
      fieldNames: [tableName],
      skipClientData: true,
      skipEnvelopeSetValue: true,
      skipOnElement: true,
      source: 'ElementRenderer.treeLazyExpand',
    })
    // Реестр forms (источник elementApi.value) иначе не получает вложенные items — getTreeTableItems тогда
    // читает только pageFields или fallback на старый value без детей.
    if (didMergeLazyChildren && tableFieldToApply) {
      elementApi.refill?.({ [tableName]: tableFieldToApply })
      await nextTick()
    }
  } finally {
    if (treeTableExpandInFlightKey.value === key) {
      treeTableExpandInFlightKey.value = null
    }
  }
}

const handleTreeTableFindParent = async (payload: { node: any }) => {
  const node = payload?.node
  if (!isTreeTableGrowUpRootNode(node)) return

  const mesCannot = String(props.config.element?.[IKey.TT_MES_CANNOT_OPEN_PARENT]?._text ?? '').trim()
  const mesNotFound = String(props.config.element?.[IKey.TT_MES_PARENT_NOT_FOUND]?._text ?? '').trim()
  const children = Array.isArray(node.children) ? node.children : []
  if (!children.length) {
    if (mesCannot) await messagesService.showMessage(mesCannot, '')
    return
  }

  const tableName = elementApi.fieldName.value
  if (!tableName) return

  const first = children[0]
  const contractColumns = getTreeTableContractColumns()
  const paramFields = buildTreeTableExpandParamsFields(first?.data, contractColumns)
  const out = buildFindParentRootReloadPayload(tableName, paramFields, 0)

  const result = await elementApi.reload?.(out, {
    applyResult: false,
    source: 'ElementRenderer.treeFindParent',
  })
  if (!result) return

  const serverTable = result.fields?.fields?.[tableName]
  const parentRows = extractTreeTableParentRowsFromReload(serverTable)
  if (!parentRows.length) {
    if (mesNotFound) await messagesService.showMessage(mesNotFound, '')
    return
  }

  const currentRows = getTreeTableBackingRows()
  const wrapped = wrapTreeTableBackingWithParent(parentRows[0], currentRows)
  const snapshot = resolveTreeTableMergeSnapshotField()
  const tableFieldToApply = applyPathMergeToTableField(snapshot, [wrapped])

  const patchedResult = {
    ...result,
    fields: {
      ...result.fields,
      fields: {
        ...(result.fields?.fields || {}),
        [tableName]: tableFieldToApply,
      },
    },
  }
  elementApi.onReloaded?.(patchedResult, {
    fieldNames: [tableName],
    skipClientData: true,
    skipEnvelopeSetValue: true,
    skipOnElement: true,
    source: 'ElementRenderer.treeFindParent',
  })
  elementApi.refill?.({ [tableName]: tableFieldToApply })
  await nextTick()
}

const executeTableInlineAction = async (action: any, event?: any) => {
  const tableName = elementApi.fieldName.value
  const hasTableContextKeys = (obj: any) =>
    obj && typeof obj === 'object' && Object.keys(obj).some((k) => k.endsWith('__rowNum') || k.endsWith('__column'))

  const addTableContextServiceFields = (row: Record<string, any>, ev: any) => {
    if (!tableName || !row || typeof row !== 'object') return
    if (ev?.selectedIdx != null) row[`${tableName}__${IKey.ROW_NUM}`] = ev.selectedIdx
    if (ev?.column != null && ev.column !== '') row[`${tableName}__${IKey.COLUMN}`] = ev.column
  }

  const resolveInlineActionSelectedIdx = (ev: any, row: Record<string, any>) => {
    const selectedIdx =
      typeof ev?.selectedIdx === 'number' && ev.selectedIdx >= 0 ? ev.selectedIdx : -1
    if (selectedIdx >= 0) return selectedIdx

    const items =
      elementApi.type.value === 'TREETABLE' ? getTreeTableItems() : getTableItems()
    if (elementApi.type.value === 'TREETABLE') {
      return resolveTreeTableContextSourceRowByIdentity(items, row, -1).selectedIdx
    }
    return findTableContextRowIndex(items, row)
  }

  try {
    let rowDataForAction = selectedRowData.value
    const eventSelected = event ? extractRowDataFromEvent(event) : { normalized: null as Record<string, any> | null }
    if (eventSelected.normalized && (eventSelected.normalized._id || eventSelected.normalized._typeId)) {
      // Inline action must prefer row from current event to avoid stale selectedRowData/idx.
      rowDataForAction = { ...eventSelected.normalized }
      selectedRowData.value = rowDataForAction
    }

    // Данные контекстного меню таблицы (уже с __rowNum, __column) не перезаписывать — иначе теряются служебные поля и в clientData уходит вся форма
    if (hasTableContextKeys(rowDataForAction)) {
      rowDataForAction = {...rowDataForAction}
    } else if (!rowDataForAction || (!rowDataForAction._id && !rowDataForAction._typeId)) {
      if (event?.data) {
        const rowData = event.data._originalRow || event.data._rowData || event.data
        rowDataForAction =
          typeof rowData === 'object' && !Array.isArray(rowData) ? {...rowData} : {}

        if (rowData.fields && typeof rowData.fields === 'object') {
          Object.keys(rowData.fields).forEach((fieldName: string) => {
            const fieldData = rowData.fields[fieldName]
            if (!rowDataForAction.hasOwnProperty(fieldName)) {
              const value = fieldData?.value !== undefined ? fieldData.value : fieldData
              rowDataForAction[fieldName] = value
            }
          })
        }

        if (!rowDataForAction._id) {
          rowDataForAction._id =
            rowData.fields?._id?.value || rowData._id || rowData.requestId || rowData.id
        }
        if (!rowDataForAction._typeId) {
          rowDataForAction._typeId = rowData.fields?._typeId?.value || rowData._typeId
        }
        if (!rowDataForAction.hasOwnProperty('_versionId')) {
          rowDataForAction._versionId =
            rowData.fields?._versionId?.value || rowData._versionId || null
        }

        if (!rowDataForAction._id && !rowDataForAction._typeId) {
          // Try common ID fields
          rowDataForAction._id =
            rowDataForAction.requestId || rowDataForAction.id || rowDataForAction.code
        }

        selectedRowData.value = rowDataForAction
      } else if (event && typeof event === 'object') {
        const extracted: any = {}
        if (event.rowData) {
          Object.assign(extracted, event.rowData)
        }
        if (event.data && typeof event.data === 'object') {
          Object.assign(extracted, event.data)
        }
        // Check nested structures
        if (extracted.fields) {
          Object.keys(extracted.fields).forEach((key: string) => {
            const fieldData = extracted.fields[key]
            extracted[key] = fieldData?.value !== undefined ? fieldData.value : fieldData
          })
        }
        // Try to extract _id from various possible fields
        if (!extracted._id) {
          extracted._id = extracted.requestId || extracted.id || extracted._id
        }
        if (!extracted._typeId) {
          extracted._typeId = extracted._typeId
        }
        if (extracted._id || extracted._typeId || Object.keys(extracted).length > 0) {
          rowDataForAction = extracted
          selectedRowData.value = rowDataForAction
        }
      }

      if (
        !rowDataForAction &&
        event &&
        typeof event === 'object' &&
        !event.data &&
        !event.rowData
      ) {
        if (
          event.requestId ||
          event.id ||
          event._id ||
          Object.keys(event).some((k) => k.startsWith('_'))
        ) {
          rowDataForAction = {...event}
          selectedRowData.value = rowDataForAction
        }
      }
    }
    // Для таблицы: собрать плоскую строку по образцу Swing (только имена столбцов → примитивы + __rowNum, __column)
    const isTableEl = elementApi.type.value === 'TABLE' || elementApi.type.value === 'EDITABLE' || elementApi.type.value === 'TREETABLE'
    if (isTableEl && rowDataForAction && tableName) {
      const selectedIdx = resolveInlineActionSelectedIdx(event, rowDataForAction)
      const column = resolveTableContextColumn(event)
      const columns = (
        elementApi.type.value === 'TREETABLE'
          ? getTreeTableExportColumns()
          : getAllElementColumns()
      )
        .filter((col: any) => !!col?.name)
        .map((col: any) => ({ name: col.name, type: col.type }))
      const items =
        elementApi.type.value === 'TREETABLE' ? getTreeTableItems() : getTableItems()
      const resolvedSource = resolveTableContextSourceRow(items, rowDataForAction, selectedIdx)
      lastTableContextSelectedIdx.value = resolvedSource.selectedIdx
      rowDataForAction = (
        elementApi.type.value === 'TREETABLE'
          ? buildTreeTableActionContextRow(
              resolvedSource.row ?? null,
              items,
              columns,
              resolvedSource.selectedIdx,
              column,
              tableName,
            )
          : buildPlainRowForTableContext(
              resolvedSource.row ?? null,
              columns,
              resolvedSource.selectedIdx,
              column,
              tableName,
            )
      ) as Record<string, any>
    } else if (rowDataForAction && isTableEl) {
      const selectedIdx = resolveInlineActionSelectedIdx(event, rowDataForAction)
      addTableContextServiceFields(rowDataForAction, {
        selectedIdx: selectedIdx >= 0 ? selectedIdx : undefined,
        column: resolveTableContextColumn(event),
      })
    } else if (elementApi.type.value === 'TILES' && rowDataForAction && tableName) {
      const selectedIdx =
        typeof event?.selectedIdx === 'number' && event.selectedIdx >= 0
          ? event.selectedIdx
          : lastTableContextSelectedIdx.value
      rowDataForAction = mergeTilesInlineSelectedData(
        rowDataForAction,
        tableName,
        selectedIdx >= 0 ? selectedIdx : 0,
      ) as Record<string, any>
    }

    if (!rowDataForAction) {
      console.error(
        'Missing row data for action. Event:',
        event,
        'SelectedRowData:',
        selectedRowData.value,
      )
      return
    }

    const platform = action.platform?._text || currentPlatform.value || undefined

    if (platform) {
      appStore.updateCurrentPlatform(platform)
    }

    const transfer =
      pickXmlActionScalar(action.transfer) ||
      pickXmlActionScalar(action.address) ||
      pickXmlActionScalar((action as any)._attributes?.address)

    const oe = event?.originalEvent ?? event
    const actionContext: ActionContext = {
      id: action._attributes?.id || '0',
      name: action.name?._text || 'Unknown Action',
      formId: props.formPath.formId || elementContext?.formId,
      elementId: elementId.value != null ? String(elementId.value) : undefined,
      panelId: props.formPath.panelId,
      level: 'element',
      data: action,
      platform,
      selectedData: rowDataForAction,
      limitToSelectedData: true,
      doActionForAdjacentWindow: isTruthyFlag(action.doActionForAdjacentWindow?._text),
      windowType: pickXmlActionScalar(action.window__type),
      openInNewWindowFromModifier: Boolean(
        oe && typeof oe === 'object' && 'altKey' in oe && (oe as MouseEvent).altKey,
      ),
      onClick: action.onClick?._text,
      confirm: action.confirm?._text,
      transfer,
      transferType: pickXmlActionScalar(action.transferType),
      event: {
        dialogClosed: getActionDialogClosedScript(action as Record<string, any>),
        chainCalled: action.event?.chainCalled?._text,
      },
      flow: action.flow?._text,
    }

    await actionService.executeAction(actionContext, {
      appStore,
      router,
      actions,
      dialog,
      reloadElement: reloadElement ?? formProvider?.reloadElement,
      refillElement: formProvider?.refillElement,
      openUrls,
      pageErrors,
      formStates,
      alerts,
      getForm,
      getPageData,
      runCode,
      formProvider,
      pageData: pageData.value,
      pageDataSlot: pageDataSlot.value,
      pageRegistryFormId: pageRegistryFormId.value,
      pageWindowInstanceId: pageWindowInstanceId.value,
    })
  } catch (error) {
    console.error('Error executing table context action:', error)
  }
}

const isTableInlineActionVisible = (
  action: any,
  visibilityOverride?: Record<string, boolean>,
): boolean => {
  const actionId = String(action?._attributes?.id ?? action?.id ?? '').trim()
  if (!actionId) return true

  const fromMenu =
    visibilityOverride != null
      ? visibilityOverride[actionId]
      : tableInlineActionVisibility.value[actionId]
  const deferred = peekDeferredActionVisibility(actionId)

  const actionsRegistry = (formProvider as any)?.getActions?.() as
    | Record<string, { isVisible?: () => boolean; visible?: boolean }>
    | undefined
  const registered = actionsRegistry?.[actionId]
  let registryVisible: boolean | undefined
  if (registered) {
    if (typeof registered.isVisible === 'function') {
      registryVisible = Boolean(registered.isVisible())
    } else if (typeof registered.visible === 'boolean') {
      registryVisible = registered.visible
    }
  }

  return resolveTableInlineActionVisible({
    fromMenu,
    deferred,
    registryVisible,
  })
}

const syncTableInlineActionVisibilityFromDeferred = () => {
  // Каждый show меню — с нуля: иначе false после group-row залипает, если setVisible(true)
  // не попал в deferred (другой target / сбой onInlineMenuShow).
  tableInlineActionVisibility.value = buildTableInlineActionVisibilityMap(
    collectInlineActionIds(getTableInlineActions()),
    peekDeferredActionVisibility,
  )
}

/** Сброс stale deferred для inline ids текущего элемента перед onInlineMenuShow. */
const resetTableInlineActionDeferredVisibility = () => {
  const ids = collectInlineActionIds(getTableInlineActions())
  clearDeferredFormVisibilityForActionIds(ids)
  tableInlineActionVisibility.value = {}
}

const getTableContextMenuItems = (visibilityOverride?: Record<string, boolean>) => {
  return getTableInlineActions()
    .filter((action) => isTableInlineActionVisible(action, visibilityOverride))
    .map((action) => ({
    label: action.title?._text || action.description?._text || action.name?._text || 'Action',
    icon: action.image?._text,
    command: async (event?: any) => {
      const snap = tableContextMenuSnapshot.value
      const item = event?.item
      const hasRowLikeItem =
        item &&
        typeof item === 'object' &&
        (item._id != null ||
          item._typeId != null ||
          item.fields ||
          item._rowData ||
          item._originalRow)
      const hasRowEvent =
        event &&
        typeof event === 'object' &&
        (event.data || event.rowData || event.node?.data || event.row || hasRowLikeItem)
      await executeTableInlineAction(
        action,
        hasRowEvent
          ? event
          : snap
          ? {
              data: snap.rowData,
              selectedIdx: snap.selectedIdx >= 0 ? snap.selectedIdx : undefined,
              column: snap.column,
            }
          : undefined,
      )
    },
  }))
}

/** Живой список пунктов ПКМ — обновляется синхронно после onInlineMenuShow. */
const tableContextMenuItemsLive = ref<Array<{ label: string; icon?: string; command: (event?: any) => Promise<void> }>>(
  [],
)

const refreshTableContextMenuItemsLive = () => {
  tableContextMenuItemsLive.value = getTableContextMenuItems()
}

// Держим live-меню актуальным для dblclick / первого рендера (без side-effect в componentProps).
// Preview onInlineMenuShow не должен refresh — иначе @shown remount → цикл.
let suppressTableContextMenuLiveRefresh = false
watch(
  [
    pageActions,
    () => elementApi.id.value,
    () => elementApi.type.value,
    tableInlineActionVisibility,
  ],
  () => {
    if (suppressTableContextMenuLiveRefresh) return
    const elType = elementApi.type.value
    if (
      elType !== 'TABLE' &&
      elType !== 'EDITABLE' &&
      elType !== 'TREETABLE' &&
      elType !== 'TILES'
    ) {
      return
    }
    refreshTableContextMenuItemsLive()
  },
  {immediate: true},
)

function addTableContextMenuServiceFields(merged: Record<string, any>, event: any): Record<string, any> {
  const elType = elementApi.type.value
  if (
    elType !== 'TABLE' &&
    elType !== 'EDITABLE' &&
    elType !== 'TREETABLE' &&
    elType !== 'TILES'
  ) {
    return merged
  }
  const tableName = elementApi.fieldName.value
  if (!tableName) return merged
  const result = {...merged}
  if (event?.column != null) {
    result[`${tableName}__${IKey.COLUMN}`] = event.column
  }
  if (event?.selectedIdx != null) {
    result[`${tableName}__${IKey.ROW_NUM}`] = event.selectedIdx
  }
  return result
}

/** Jackson NPE */
const defaultScriptTypedValueForType = (t: string): any => {
  const u = (t || '').toLowerCase()
  if (u === 'unidatatable' || u === 'unidatatablefields') {
    return { attributes: {}, columns: [], rows: [], empty: true }
  }
  if (
    u.includes('long') ||
    u.includes('int') ||
    u.includes('double') ||
    u.includes('float') ||
    u === 'number'
  ) {
    return 0
  }
  if (u.includes('bool')) {
    return false
  }
  return ''
}

const wrapFieldForExecuteScript = (v: any) => {
  if (v && typeof v === 'object' && 'type' in v) {
    const promoted = tryPromoteStringWrappedUniDataTable(v)
    if (promoted) {
      v = promoted
    }
    const t = String((v as any).type ?? 'String')
    if ('value' in v) {
      let val = (v as any).value
      if (val === null || val === undefined) {
        val = defaultScriptTypedValueForType(t)
      }
      return { type: t, value: val }
    }
    return { type: t, value: defaultScriptTypedValueForType(t) }
  }
  if (v === null || v === undefined) {
    return {type: 'String', value: ''}
  }
  const typ =
    typeof v === 'number'
      ? 'Long'
      : typeof v === 'boolean'
        ? 'Boolean'
        : 'String'
  return {type: typ, value: v}
}

const sanitizeFieldsMapForExecuteScript = (fields: Record<string, any>): Record<string, any> => {
  const out: Record<string, any> = {}
  for (const [k, v] of Object.entries(fields)) {
    out[k] = wrapFieldForExecuteScript(v)
  }
  return out
}

const sanitizeParamsForExecuteScript = (params: Record<string, any>): Record<string, any> => {
  const out: Record<string, any> = {}
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined) {
      continue
    }
    out[k] = v
  }
  return out
}

function isUniDataTableInnerShape(v: unknown): boolean {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false
  return Array.isArray((v as Record<string, unknown>).rows)
}

function clonePlainForExecuteScript(v: unknown): unknown {
  try {
    return JSON.parse(JSON.stringify(v))
  } catch {
    return v
  }
}

function mergeTableContextRowIntoFormFields(
  base: Record<string, any>,
  row: Record<string, any> | null | undefined,
): Record<string, any> {
  if (!row || typeof row !== 'object') {
    return base
  }
  const merged: Record<string, any> = {...base}
  const fromFields = new Set<string>()
  if (row.fields && typeof row.fields === 'object') {
    for (const [k, v] of Object.entries(row.fields)) {
      merged[k] = wrapFieldForExecuteScript(v)
      fromFields.add(k)
    }
  }
  const skip = new Set(['fields', '_rowData', '_originalRow'])
  for (const [k, v] of Object.entries(row)) {
    if (skip.has(k)) continue
    if (fromFields.has(k)) continue

    if (
      v !== null &&
      typeof v === 'object' &&
      !Array.isArray(v) &&
      typeof (v as any).type === 'string'
    ) {
      merged[k] = wrapFieldForExecuteScript(v)
      continue
    }

    if (v !== null && typeof v === 'object' && !Array.isArray(v) && isUniDataTableInnerShape(v)) {
      merged[k] = wrapFieldForExecuteScript({
        type: 'UniDataTable',
        value: clonePlainForExecuteScript(v),
      })
      continue
    }

    const nestedPlain =
      v !== null &&
      typeof v === 'object' &&
      !Array.isArray(v) &&
      !('value' in v && 'type' in v)
    if (nestedPlain) {
      for (const [sk, sv] of Object.entries(v as Record<string, unknown>)) {
        const fk = `${k}.${sk}`
        if (!fromFields.has(fk) && merged[fk] === undefined) {
          merged[fk] = wrapFieldForExecuteScript(sv)
        }
      }
      continue
    }

    merged[k] = wrapFieldForExecuteScript(v)
  }
  return merged
}

const runInlineMenuShowOnServer = async (
  inlineMenuScript: string,
  el: any,
  contextRow: Record<string, any> | null | undefined,
) => {
  try {
    const formId = (elementContext as any)?.formId ?? props.formPath?.formId
    const panelId = (elementContext as any)?.panelId ?? props.formPath?.panelId

    const pageDataForForm =
      findStorePageDataByLogicalFormId(formId) ||
      findPageDataByFormId() ||
      (appStore.currentPageId
        ? appStore.pagesData[String(appStore.currentPageId)]
        : null)

    const injectedPd = pageData.value as any
    const dataForParams = pageDataForForm ?? injectedPd ?? {}

    const sessionIdFromPageDataForForm =
      normalizeSessionId(unwrapRef((pageDataForForm as any)?.sessionId)) ||
      normalizeSessionId(unwrapRef((pageDataForForm as any)?.cloneOf))
    const sessionId = sessionIdFromPageDataForForm || getSessionId()
    if (!sessionId) {
      console.warn(
        'No sessionId available for element onInlineMenuShow script execution',
        {formId, panelId},
      )
      return
    }

    const {scriptService} = await import('@/services/script/script.service')
    const {ScriptLevel, ScriptName} = await import('@/services/websocket/types')

    const panelData = formProvider?.getForm({formId, panelId, flatten: false}) || {}
    const fields: Record<string, any> = {}

    Object.keys(panelData).forEach((key) => {
      const field = panelData[key]
      if (field && typeof field === 'object' && 'value' in field && 'type' in field) {
        fields[key] = field
      } else {
        fields[key] = {
          value: field,
          type:
            typeof field === 'string'
              ? 'String'
              : typeof field === 'number'
                ? 'Long'
                : typeof field === 'boolean'
                  ? 'Boolean'
                  : 'String',
        }
      }
    })

    const mergedFields = sanitizeFieldsMapForExecuteScript(
      mergeTableContextRowIntoFormFields(fields, contextRow ?? undefined),
    )
    const serverParams = sanitizeParamsForExecuteScript(
      dataForParams.uniData?.clientData?.params &&
        typeof dataForParams.uniData.clientData.params === 'object'
        ? {...dataForParams.uniData.clientData.params}
        : {},
    )

    const clientData = {
      fields: {
        fields: mergedFields,
        fieldAttributes: {},
        errors: {},
        attributes: {},
      },
      clientData: {
        elementConditions: null,
        reloadActions: null,
        params: serverParams,
        doActionId: 0,
        openPage: 0,
      },
      formError: null,
    }

    const elementIdNum = elementId.value
      ? parseInt(elementId.value.toString())
      : parseInt(el?._attributes?.id)

    if (!Number.isNaN(elementIdNum)) {
      const scriptResponse = await scriptService.executeScript({
        sessionId,
        currentData: clientData,
        elementId: elementIdNum,
        level: ScriptLevel.ELEMENT,
        scriptName: ScriptName.ON_INLINE_MENU_SHOW,
        scriptBody: inlineMenuScript,
      })
      // Middle en.setVisible → dispatched_visibilities; без apply deferred пуст → inline default visible.
      applyInlineMenuShowScriptVisibilities(scriptResponse, formProvider ?? undefined)
    }
  } catch (error) {
    console.error('Error executing onInlineMenuShow script on server:', error)
  }
}

/**
 * Context menu handler — от MTable / MTreeTable / MMultiSelect / MTiles (строчный эмит с data и selectedIdx).
 */
const handleContextMenu = async (event: any) => {
  let rowData: any = null

  if (elementApi.type.value === 'MULTISELECT') {
    rowData = event?.data || null
    const items = getMultiselectItems()
    const foundItem = items.find((item: { value: any; _rowData?: any }) => {
      const itemValue = item.value
      // Compare by value or by object equality
      if (itemValue === rowData) return true
      if (typeof itemValue === 'object' && typeof rowData === 'object') {
        // Try JSON comparison
        try {
          if (JSON.stringify(itemValue) === JSON.stringify(rowData)) return true
        } catch {
        }
        // Try comparing by _id if available
        if (itemValue && rowData) {
          const itemId = itemValue.fields?._id?.value || itemValue._id
          const rowId = rowData.fields?._id?.value || rowData._id
          if (itemId && rowId && itemId === rowId) return true
        }
      }
      return false
    })
    if (foundItem && foundItem._rowData) {
      rowData = foundItem._rowData
    } else if (
      foundItem &&
      foundItem.value &&
      typeof foundItem.value === 'object' &&
      foundItem.value.fields
    ) {
      // If the item value itself has fields structure, use it
      rowData = foundItem.value
    }
  }

  if (elementApi.type.value === 'TILES' && event?.data != null) {
    rowData = event.data
  }

  const elType = elementApi.type.value
  const isTable = elType === 'TABLE' || elType === 'EDITABLE' || elType === 'TREETABLE'
  const contextEvent =
    elType === 'TILES' &&
    swingTilesRowScope &&
    (typeof event?.selectedIdx !== 'number' || event.selectedIdx < 0)
      ? { ...(event || {}), selectedIdx: swingTilesRowScope.rowIndex }
      : event

  const resolveTableSelectedIdx = (ev: any, dataSource: any) => {
    if (elType === 'TREETABLE') {
      const fromEvent = ev?.selectedIdx
      const preferredIdx = typeof fromEvent === 'number' && fromEvent >= 0 ? fromEvent : -1
      const items = getTreeTableItems()
      const resolved = resolveTreeTableContextSourceRowByIdentity(items, dataSource ?? null, preferredIdx)
      return resolved.selectedIdx
    }
    const fromEvent = ev?.selectedIdx
    if (typeof fromEvent === 'number' && fromEvent >= 0) return fromEvent
    const items = getTableItems()
    return findTableContextRowIndex(items, dataSource)
  }

  let mergedSelected: any | null = null

  if (!rowData) {
    if (elType === 'TILES' && swingTilesRowScope?.getRowData) {
      const scopedRow = swingTilesRowScope.getRowData()
      if (scopedRow != null) {
        rowData = scopedRow
      }
    }
    const {raw, normalized, selectedData} = extractRowDataFromEvent(rowData != null ? { data: rowData } : contextEvent)
    if (raw || normalized || selectedData) {
      mergedSelected = {
        ...(selectedData || {}),
        ...(normalized || {}),
      }
      mergedSelected = addTableContextMenuServiceFields(mergedSelected, contextEvent)
      selectedRowData.value = mergedSelected
      if (isTable) {
        lastTableContextSelectedIdx.value = resolveTableSelectedIdx(contextEvent, contextEvent?.data)
        const resolvedColumn = resolveTableContextColumn(contextEvent)
        if (resolvedColumn !== '') {
          lastTableContextColumn.value = resolvedColumn
        }
      } else if (
        elType === 'TILES' &&
        typeof contextEvent?.selectedIdx === 'number' &&
        contextEvent.selectedIdx >= 0
      ) {
        lastTableContextSelectedIdx.value = contextEvent.selectedIdx
      }
    } else {
      selectedRowData.value = null
      console.warn('Context menu opened but no row data in event:', event)
    }
  } else {
    const {raw, normalized, selectedData} = extractRowDataFromEvent({data: rowData})
    if (raw || normalized || selectedData) {
      mergedSelected = {
        ...(selectedData || {}),
        ...(normalized || {}),
      }
      mergedSelected = addTableContextMenuServiceFields(mergedSelected, contextEvent)
      selectedRowData.value = mergedSelected
      if (isTable) {
        lastTableContextSelectedIdx.value = resolveTableSelectedIdx(contextEvent, rowData)
        const resolvedColumn = resolveTableContextColumn(contextEvent)
        if (resolvedColumn !== '') {
          lastTableContextColumn.value = resolvedColumn
        }
      } else if (
        elType === 'TILES' &&
        typeof contextEvent?.selectedIdx === 'number' &&
        contextEvent.selectedIdx >= 0
      ) {
        lastTableContextSelectedIdx.value = contextEvent.selectedIdx
      }
    } else {
      selectedRowData.value = null
      console.warn('Context menu opened but no row data in event:', event)
    }
  }

  // Execute onInlineMenuShow script before the context menu is shown (if defined)
  const el = props.config?.element || props.config
  const inlineMenuScript = el?.onInlineMenuShow?._text

  if (isTable && mergedSelected) {
    const selectedIdx = resolveTableSelectedIdx(contextEvent, mergedSelected)
    const column = resolveTableContextColumn(contextEvent)
    let rowDataForSnapshot: Record<string, any> = mergedSelected

    if (elType === 'TREETABLE') {
      const tableName = elementApi.fieldName.value || ''
      const items = getTreeTableItems()
      const columns = getTreeTableExportColumns()
        .filter((col: any) => !!col?.name)
        .map((col: any) => ({ name: col.name, type: col.type }))
      const resolvedSource = resolveTableContextSourceRow(items, mergedSelected, selectedIdx)
      const fromNode = contextEvent?.node?.isAllowChildren
      const treeItem = findTreeTableContextItem(
        items,
        resolvedSource.row ?? mergedSelected,
        resolvedSource.selectedIdx,
      )
      const allowsChildren =
        fromNode === true || fromNode === 'true' || fromNode === 1 || fromNode === '1'
          ? true
          : fromNode === false || fromNode === 'false' || fromNode === 0 || fromNode === '0'
            ? false
            : resolveTreeTableInlineIsAllowChildren(
                resolvedSource.row ?? mergedSelected,
                treeItem,
                items,
              )
      rowDataForSnapshot = buildTreeTableActionContextRow(
        resolvedSource.row ?? null,
        items,
        columns,
        resolvedSource.selectedIdx,
        column,
        tableName,
      ) as Record<string, any>
      // Гарантируем isAllowChildren для onInlineMenuShow (как Swing convertNode).
      rowDataForSnapshot.isAllowChildren = allowsChildren
      if (rowDataForSnapshot.fields && typeof rowDataForSnapshot.fields === 'object') {
        rowDataForSnapshot.fields.isAllowChildren = {
          value: allowsChildren,
          type: 'Boolean',
        }
      }
      selectedRowData.value = rowDataForSnapshot
      lastTableContextSelectedIdx.value = resolvedSource.selectedIdx
    }

    tableContextMenuSnapshot.value = {
      selectedIdx: elType === 'TREETABLE' ? lastTableContextSelectedIdx.value : selectedIdx,
      column,
      rowData: rowDataForSnapshot,
    }
  } else if (elType === 'TILES' && mergedSelected) {
    tableContextMenuSnapshot.value = {
      selectedIdx:
        typeof contextEvent?.selectedIdx === 'number' && contextEvent.selectedIdx >= 0
          ? contextEvent.selectedIdx
          : -1,
      column: '',
      rowData: mergedSelected,
    }
  } else {
    tableContextMenuSnapshot.value = null
  }

  // Stale deferred false (group-row / другой target) не должен переживать текущий show.
  const isPreview = event?.preview === true
  const inlineActionIds = collectInlineActionIds(getTableInlineActions())
  const deferredSnap = snapshotDeferredActionVisibility()
  if (isPreview) {
    suppressTableContextMenuLiveRefresh = true
    clearDeferredFormVisibilityForActionIds(inlineActionIds)
  } else {
    resetTableInlineActionDeferredVisibility()
  }

  if (inlineMenuScript && typeof inlineMenuScript === 'string' && inlineMenuScript.trim()) {
    const attrs = el.onInlineMenuShow?._attributes || {}
    const webClientExec = getWebClientExecFromScriptNode({ _attributes: attrs })

    const selectedDataForScript =
      tableContextMenuSnapshot.value?.rowData ?? mergedSelected ?? selectedRowData.value ?? null

    try {
      if (typeof elementApi.invokeEvent === 'function' && (webClientExec || isPreview)) {
        await elementApi.invokeEvent('inlineMenuShow', {selectedData: selectedDataForScript})
      } else if (!webClientExec && formProvider && !isPreview) {
        await runInlineMenuShowOnServer(inlineMenuScript, el, selectedDataForScript)
      }
    } catch (error) {
      console.error('Error executing onInlineMenuShow:', error)
    }
  }

  if (isPreview) {
    try {
      const previewVis = buildTableInlineActionVisibilityMap(
        inlineActionIds,
        peekDeferredActionVisibility,
      )
      return getTableContextMenuItems(previewVis)
    } finally {
      restoreDeferredActionVisibility(deferredSnap, inlineActionIds)
      suppressTableContextMenuLiveRefresh = false
    }
  }

  // Visibility + live menu после onInlineMenuShow (и без скрипта — актуальный список).
  syncTableInlineActionVisibilityFromDeferred()
  refreshTableContextMenuItemsLive()
  await nextTick()

  const liveItems = tableContextMenuItemsLive.value
  restoreDeferredActionVisibility(deferredSnap, inlineActionIds)
  syncTableInlineActionVisibilityFromDeferred()
  refreshTableContextMenuItemsLive()

  // Актуальных items для useTableContextMenu (не ждать props flush).
  return liveItems
}

// MediaWall widget event handlers
const handleWidgetClick = (widget: any) => {
  if (widget && widget.payload) {
    selectedRowData.value = widget.payload
  }
  executeEventAction('widget-click')
  executeEventAction('widgetClick')
  executeEventAction('onClick')
}

const handleWidgetDoubleClick = (widget: any) => {
  if (widget && widget.payload) {
    selectedRowData.value = widget.payload
  }
  executeEventAction('widget-dblclick')
  executeEventAction('widgetDblclick')
  executeEventAction('inlineDoFirstAction')
}

const handleWidgetContextMenu = (payload: { widget: any; event: MouseEvent }) => {
  const {widget} = payload
  if (widget && widget.payload) {
    selectedRowData.value = widget.payload
  }
  executeEventAction('context-menu')
  executeEventAction('contextMenu')
  executeEventAction('inlineMenuShow')
}

// MultiSelect chip action handler
const handleChipAction = async (payload: { action: any; chipValue: any; chipIndex: number }) => {
  const {action, chipValue} = payload

  try {
    // Extract row data from chip value (similar to table row handling)
    let rowData: any = chipValue

    // If chipValue is a value reference, try to find the full item data
    if (chipValue && typeof chipValue === 'object') {
      // Check if it has fields structure (server row data)
      if (chipValue.fields) {
        rowData = chipValue
      } else {
        // Try to find the full item in the items list
        const items = getListboxItems()
        const foundItem = items.find((item: { value: any; _rowData?: any }) => {
          const itemValue = item.value
          return (
            itemValue === chipValue ||
            (typeof itemValue === 'object' &&
              typeof chipValue === 'object' &&
              JSON.stringify(itemValue) === JSON.stringify(chipValue))
          )
        })
        if (foundItem && foundItem._rowData) {
          rowData = foundItem._rowData
        } else {
          rowData = chipValue
        }
      }
    }

    const rowDataForAction = rowData ? {...rowData} : null

    // Extract data similar to table row handling
    if (rowDataForAction && rowData.fields && typeof rowData.fields === 'object') {
      Object.keys(rowData.fields).forEach((fieldName: string) => {
        const fieldData = rowData.fields[fieldName]
        if (!rowDataForAction.hasOwnProperty(fieldName)) {
          const value = fieldData?.value !== undefined ? fieldData.value : fieldData
          rowDataForAction[fieldName] = value
        }
      })
    }

    if (rowDataForAction) {
      if (!rowDataForAction._id) {
        rowDataForAction._id =
          rowData.fields?._id?.value || rowData._id || rowData.requestId || rowData.id
      }
      if (!rowDataForAction._typeId) {
        rowDataForAction._typeId = rowData.fields?._typeId?.value || rowData._typeId
      }
      if (!rowDataForAction.hasOwnProperty('_versionId')) {
        rowDataForAction._versionId =
          rowData.fields?._versionId?.value || rowData._versionId || null
      }

      if (!rowDataForAction._id && !rowDataForAction._typeId) {
        rowDataForAction._id =
          rowDataForAction.requestId || rowDataForAction.id || rowDataForAction.code
      }

      selectedRowData.value = rowDataForAction
    }

    if (!rowDataForAction) {
      console.error('Missing row data for chip action. ChipValue:', chipValue)
      return
    }

    // Create action context and execute (same pattern as table context menu)
    const platform = action.platform?._text || currentPlatform.value || undefined

    if (platform) {
      appStore.updateCurrentPlatform(platform)
    }

    const transfer = action.transfer?._text || action.address?._text || action._attributes?.address

    const actionContext: ActionContext = {
      id: action._attributes?.id || '0',
      name: action.name?._text || 'Unknown Action',
      formId: props.formPath.formId || elementContext?.formId,
      elementId: elementId.value?.toString() ?? '',
      panelId: props.formPath.panelId,
      level: 'element',
      data: action,
      platform,
      selectedData: rowDataForAction,
      windowType: action.window__type?._text,
      onClick: action.onClick?._text,
      confirm: action.confirm?._text,
      transfer: transfer,
      transferType: action.transferType?._text,
      event: {
        dialogClosed: getActionDialogClosedScript(action as Record<string, any>),
        chainCalled: action.event?.chainCalled?._text,
      },
      flow: action.flow?._text,
    }

    await actionService.executeAction(actionContext, {
      appStore,
      router,
      actions,
      dialog,
      reloadElement: reloadElement || undefined,
      refillElement: formProvider?.refillElement,
      openUrls,
      pageErrors,
      formStates,
      alerts,
      getForm,
      getPageData,
      runCode,
      formProvider,
      pageData: pageData.value,
      pageDataSlot: pageDataSlot.value,
      pageRegistryFormId: pageRegistryFormId.value,
      pageWindowInstanceId: pageWindowInstanceId.value,
    })
  } catch (error) {
    console.error('Error executing chip action:', error)
  }
}

const handleWidgetReplace = (payload: { widget: any; replaced: any[] }) => {
  if (payload.widget && payload.widget.payload) {
    selectedRowData.value = payload.widget.payload
  }
  executeEventAction('onReplace')
  executeEventAction('replace')
  executeEventAction('onChange')
}

const handleDragStart = (widget: any) => {
  if (widget && widget.payload) {
    selectedRowData.value = widget.payload
  }
  executeEventAction('onDrag')
  executeEventAction('preDrag')
}

const handleDragEnd = (widget: any) => {
  if (widget && widget.payload) {
    selectedRowData.value = widget.payload
  }
  executeEventAction('dragEnd')
}

const handleImport = (data: any) => {
  selectedRowData.value = data
  executeEventAction('onImport')
  executeEventAction('canImport')
}

const cloneTablePaginationSnapshot = (field: unknown): unknown => {
  if (field == null) return field
  try {
    return structuredClone(field)
  } catch {
    return JSON.parse(JSON.stringify(field))
  }
}

const applyTablePaginationEmptyPageRollback = (options: {
  tableName: string
  snapshotTableField: unknown
  clamp: TablePaginationEmptyPageClamp
}) => {
  const { tableName, snapshotTableField, clamp } = options
  tablePaginationEmptyPageClamp.value = clamp
  tablePaginationUiEpoch.value += 1

  const formId = elementContext?.formId || props.formPath?.formId
  const panelId = elementContext?.panelId || props.formPath?.panelId
  const elId = elementContext?.elementId || elementId.value
  const fieldPatch = buildTablePaginationEmptyPageFieldPatch(tableName, {
    snapshotTableField,
    clamp,
  })
  const typedFieldPatch = setTypes(fieldPatch as Record<string, unknown>)

  if (formId && panelId && elId && formProvider?.setValue) {
    formProvider.setValue({
      value: fieldPatch,
      formId,
      panelId,
      elementId: elId,
      pageDataSlot: pageDataSlot.value,
    })
  }

  const slotKey = String(pageDataSlot.value ?? '').trim()
  const resolvedStore = findPageStoreEntryForForm(appStore.pagesData as Record<string, any>, {
    slotId: slotKey || undefined,
    registryFormId: pageRegistryFormId.value || formId,
    formId,
    currentPageId: appStore.currentPageId,
    topDialogId: appStore.dialogIds?.length
      ? String(appStore.dialogIds[appStore.dialogIds.length - 1] ?? '')
      : '',
  })
  const storeKey = resolvedStore?.storeKey ?? slotKey
  if (!storeKey) return

  const pageEntry = (appStore.pagesData as Record<string, any>)[storeKey]
  if (!pageEntry || typeof pageEntry !== 'object') return

  const paramsPatch = buildTablePaginationEmptyPageParamsPatch(tableName, clamp)
  const mergeFields = (existing: Record<string, unknown> | undefined) => ({
    ...(existing || {}),
    ...typedFieldPatch,
  })

  appStore.setPageData(
    storeKey,
    {
      ...pageEntry,
      uniData: {
        ...(pageEntry.uniData || {}),
        clientData: {
          ...(pageEntry.uniData?.clientData || {}),
          params: {
            ...(pageEntry.uniData?.clientData?.params || {}),
            ...paramsPatch,
          },
        },
        fields: {
          ...(pageEntry.uniData?.fields || {}),
          fields: mergeFields(pageEntry.uniData?.fields?.fields),
        },
      },
      jsResult: {
        ...(pageEntry.jsResult || {}),
        changed_data: {
          ...(pageEntry.jsResult?.changed_data || {}),
          fields: {
            ...(pageEntry.jsResult?.changed_data?.fields || {}),
            fields: mergeFields(pageEntry.jsResult?.changed_data?.fields?.fields),
          },
        },
      },
    },
    undefined,
    undefined,
    'ElementRenderer.applyTablePaginationEmptyPageRollback',
  )
}

// Handle pagination meta updates by reloading server-side page
const handleUpdateMeta = async (newMeta: any) => {
  const endMeasure = startWorkflowMeasure('ElementRenderer.handleUpdateMeta', {
    elementName: elementApi.name.value,
    fieldName: elementApi.fieldName.value,
    elementId: elementId.value,
    formId: elementContext?.formId || props.formPath?.formId,
    panelId: elementContext?.panelId || props.formPath?.panelId,
    newMeta,
  })
  const tableName = elementApi.fieldName.value
  const currentMeta = getTableMeta() || {}
  const currentSort = currentMeta.sort || {}
  const nextSort = newMeta?.sort || {}
  const itemsPerPageChanged =
    Number(newMeta?.itemsPerPage ?? 0) !== Number(currentMeta.itemsPerPage ?? 0)
  const metaChanged =
    Number(newMeta?.page ?? 1) !== Number(currentMeta.page ?? 1) ||
    itemsPerPageChanged ||
    String(nextSort.column ?? '') !== String(currentSort.column ?? '') ||
    String(nextSort.direction ?? '') !== String(currentSort.direction ?? '')

  if (!metaChanged) {
    endMeasure()
    return
  }

  const paginationAction = itemsPerPageChanged ? 'maxOnPage' : 'page'
  const nextPage = Number(newMeta.page ?? currentMeta.page ?? 1)
  const nextMaxOnPage = Number(newMeta.itemsPerPage ?? currentMeta.itemsPerPage ?? 50)
  const nextSortColumn = String(nextSort.column ?? '').trim()
  const nextSortDirection = String(nextSort.direction ?? '').trim()

  if (itemsPerPageChanged) {
    tablePaginationEmptyPageClamp.value = null
    tablePaginationUiEpoch.value += 1
  }

  const paginationTableSnapshot =
    paginationAction === 'page' && nextPage > 1
      ? cloneTablePaginationSnapshot((pageFields.value as any)?.[tableName])
      : null

  // Сохраняем itemsPerPage в настройки элемента
  const formId = elementContext?.formId || props.formPath?.formId
  const pageId = elementContext?.pageId
  const panelId = elementContext?.panelId || props.formPath?.panelId
  const elId = elementContext?.elementId || elementId.value

  if (formId && pageId && panelId && elId && newMeta.itemsPerPage) {
    setElementSetting(formId, pageId, panelId, elId, 'itemsPerPage', newMeta.itemsPerPage)
  }

  // Include current form data to maintain context, but exclude table data
  const currentForm = elementApi.getContext().pageData
  const formData = elementApi.getContext().pageFields

  // Filter out table data to avoid sending large payloads
  const filteredFormData = Object.keys(formData).reduce((acc, key) => {
    // Skip table data fields
    if (key === tableName) {
      return acc
    }
    acc[key] = formData[key]
    return acc
  }, {} as any)

  const updatedFormData = {
    ...filteredFormData,
    ...buildTablePaginationReloadFields(tableName, {
      page: nextPage,
      maxOnPage: nextMaxOnPage,
      action: paginationAction,
      sortColumn: nextSortColumn || undefined,
      sortDirection: nextSortDirection || undefined,
    }),
  }

  // Set loading state
  isTableReloading.value = true
  try {
    startWorkflowMeasure('ElementRenderer.handleUpdateMeta.reloadStart', {
      elementName: elementApi.name.value,
      fieldName: tableName,
      elementId: elementId.value,
      currentMeta,
      newMeta,
      payload: profilePayload(updatedFormData),
    })()
    // Trigger element reload with updated form data
    await elementApi.reload(updatedFormData, {
      source: 'ElementRenderer.handleUpdateMeta',
    })

    if (
      paginationAction === 'page' &&
      paginationTableSnapshot &&
      isTablePaginationEmptyPageResponse({
        page: nextPage,
        currentItemsCount: extractUniDataTableRows((pageFields.value as any)?.[tableName]).length,
        paginationAction,
      })
    ) {
      applyTablePaginationEmptyPageRollback({
        tableName,
        snapshotTableField: paginationTableSnapshot,
        clamp: resolveTablePaginationEmptyPageCorrection({
          page: nextPage,
          maxOnPage: nextMaxOnPage,
        }),
      })
    }
  } finally {
    // Always reset loading state, even on error
    isTableReloading.value = false
    endMeasure()
  }
}

const executeElementAction = async (action: any) => {
  const platform = action.platform?._text || currentPlatform.value || undefined

  if (platform) {
    appStore.updateCurrentPlatform(platform)
  }

  const layout = action?.layout || {}
  const layoutLevel = layout.level?._text || 'element'
  const layoutId = layout.id?._text || layout.id
  const pageId = layoutLevel === 'page' && layoutId ? layoutId : undefined

  const actionContext: ActionContext = {
    id: action._attributes?.id || '0',
    name: action.name?._text || 'Unknown Action',
    elementId:
      layoutLevel === 'element' && layoutId
        ? String(layoutId)
        : elementId.value != null
          ? String(elementId.value)
          : undefined,
    panelId: props.formPath?.panelId,
    formId: props.formPath.formId || elementContext?.formId,
    pageId: pageId?.toString(),
    level: layoutLevel,
    data: action,
    platform,
    windowType: action.window__type?._text,
    onClick: action.onClick?._text,
    confirm: action.confirm?._text,
    transfer: action.transfer?._text,
    transferType: action.transferType?._text,
    event: {
      dialogClosed: getActionDialogClosedScript(action as Record<string, any>),
      chainCalled: action.event?.chainCalled?._text,
    },
    flow: action.flow?._text,
    selectedData: null,
    doActionForAdjacentWindow: isTruthyFlag(action.doActionForAdjacentWindow?._text),
  }

  await actionService.executeAction(actionContext, {
    appStore,
    router,
    actions,
    dialog,
    reloadElement: reloadElement || undefined,
    refillElement: formProvider?.refillElement,
    openUrls,
    pageErrors,
    formStates,
    alerts,
    getForm,
    getPageData,
    getTableSelection: formProvider?.getTableSelection,
    runCode,
    formProvider,
    pageData: pageData.value,
    pageDataSlot: pageDataSlot.value,
    pageRegistryFormId: pageRegistryFormId.value,
    pageWindowInstanceId: pageWindowInstanceId.value,
  })
}

async function handlePrimaryMultiSelectClick() {
  const action = primaryMultiselectRightAction.value
  if (!action) return

  try {
    await executeElementAction(action)
  } catch (error) {
    console.error('Error executing primary multiselect action:', error)
  }
}

const mappedEventNames = computed(() => getSupportedEventNamesForType(elementApi.type.value))

const registeredMetadataEvents = computed(() => {
  const elementMetadata = props.config?.element || props.config
  const registeredEvents = resolveRegisteredEvents({
    elementType: elementApi.type.value,
    elementMetadata,
    eventActions: props.events || {},
  })

  return registeredEvents
})

const shouldDispatchMappedEvent = (eventName: string, eventAction?: any): boolean => {
  if (!eventName) return false
  const isMapped = mappedEventNames.value.has(eventName)
  if (!isMapped) {
    return Boolean(eventAction)
  }
  return registeredMetadataEvents.value.has(eventName) || Boolean(eventAction)
}

// Helper function to execute event-positioned actions
const executeEventAction = async (eventName: string, context?: any) => {
  if (eventName === 'onChange') {
    const formId = (elementContext as any)?.formId ?? props.formPath?.formId
  }
  // TEXT: onChange (Enter/blur/…) и textEdited (ввод); PASSWORD/TEXTAREA — только onChange
  const allowListenersForText = shouldAllowTextFieldListenerEvent(
    eventName,
    elementApi.type.value,
  )
  if (elementId.value && allowListenersForText) {
    await invokeListeners('element', elementId.value, eventName, context || {})
  }

  // Event action from pageActions (layout position=events); fallback to formProvider if not in props.events
  let eventAction = props.events?.[eventName]
  const formProviderAny = formProvider as any
  if (!eventAction && formProviderAny?.getEventActionForElement && elementId.value != null) {
    const formId = (elementContext as any)?.formId ?? props.formPath?.formId
    if (formId != null) {
      eventAction = formProviderAny.getEventActionForElement(formId, elementId.value, eventName)
    }
  }
  const shouldDispatchEventAction = shouldDispatchMappedEvent(eventName, eventAction)

  // For onSelectionChanged: if element has doActionForAdjacentWindow, run full action flow (split) even when eventAction exists
  // The onClick may come from eventAction (referenced action) or from the element
  if (eventName === 'onSelectionChanged' && shouldDispatchEventAction && props.config && formProvider?.runAction) {
    const el = props.config?.element || props.config
    const hasSplit = el?.doActionForAdjacentWindow?._text === 'true' || el?.doActionForAdjacentWindow?._text === '1'
    const hasTransfer = el?.transferType?._text || el?.transfer?._text
    if (hasSplit && hasTransfer) {
      const onClickFromAction = eventAction?.onClick?._text
      const documentViewFromAction = eventAction?.documentView?._text
      const mergedData = (onClickFromAction || documentViewFromAction)
        ? {...el, ...(onClickFromAction && {onClick: eventAction.onClick}), ...(documentViewFromAction && {documentView: eventAction.documentView})}
        : el
      await formProvider.runAction({
        data: mergedData,
        level: 'element',
        eventName: 'onSelectionChanged',
        selectedData: context?.selectedData,
        formId: (elementContext as any)?.formId ?? props.formPath?.formId,
        panelId: (elementContext as any)?.panelId ?? props.formPath?.panelId,
        elementId: props.formPath?.elementId,
      })
      return
    }
  }

  if (eventAction && shouldDispatchEventAction) {
    await elementApi.runEvent(eventAction, eventName, context)
    return
  }

  // Fallback для onChange: если в form.actions нет action (position=events), всё равно вызываем runAction с конфигом элемента —
  // бэкенд выполняет скрипт по elementId и ScriptName.ON_CHANGE
  const el = props.config?.element || props.config
  const inlineOnChangeScript = pickElementEventScript(el, 'onChange') ?? ''
  const isEditableTableOnChange =
    eventName === 'onChange' &&
    elementApi.type.value === 'EDITABLE' &&
    inlineOnChangeScript !== ''
  const onChangeScriptNode = pickElementEventScriptNode(el, eventName)
  const runEditableOnChangeOnClient =
    isEditableTableOnChange &&
    shouldExecuteElementOnChangeOnClient(onChangeScriptNode, inlineOnChangeScript, {
      clientImportNames: Object.keys(appStore.imports ?? {}),
      serverOnlyImportNames: appStore.serverOnlyImportNames ?? [],
    }, readRootFormFunctions((pageData.value as { form?: unknown } | undefined)?.form))
  const onChangeAlreadyRanViaInput = shouldTreatOnChangeAsAlreadyRanViaInput({
    eventName,
    inlineOnChangeScript,
    elementType: elementApi.type.value,
    isEditableTableOnChange,
  })
  // onChange уже из _input → onElement (DATE/DATETIME и др.) — не дублировать runAction/runCode.
  if (onChangeAlreadyRanViaInput) {
    return
  }
  if (
    eventName === 'onChange' &&
    !eventAction &&
    !runEditableOnChangeOnClient &&
    formProvider?.runAction &&
    elementId.value != null &&
    el
  ) {
    const formId = (elementContext as any)?.formId ?? props.formPath?.formId
    const panelId = (elementContext as any)?.panelId ?? props.formPath?.panelId
    if (formId != null) {
      await formProvider.runAction({
        data: el,
        level: 'element',
        eventName,
        selectedData: context?.selectedData ?? context?.value,
        limitToSelectedData: Boolean(context?.selectedData && (context?.selectedIdx != null || context?.column != null)),
        formId,
        panelId,
        elementId: elementId.value,
        pageSlotId: pageDataSlot.value,
      })
      return
    }
  }

  if (
    eventName === 'onSelectionChanged' &&
    !eventAction &&
    formProvider?.runAction &&
    elementId.value != null &&
    el
  ) {
    const formId = (elementContext as any)?.formId ?? props.formPath?.formId
    const panelId = (elementContext as any)?.panelId ?? props.formPath?.panelId
    if (formId != null) {
      await formProvider.runAction({
        data: el,
        level: 'element',
        eventName: 'onSelectionChanged',
        selectedData: context?.selectedData,
        limitToSelectedData: Boolean(context?.selectedData),
        formId,
        panelId,
        elementId: elementId.value,
        pageSlotId: pageDataSlot.value,
      })
      return
    }
  }

  // EDITABLE: web_client_exec или client-safe (extractProtocolAndDomain / putFullData).
  const script = pickElementEventScript(el, eventName)
  if (
    script &&
    runCode &&
    (!isEditableTableOnChange || runEditableOnChangeOnClient)
  ) {
    runCode(script, resolveInvokeEventSelectedData(context) ?? context ?? {})
  }
}

const buildSelectedData = (row: any, normalized: any) => {
  if (row?.fields && typeof row.fields === 'object') {
    return row
  }

  if (!normalized || typeof normalized !== 'object') {
    return null
  }

  const fields: Record<string, any> = {}
  Object.keys(normalized).forEach((key) => {
    fields[key] = setType(normalized[key])
  })

  return {fields}
}

const extractRowDataFromEvent = (event: any) => {
  const item = event?.item
  const itemIsRowLike =
    item &&
    typeof item === 'object' &&
    (item._id != null ||
      item._typeId != null ||
      item.fields ||
      item._rowData ||
      item._originalRow)
  const candidateList = [
    event?.rowData,
    event?.node?.data,
    event?.data,
    itemIsRowLike ? item : null,
    event?.row,
    event?.data?.data,
  ]
  const rawCandidate = candidateList.find((candidate) => candidate && typeof candidate === 'object') ?? null
  const rawRow = rawCandidate?._rowData || rawCandidate?._originalRow || rawCandidate

  if (!rawRow || typeof rawRow !== 'object') {
    return {raw: null, normalized: null}
  }

  const normalized: any = {...rawRow}
  if (rawRow.fields && typeof rawRow.fields === 'object') {
    Object.keys(rawRow.fields).forEach((fieldName: string) => {
      const fieldData = rawRow.fields[fieldName]
      if (!normalized.hasOwnProperty(fieldName)) {
        normalized[fieldName] = fieldData?.value !== undefined ? fieldData.value : fieldData
      }
    })
  }

  if (!normalized._id) {
    normalized._id = rawRow.fields?._id?.value || rawRow._id || rawRow.requestId || rawRow.id
  }
  if (!normalized._typeId) {
    normalized._typeId = rawRow.fields?._typeId?.value || rawRow._typeId
  }
  if (!normalized.hasOwnProperty('_versionId')) {
    normalized._versionId = rawRow.fields?._versionId?.value || rawRow._versionId || null
  }

  const selectedData = buildSelectedData(rawRow, normalized)
  return {raw: rawRow, normalized, selectedData}
}

const handleTableDoubleClick = async (event: any) => {
  const elType = elementApi.type.value
  if (elType !== 'TABLE' && elType !== 'EDITABLE' && elType !== 'TREETABLE') {
    return
  }
  if (elType === 'TREETABLE' && isTreeTableGrowUpRootNode(event?.node)) {
    await handleTreeTableFindParent({ node: event.node })
    return
  }
  if (
    elType === 'TREETABLE' &&
    event?.node &&
    !isTreeTableSelectiveNode(event.node, {
      forbidSelectNode: isXmlTrue(props.config.element?.[IKey.TT_FORBID_SELECT_NODE]?._text, false),
    })
  ) {
    return
  }
  const {raw, normalized, selectedData} = extractRowDataFromEvent(event)
  if (!raw && !normalized && !selectedData) {
    return
  }
  let mergedSelected: any = {
    ...(selectedData || {}),
    ...(normalized || {}),
  }
  mergedSelected = addTableContextMenuServiceFields(mergedSelected, event)
  selectedRowData.value = mergedSelected
  if (typeof event?.selectedIdx === 'number' && event.selectedIdx >= 0) {
    lastTableContextSelectedIdx.value = event.selectedIdx
  }

  await executeEventAction('inlineDoFirstAction', {
    selectedData: mergedSelected,
    selectedIdx: event?.selectedIdx,
    column: event?.column,
    data: event?.data,
  })

  syncTableInlineActionVisibilityFromDeferred()
  const first = getTableContextMenuItems()[0]
  if (typeof first?.command === 'function') {
    await first.command({
      data: event?.data,
      node: event?.node,
      column: event?.column,
      selectedIdx: event?.selectedIdx,
      rowData: mergedSelected,
    })
  }
}

// Обработчики событий

function isTextLikeElementType(type = elementApi.type.value): boolean {
  return isTextLikeFormElementType(type)
}

function getLiveTextLikeValue(): unknown {
  if (!isTextLikeElementType()) {
    return getModelValue()
  }
  // Prefer in-progress buffer: blur may clear it after commit; click can race with controlled reset.
  if (textLikeEditingValue.value !== null) {
    return textLikeEditingValue.value
  }
  const inst = componentRef.value as { $el?: HTMLElement } | HTMLElement | null
  const root = inst && '$el' in inst ? inst.$el : inst
  const input = root?.querySelector?.('input, textarea') as
    | HTMLInputElement
    | HTMLTextAreaElement
    | null
    | undefined
  if (input && String(input.value ?? '') !== '') {
    return input.value
  }
  const modelValue = getModelValue()
  if (input) {
    // Controlled empty model can wipe DOM after editing buffer clear — keep non-empty model.
    return modelValue !== undefined && modelValue !== null && String(modelValue) !== ''
      ? modelValue
      : input.value
  }
  return modelValue
}

function resolveTextLikeCommittedValue(value: unknown): unknown {
  if (value instanceof Event) {
    const target = value.target
    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
      return target.value
    }
    return getLiveTextLikeValue()
  }
  if (value !== undefined && value !== null) {
    return value
  }
  return getLiveTextLikeValue()
}

function commitTextLikeFieldToStore(value: unknown): void {
  elementApi._input(value, { skipOnElement: true })
}

const handleInput = async (value: any) => {
  if (elementApi.type.value === 'FILEATTACHMENTS') {
    const items = Array.isArray(value) ? (value as FileItem[]) : []
    fileAttachmentsElement.handleInput(items)
    return
  }

  // Transform MediaWallModel back to backend format
  if (
    elementApi.type.value === 'MEDIAWALL' &&
    value &&
    typeof value === 'object' &&
    value.widgets
  ) {
  } else {
    const editableTableUpdate =
      elementApi.type.value === 'EDITABLE' &&
      value &&
      typeof value === 'object' &&
      (value as any).__editableTableUpdate
    const normalizedValue =
      editableTableUpdate
        ? (value as any).value
        : elementApi.type.value === 'MULTISELECT'
        ? toMultiSelectTypedValue(value)
        : value
    const isTextLike = isTextLikeElementType()
    // DATE/DATETIME/DATERANGE: всегда коммитить в pagesData (иначе syncPageDataToRegistry
    // перед action откатывает UI к stale __first/__second — форма 2565 / поиск 27518).
    const isDateLikeField =
      elementApi.type.value === 'DATE' ||
      elementApi.type.value === 'DATETIME' ||
      elementApi.type.value === 'DATERANGE'
    if (isDateLikeField || isSelectionFormElementType(elementApi.type.value)) {
      markUserInteraction()
    }
    const isUserInput = hasRecentUserInteraction()
    if (isTextLike) {
      markUserInteraction()
      textLikeEditingValue.value = normalizedValue as string | number
      textLikeDirtySinceFocus.value =
        String(normalizedValue ?? '') !== String(textLikeValueAtFocus.value ?? '')
    }
    if (
      isTextLike &&
      (normalizedValue === '' || normalizedValue == null)
    ) {
      const formId = (elementContext as any)?.formId ?? props.formPath?.formId
      if (
        formId != null &&
        (hasPendingTreeTableSearchApply(formId) ||
          getTreeTableSearchSubmittedCriteria(formId))
      ) {
        resetTreeTableSearchApplyState(formId)
      }
    }
    const deferOnChangeToHandleChange =
      hasElementOnChangeScript() && isSelectionFormElementType(elementApi.type.value)
    const previousSelectionValue = deferOnChangeToHandleChange
      ? toListboxScalarModelValue(elementApi.value.value)
      : undefined
    const { skipStore, skipOnElement } = resolveTextInputStoreOptions(isTextLike, false, {
      editableTableUpdate,
      isUserInput,
    })
    const forceStoreWriteForListboxDefault =
      (elementApi.type.value === 'LISTBOX' || elementApi.type.value === 'COMBOBOX') &&
      !isUserInput
    const shouldSkipStore =
      !forceStoreWriteForListboxDefault &&
      !deferOnChangeToHandleChange &&
      !isDateLikeField &&
      skipStore
    elementApi._input(normalizedValue, {
      ...(deferOnChangeToHandleChange || skipOnElement ? { skipOnElement: true } : {}),
      ...(shouldSkipStore ? { skipStore: true } : {}),
    })
    if (
      deferOnChangeToHandleChange &&
      shouldRunSelectionOnChangeAfterInput({
        previousValue: previousSelectionValue,
        nextValue: toListboxScalarModelValue(normalizedValue),
      })
    ) {
      await runSelectionOnChange(normalizedValue)
    }
    // TEXT: textEdited на пользовательский ввод; keyTyped — для не-text-like
    if (shouldFireTextEditedOnInput(elementApi.type.value, isUserInput) && !editableTableUpdate) {
      await executeEventAction('textEdited')
    }
    if (!isTextLike && !editableTableUpdate && isUserInput) {
      await executeEventAction('keyTyped')
      await executeEventAction('keyTyped=1s')
    }
  }

  // Emit input event for parent components
  emit('input', value)
}

function hasElementOnChangeScript(): boolean {
  const el = props.config?.element || props.config
  return Boolean(pickElementEventScript(el, 'onChange'))
}

const lastSelectionUserChangeAt = ref(0)
const lastSelectionUserChangeValue = ref<unknown>(undefined)

const runSelectionOnChange = async (value: unknown) => {
  if (
    shouldSkipDuplicateSelectionOnChange({
      value,
      lastValue: lastSelectionUserChangeValue.value,
      lastAt: lastSelectionUserChangeAt.value,
    })
  ) {
    return
  }
  lastSelectionUserChangeValue.value = value
  lastSelectionUserChangeAt.value = Date.now()
  markUserInteraction()
  elementApi._input(value, { skipOnElement: true, skipStore: false })
  const changeContext = { value, selectedData: value }
  await executeEventAction('onChange', changeContext)
  if (elementId.value) {
    await invokeListeners('element', elementId.value, 'change', changeContext)
  }
}

const handleSelectionUserChange = async (value: unknown) => {
  if (
    !isSelectionFormElementType(elementApi.type.value) ||
    !hasElementOnChangeScript()
  ) {
    return
  }
  await runSelectionOnChange(value)
}

/** Enter в TEXT/PASSWORD: commit + onChange (если есть) + emit для linked action в Element.vue (Swing VisualEntity). */
const handleEnter = async (value?: string | number) => {
  if (elementApi.type.value !== 'TEXT' && elementApi.type.value !== 'PASSWORD') {
    return
  }
  const committed = resolveTextLikeCommittedValue(value)
  commitTextLikeFieldToStore(committed)
  if (hasElementOnChangeScript()) {
    await runOnChangeForText(committed)
  }
  emit('enter', committed)
}

const pendingEditableChangePromise = ref<Promise<void> | null>(null)

const runHandleChange = async (value: any) => {
  const isTextLike = isTextLikeElementType()
  if (isTextLike) {
    const committed = resolveTextLikeCommittedValue(value)
    emit('change', committed)
    commitTextLikeFieldToStore(committed)
    // MInput: change по Enter (linked action в handleEnter) и при выборе из автодополнения
    await runOnChangeForText(committed)
    return
  }
  emit('change', value)
  const editableTableUpdate =
    elementApi.type.value === 'EDITABLE' &&
    value &&
    typeof value === 'object' &&
    (value as any).__editableTableUpdate

  let changeValue = editableTableUpdate ? (value as any).value : value
  let selectedData = editableTableUpdate ? (value as any).selectedData : value
  if (elementApi.type.value === 'MULTISELECT' && !editableTableUpdate) {
    const normalized = toMultiSelectTypedValue(value)
    changeValue = normalized
    selectedData = normalized
  }
  const changeContext = editableTableUpdate
    ? {
        value: changeValue,
        selectedData,
        selectedIdx: (value as any).selectedIdx,
        column: (value as any).column,
      }
    : { value: changeValue, selectedData }

  if (!editableTableUpdate && !hasRecentUserInteraction()) {
    return
  }

  if (
    !editableTableUpdate &&
    isSelectionFormElementType(elementApi.type.value) &&
    hasElementOnChangeScript()
  ) {
    await runSelectionOnChange(changeValue)
    return
  }

  if (editableTableUpdate) {
    await nextTick()
  }

  await executeEventAction('onChange', changeContext)
  if (elementId.value) {
    await invokeListeners('element', elementId.value, 'change', changeContext)
  }
}

const handleChange = async (value: any) => {
  const task = runHandleChange(value)
  pendingEditableChangePromise.value = task
  try {
    await task
  } finally {
    if (pendingEditableChangePromise.value === task) {
      pendingEditableChangePromise.value = null
    }
  }
}

const handleSelectionChange = async (selected: any[]) => {
  internalSelected.value = selected
  emit('update:selected', selected)

  // Register table selection for withCheckBoxes
  const withCheckbox = readElementWithCheckBoxes(props?.config?.element)
  if (
    (elementApi.type.value === 'TABLE' ||
      elementApi.type.value === 'EDITABLE' ||
      elementApi.type.value === 'TREETABLE') &&
    withCheckbox &&
    formProvider?.registerTableSelection &&
    elementId.value != null
  ) {
    const formId = (elementContext as any)?.formId ?? props.formPath?.formId
    const panelId = (elementContext as any)?.panelId ?? props.formPath?.panelId
    const elId = elementId.value
    const fieldName = elementApi.fieldName.value
    const rowsForServer = Array.isArray(selected)
      ? selected.map((r: any) => {
        const row = r?._rowData ?? r
        const idx = r?._rowIndex
        return {
          ...(row && typeof row === 'object' ? row : {}),
          _rowIndex: idx,
        }
      })
      : []
    formProvider.registerTableSelection(formId, panelId, elId, fieldName, rowsForServer)
  }

  // Pass selected data to event actions for transfer operations
  let selectedDataForEvent: any = selected
  const isTableEl =
    elementApi.type.value === 'TABLE' ||
    elementApi.type.value === 'EDITABLE' ||
    elementApi.type.value === 'TREETABLE'
  if (isTableEl && Array.isArray(selected) && selected.length === 1) {
    const selectedRow = selected[0]
    const tableItems = elementApi.type.value === 'TREETABLE' ? getTreeTableItems() : getTableItems()
    // Не доверять lastTableContextSelectedIdx: watch selectedKeys в TreeTable часто шлёт update:selected
    // раньше отложенного click — остаётся индекс прошлой строки (часто 0 = корень), и в executeScript уходит родитель.
    let selectedIdx = findTableContextRowIndex(tableItems, selectedRow)
    if (selectedIdx < 0 && lastTableContextSelectedIdx.value >= 0) {
      selectedIdx = lastTableContextSelectedIdx.value
    }
    const resolved = resolveTableContextSourceRow(
      tableItems,
      selectedRow,
      selectedIdx,
    )
    const columns = getAllElementColumns()
      .filter((col: any) => !!col?.name)
      .map((col: any) => ({name: col.name, type: col.type}))
    selectedDataForEvent = buildPlainRowForTableContext(
      resolved.row ?? null,
      columns,
      resolved.selectedIdx,
      lastTableContextColumn.value,
      elementApi.fieldName.value,
    )
  }

  const selectionContext = {selectedData: selectedDataForEvent, selected}
  await executeEventAction('selectionChanged', selectionContext)
  await executeEventAction('onSelectionChanged', selectionContext)

  if (elementId.value) {
    await invokeListeners('element', elementId.value, 'selectionChange', {selected})
  }
}

const handleBlur = async () => {
  const committed = isTextLikeElementType() ? getLiveTextLikeValue() : undefined
  const textLikeChangedOnBlur =
    isTextLikeElementType() &&
    (textLikeDirtySinceFocus.value ||
      String(committed ?? '') !== String(textLikeValueAtFocus.value ?? ''))
  // Skip no-op store writes: setPageData was snapping platform dialog size on every blur.
  if (isTextLikeElementType() && textLikeChangedOnBlur) {
    commitTextLikeFieldToStore(committed)
  }

  await executeEventAction('focusLost')

  // Call verify() on blur to validate the element
  if (elementApi.verify) {
    elementApi.verify()
  }

  if (elementApi.focused) {
    elementApi.removeFocus()
  }

  // Вызываем listeners для события blur
  if (elementId.value) {
    await invokeListeners('element', elementId.value, 'blur')
  }

  if (
    isTextLikeElementType() &&
    hasElementOnChangeScript() &&
    elementId.value &&
    !hasListeners('element', elementId.value, 'blur') &&
    textLikeChangedOnBlur
  ) {
    await runOnChangeForText(committed)
  }
  textLikeDirtySinceFocus.value = false
  textLikeValueAtFocus.value = undefined
  // Keep last committed value until next focus. Clearing to null races button-click
  // actions (blur → controlled model reset → empty live read → en.setContext).
  if (isTextLikeElementType() && committed != null) {
    textLikeEditingValue.value = committed as string | number
  } else {
    textLikeEditingValue.value = null
  }
}

const handleFocus = async () => {
  markUserInteraction()
  if (isTextLikeElementType()) {
    const live = getLiveTextLikeValue()
    textLikeValueAtFocus.value = live
    textLikeDirtySinceFocus.value = false
    textLikeEditingValue.value = live ?? ''
  }
  // Execute focusGained event-positioned action
  await executeEventAction('focusGained')

  // Вызываем listeners для события focus
  if (elementId.value) {
    await invokeListeners('element', elementId.value, 'focus')
  }

  // Запрос справочника автодополнения у middle только при фокусе на поле с атрибутом autoComplete.
  if (isAutoCompleteField.value && elementApi.getAutoCompleteList) {
    if (openSuggesterSuppressFocusFetch.value) {
      openSuggesterSuppressFocusFetch.value = false
      return
    }
    const currentValue = getModelValue()
    const filter = currentValue != null ? String(currentValue) : ''
    try {
      const list = await elementApi.getAutoCompleteList({
        filter,
        cursor: filter.length,
        forced: resolveAutocompleteForced(filter),
      })
      autoCompleteSuggestions.value = Array.isArray(list) ? list : []
    } catch {
      autoCompleteSuggestions.value = []
    }
  }
}

const handleClick = async (event?: any) => {
  markUserInteraction()
  const {raw, normalized, selectedData} = extractRowDataFromEvent(event)
  if (normalized) {
    selectedRowData.value = normalized
  }

  const isTilesEl = elementApi.type.value === 'TILES'
  const isTableEl =
    elementApi.type.value === 'TABLE' ||
    elementApi.type.value === 'EDITABLE' ||
    elementApi.type.value === 'TREETABLE'
  if (isTableEl && event) {
    if (typeof event?.selectedIdx === 'number') {
      lastTableContextSelectedIdx.value = event.selectedIdx
    }
    const resolvedColumn = resolveTableContextColumn(event)
    if (resolvedColumn !== '') {
      lastTableContextColumn.value = resolvedColumn
    }
  }

  const isTableRowClick = !!event?.data || !!event?.node
  if (isTilesEl && isTableRowClick && getTableInlineActions().length > 0) {
    if (raw || normalized || selectedData) {
      let mergedSelected: any = {
        ...(selectedData || {}),
        ...(normalized || {}),
      }
      mergedSelected = addTableContextMenuServiceFields(mergedSelected, event)
      selectedRowData.value = mergedSelected
      if (typeof event?.selectedIdx === 'number' && event.selectedIdx >= 0) {
        lastTableContextSelectedIdx.value = event.selectedIdx
      }
      await executeEventAction('inlineDoFirstAction', {
        selectedData: mergedSelected,
        selectedIdx: event?.selectedIdx,
        data: event?.data,
      })
      await nextTick()
      const firstAction = getTableInlineActions()[0]
      if (firstAction) {
        await executeTableInlineAction(firstAction, {
          data: event?.data,
          selectedIdx: event?.selectedIdx,
          rowData: mergedSelected,
        })
      }
    }
    return
  }

  const hasInlineActions =
    ['TABLE', 'TREETABLE', 'GROUPTABLE', 'EDITABLE'].includes(elementApi.type.value) &&
    isTableRowClick &&
    getTableContextMenuItems().length > 0

  // Execute onClick event-positioned action
  if (!hasInlineActions) {
    await executeEventAction('onClick', {selectedData: selectedData || raw || normalized})
  }

  // Вызываем listeners для события click
  if (elementId.value && !hasInlineActions) {
    await invokeListeners('element', elementId.value, 'click', {
      selectedData: selectedData || raw || normalized,
    })
  }
}

const handleHover = async () => {
  // Execute onHover event-positioned action
  await executeEventAction('onHover')

  // Вызываем listeners для события hover
  if (elementId.value) {
    await invokeListeners('element', elementId.value, 'hover')
  }
}

const handleKeyPress = async (event: KeyboardEvent) => {
  markUserInteraction()
  // Execute keyPressed event-positioned action
  await executeEventAction('keyPressed')

  // Вызываем listeners для события keyPress
  if (elementId.value) {
    await invokeListeners('element', elementId.value, 'keyPress', {event})
  }
}

const handleKeyRelease = async (event: KeyboardEvent) => {
  markUserInteraction()
  // Execute keyReleased event-positioned action
  await executeEventAction('keyReleased')

  // Вызываем listeners для события keyRelease
  if (elementId.value) {
    await invokeListeners('element', elementId.value, 'keyRelease', {event})
  }
}

const handleFileChange = async () => {
  // Execute fileChanged event-positioned action
  await executeEventAction('fileChanged')

  // Вызываем listeners для события fileChange
  if (elementId.value) {
    await invokeListeners('element', elementId.value, 'fileChange')
  }
}

const buildFileDownloadItem = (): FileItem => {
  const meta = getFileDownloadMeta()
  const fields: Record<string, { type: string; value: unknown }> = {}
  const hasCode = meta.code !== undefined && meta.code !== null && meta.code !== ''
  if (hasCode) {
    fields.code = { type: 'Long', value: Number(meta.code) }
    fields.version = {
      type: 'Long',
      value:
        meta.version !== undefined && meta.version !== null && meta.version !== ''
          ? Number(meta.version)
          : 0,
    }
  }
  if (meta.fileName) {
    fields.name = { type: 'String', value: meta.fileName }
  }
  const documentSessionId = String(meta.documentSessionId ?? '').trim()
  if (documentSessionId) {
    fields.documentSessionId = { type: 'String', value: documentSessionId }
    fields[ATTACHMENT_DOCUMENT_SESSION_ID] = { type: 'String', value: documentSessionId }
  }
  return { fields } as FileItem
}

const persistFileDownloadMeta = (meta: PlatformDownloadMeta) => {
  const fieldName = String(elementApi.fieldName.value ?? '').trim()
  const elementIdForDownload = props.formPath.elementId

  if (fieldName && formProvider?.setValue) {
    const formId = elementContext?.formId ?? props.formPath?.formId
    const panelId = elementContext?.panelId ?? props.formPath?.panelId
    const elId = elementContext?.elementId ?? elementId.value
    if (formId && panelId && elId) {
      formProvider.setValue({
        value: buildPlatformDownloadPageFieldPatch(fieldName, meta),
        formId,
        panelId,
        elementId: elId,
      })
    }
  }

  const item = buildFileDownloadItem()
  if (elementIdForDownload != null) {
    applyPlatformDownloadMeta(item, elementIdForDownload, meta)
  }
}

const fetchFileDownloadBlob = async () => {
  const meta = getFileDownloadMeta()
  const sessionId = getSessionId()

  if (!sessionId) {
    console.warn('[FILEDOWNLOAD] Missing sessionId, download skipped')
    return null
  }

  const elementIdForDownload = props.formPath.elementId
  const item = buildFileDownloadItem()
  const documentSessionId = resolveAttachmentDocumentSessionId(item, elementIdForDownload)
  const fields = (item.fields ?? {}) as Record<string, unknown>
  const code = getScalarFromFields(fields, 'code', '_id', 'CODE', 'Code')
  if (!code && !documentSessionId) {
    console.warn('[FILEDOWNLOAD] Missing file code and documentSessionId, download skipped')
    return null
  }

  try {
    const result = await fetchPlatformAttachmentDownload({
      elementId: elementIdForDownload,
      sessionId,
      type: currentPlatform.value,
      item,
    })
    if (!result) return null

    persistFileDownloadMeta(result.meta)
    return { blob: result.blob, fileName: meta.fileName || String(meta.code) }
  } catch (error) {
    console.error('[FILEDOWNLOAD] Download failed', error)
    return null
  }
}

const handleFileDownload = async () => {
  try {
    const result = await fetchFileDownloadBlob()
    if (!result) return

    const url = window.URL.createObjectURL(result.blob)
    const a = document.createElement('a')
    a.style.display = 'none'
    a.href = url
    a.download = result.fileName || 'download'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('[FILEDOWNLOAD] Download failed', error)
  }
}

const handleFileOpen = async () => {
  try {
    const result = await fetchFileDownloadBlob()
    if (!result) return

    const url = window.URL.createObjectURL(result.blob)
    const opened = window.open(url, '_blank', 'noopener')

    if (!opened) {
      const a = document.createElement('a')
      a.style.display = 'none'
      a.href = url
      a.download = result.fileName || 'download'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    }

    window.setTimeout(() => window.URL.revokeObjectURL(url), 1000)
  } catch (error) {
    console.error('[FILEDOWNLOAD] Open failed', error)
  }
}

const handleAction = (event: any) => {
  if (event && event.action) {
    // Extract row data from event
    const rowData = event.rowData || event.data || event.item

    if (typeof event.action === 'function') {
      event.action(event)
    } else if (event.action?.command) {
      event.action.command(event)
    }
  }
}
const tryExpandEntireTree = (): boolean => {
  const inst = componentRef.value
  if (inst && typeof inst.expandEntireTree === 'function') {
    inst.expandEntireTree()
    return true
  }
  return false
}

const openSuggesterDropdown = (): boolean => {
  const inst = componentRef.value as { openAutocompleteDropdown?: () => void } | null
  if (typeof inst?.openAutocompleteDropdown === 'function') {
    inst.openAutocompleteDropdown()
    return true
  }
  const input = inst?.$el?.querySelector?.('input, textarea') as
    | HTMLInputElement
    | HTMLTextAreaElement
    | null
  if (input) {
    input.focus()
    return true
  }
  return false
}

/** Вызов из jsResult.states (open_suggester) — ref/input может появиться после refill/reload. */
const tryOpenSuggester = async (): Promise<boolean> => {
  if (!isAutoCompleteField.value || !elementApi.getAutoCompleteList) return false

  scrollIntoViewElement()
  const currentValue = getModelValue()
  const filter = currentValue != null ? String(currentValue) : ''
  try {
    const list = await elementApi.getAutoCompleteList({
      filter,
      cursor: filter.length,
      forced: true,
    })
    autoCompleteSuggestions.value = Array.isArray(list) ? list : []
  } catch {
    autoCompleteSuggestions.value = []
  }

  const hasLabeledRows = autoCompleteSuggestions.value.some(autocompleteRowHasLabel)
  await nextTick()
  openSuggesterSuppressFocusFetch.value = true

  if (hasLabeledRows && openSuggesterDropdown()) {
    return true
  }

  // SwingText.openSuggester: focus + onClick suggester (список может прийти с задержкой)
  if (isOnClickAutocomplete.value) {
    openSuggesterPending.value = !hasLabeledRows
    if (openSuggesterDropdown()) {
      return true
    }
    openSuggesterSuppressFocusFetch.value = false
  } else {
    openSuggesterSuppressFocusFetch.value = false
  }
  return false
}

watch(autoCompleteSuggestions, (rows) => {
  if (!openSuggesterPending.value) return
  const list = Array.isArray(rows) ? rows : []
  if (!list.some(autocompleteRowHasLabel)) return
  openSuggesterPending.value = false
  void nextTick(() => {
    openSuggesterSuppressFocusFetch.value = true
    if (openSuggesterDropdown()) return
    openSuggesterSuppressFocusFetch.value = false
  })
})

const openSuggesterFromServerScript = () => {
  void (async () => {
    await nextTick()
    if (await tryOpenSuggester()) return
    await nextTick()
    if (await tryOpenSuggester()) return
    window.setTimeout(() => void tryOpenSuggester(), 0)
    window.setTimeout(() => void tryOpenSuggester(), 50)
    window.setTimeout(() => void tryOpenSuggester(), 200)
    window.setTimeout(() => void tryOpenSuggester(), 400)
  })()
}

/** Вызов из jsResult.states (expand_all) — ref дерева может появиться позже первого processStates. */
const expandAllFromServerScript = () => {
  if (tryExpandEntireTree()) return
  void nextTick(() => {
    if (tryExpandEntireTree()) return
    window.setTimeout(() => tryExpandEntireTree(), 0)
    window.setTimeout(() => tryExpandEntireTree(), 50)
    window.setTimeout(() => tryExpandEntireTree(), 200)
  })
}

/** Колонки для getData: merged XML + runtime (Swing contract). */
const getTreeTableExportColumns = () => getTreeTableContractColumns()

/** SwingTreeTable.getData / getFullData — только колонки TreeTable, без сырых pageFields. */
const treeTableDefaultExpandByDefault = () =>
  resolveTreeTableExpandByDefault(
    props.config.element?.expandByDefault,
    props.config.element,
  )

const buildSwingTreeTableGetData = (_action?: unknown) => {
  void _action
  const columns = getTreeTableExportColumns()
  const rows = getTreeTableBackingRows()
  return buildTreeTableUniData(elementApi.fieldName.value, rows, columns, {
    addAll: true,
    defaultExpandByDefault: treeTableDefaultExpandByDefault(),
  })
}

// Инициализация элемента
const initializeElement = () => {
  const initialValue = getInitialValue()
  const treeExpandDefault = treeTableDefaultExpandByDefault()
  const treeTableGetDataExportContext = () => ({
    fieldName: elementApi.fieldName.value,
    columns: getTreeTableExportColumns(),
    defaultExpandByDefault: treeExpandDefault,
  })
  const treeHooks =
    elementApi.type.value === 'TREETABLE'
      ? {
          expandAll: expandAllFromServerScript,
          reloadNode: reloadNodeHook,
          buildReloadNodePayload: buildReloadNodePayloadFields,
          applyTreeTableSearch: (fieldsOverride?: Record<string, unknown>) =>
            applyTreeTableSearchFromPageFields({ forceApply: true, pageFieldsOverride: fieldsOverride }),
          getData: buildSwingTreeTableGetData,
          getFullData: () => buildSwingTreeTableGetData(null),
          getActionByColumnName: getTreeTableActionByColumnName,
          buildTreeTableGetDataExportContext: treeTableGetDataExportContext,
          treeTableDefaultExpandByDefault: treeExpandDefault,
          getTreeTableBackingRowsForExport: getTreeTableBackingRows,
          receiveFocus: () => {
            const inst = componentRef.value as { focusTreeTable?: () => void } | null
            inst?.focusTreeTable?.()
          },
        }
      : {}
  const fileHooks =
    elementApi.type.value === 'FILEATTACHMENTS'
      ? {
          ...fileAttachmentsElement.fileAttachmentHooks,
          prepareForFormSave: () => prepareFileAttachmentsForFormSave(),
        }
      : {}
  const swingTilesHooks =
    elementApi.type.value === 'TILES'
      ? {
          getData: (_action?: unknown) => componentRef.value?.swingGetData?.(_action),
          setInlineVisibility: (a: boolean | string, b?: boolean) =>
            componentRef.value?.swingSetInlineVisibility?.(a, b),
        }
      : {}
  const readLiveMultiSelectRows = () => {
    const inst = componentRef.value as { getLiveSelectedRows?: () => unknown } | null
    const live = inst?.getLiveSelectedRows?.()
    return Array.isArray(live) ? live : []
  }
  const multiSelectHooks =
    elementApi.type.value === 'MULTISELECT'
      ? {
          getData: (_action?: unknown) => {
            const fieldName = elementApi.fieldName.value
            return {
              [fieldName]: toMultiSelectTypedValue(readLiveMultiSelectRows()),
            }
          },
          getFullData: () => {
            const fieldName = elementApi.fieldName.value
            return {
              [fieldName]: toMultiSelectTypedValue(readLiveMultiSelectRows()),
            }
          },
        }
      : {}
  const checkboxLiveValueHooks =
    elementApi.type.value === 'CHECKBOX' || elementApi.type.value === 'BOOLEAN'
      ? {
          getLiveFieldValue: () => {
            const inst = componentRef.value as { getLiveTableValue?: () => unknown } | null
            const fromComponent = inst?.getLiveTableValue?.()
            if (fromComponent !== undefined && fromComponent !== null) {
              return fromComponent
            }
            return getModelValue()
          },
        }
      : {}
  const listboxLiveValueHooks =
    elementApi.type.value === 'LISTBOX' || elementApi.type.value === 'COMBOBOX'
      ? {
          getLiveFieldValue: () => {
            const fromWidget = toListboxScalarModelValue(elementApi.value.value)
            if (!isListboxEmptyValue(fromWidget)) return fromWidget
            return getModelValue()
          },
        }
      : {}
  const dateRangeLiveValueHooks =
    elementApi.type.value === 'DATERANGE'
      ? {
          // Приоритетнее stale pagesData __first/__second в applyDateRange (action/reload).
          getLiveFieldValue: () => getModelValue(),
        }
      : {}
  const readTextLikeElementSlotValue = (): unknown => {
    if (!formProvider || typeof formProvider.getForm !== 'function') return undefined
    const formId = elementContext?.formId ?? props.formPath?.formId
    const panelId = (elementContext as { panelId?: string | number } | null)?.panelId
      ?? props.formPath?.panelId
    const elementId = elementApi.id.value ?? props.formPath?.elementId
    const fieldName = elementApi.fieldName.value
    if (
      formId == null ||
      formId === '' ||
      panelId == null ||
      panelId === '' ||
      elementId == null ||
      elementId === '' ||
      Number(elementId) === -1 ||
      !fieldName
    ) {
      return undefined
    }
    const slot = formProvider.getForm({
      formId,
      panelId,
      elementId,
      flatten: true,
      pageDataSlot: String(pageDataSlot.value ?? '').trim() || undefined,
    })
    if (!slot || typeof slot !== 'object' || Array.isArray(slot)) return undefined
    const entry = (slot as Record<string, unknown>)[fieldName]
    if (entry && typeof entry === 'object' && !Array.isArray(entry) && 'value' in entry) {
      return unwrapScriptValue((entry as { value: unknown }).value)
    }
    return unwrapScriptValue(entry)
  }
  const readTextLikeWidgetValue = () => {
    const live = getLiveTextLikeValue()
    if (live !== undefined && live !== null && String(live).trim() !== '') {
      return live
    }
    const slot = readTextLikeElementSlotValue()
    if (slot !== undefined && slot !== null && String(slot).trim() !== '') {
      return slot
    }
    return resolveCommittedTextLikeModelValue(
      elementApi.fieldName.value,
      elementApi.value.value,
    )
  }
  const textLiveValueHooks =
    elementApi.type.value === 'TEXT' ||
    elementApi.type.value === 'PASSWORD' ||
    elementApi.type.value === 'TEXTAREA' ||
    elementApi.type.value === 'MULTILINETEXT'
      ? {
          getLiveFieldValue: () => readTextLikeWidgetValue(),
          getData: () => {
            const fieldName = elementApi.fieldName.value
            return { [fieldName]: readTextLikeWidgetValue() }
          },
        }
      : {}
  const textAutocompleteHooks =
    elementApi.type.value === 'TEXT' ? { openSuggester: openSuggesterFromServerScript } : {}
  const editableTableHooks =
    elementApi.type.value === 'EDITABLE'
      ? {
          commitPendingCellEdit: async () => {
            const inst = componentRef.value as { commitPendingCellEdit?: () => boolean } | null
            if (!inst?.commitPendingCellEdit?.()) return false
            await nextTick()
            for (let attempt = 0; attempt < 10; attempt++) {
              const pending = pendingEditableChangePromise.value
              if (pending) {
                await pending
                return true
              }
              await nextTick()
            }
            return true
          },
          getLiveFieldValue: () => elementApi.value.value,
        }
      : {}
  const isCheckboxTable =
    (elementApi.type.value === 'TABLE' ||
      elementApi.type.value === 'EDITABLE' ||
      elementApi.type.value === 'TREETABLE') &&
    isXmlTrue(props?.config?.element?.withCheckBoxes?._text, false)
  const resolveCheckboxTableField = () => {
    const fieldName = elementApi.fieldName.value
    const fromPage = (pageFields.value as Record<string, unknown> | undefined)?.[fieldName]
    if (fromPage !== undefined && fromPage !== null) {
      return fromPage
    }
    return elementApi.value.value
  }
  const buildSwingCheckboxTableGetData =
    (addAllOverride?: boolean) =>
    (_action?: unknown) => {
      void _action
      const fieldName = elementApi.fieldName.value
      const formId = (elementContext as any)?.formId ?? props.formPath?.formId
      const panelId = (elementContext as any)?.panelId ?? props.formPath?.panelId
      const addAll =
        addAllOverride === true ||
        shouldCheckboxTableExportAllRowsForAction(_action, elementId.value)
      const selection = formProvider?.getTableSelection?.(formId, panelId, elementId.value)
      const selectedRows =
        addAll || !selection?.fieldName || selection.fieldName !== fieldName
          ? []
          : selection.rows

      const result = collectTableLeafGetData({
        fieldName,
        elementType: elementApi.type.value,
        withCheckBoxes: true,
        mode: addAll ? 'full' : 'external',
        tableField: resolveCheckboxTableField(),
        selectedRows,
      })
      if (result.kind === 'table') {
        return { [fieldName]: result.field }
      }
      return {}
    }
  const checkboxTableHooks = isCheckboxTable
    ? {
        withCheckBoxes: true,
        getData: buildSwingCheckboxTableGetData(false),
        getFullData: () => buildSwingCheckboxTableGetData(true)(null),
      }
    : {}
  if (elementApi.type.value === 'TEXT') {
    const xmlLimit = parseMultipleLimitFromXml(props.config.element?.multipleLimit)
    if (xmlLimit != null) {
      elementApi.setMultipleLimit(xmlLimit)
    }
  }
  elementApi.register(elementApi.fieldName.value, initialValue, {
    refill: handleRefill,
    scrollIntoViewElement,
    setMultipleLimit: elementApi.setMultipleLimit,
    setAutocompleteMode: elementApi.setAutocompleteMode,
    setEnableSuggestEdit: elementApi.setEnableSuggestEdit,
    setMultipleDelimiter: elementApi.setMultipleDelimiter,
    ...treeHooks,
    ...fileHooks,
    ...swingTilesHooks,
    ...multiSelectHooks,
    ...checkboxLiveValueHooks,
    ...listboxLiveValueHooks,
    ...dateRangeLiveValueHooks,
    ...textLiveValueHooks,
    ...textAutocompleteHooks,
    ...editableTableHooks,
    ...checkboxTableHooks,
  })
}

const getInitialValue = () => {
  const type = elementApi.type.value
  const fieldName = elementApi.fieldName.value
  const defaultValue = props.config.element?.value

  const possibleFieldNames =
    type === 'MULTISELECT'
      ? [fieldName]
      : type === 'LISTBOX' || type === 'COMBOBOX'
        ? [fieldName]
        : [
            fieldName,
            `${fieldName}__listbox`,
            `${fieldName}__items`,
            `${fieldName}__action`,
          ]

  let backendField = undefined
  let usedFieldName = null

  if (swingTilesRowScope) {
    const fromRow = resolveTilesCloneRowFieldRaw(swingTilesRowScope, fieldName)
    if (fromRow !== undefined && fromRow !== null) {
      backendField =
        fromRow && typeof fromRow === 'object' && !Array.isArray(fromRow) && 'value' in fromRow
          ? (fromRow as { value: unknown }).value
          : fromRow
      usedFieldName = fieldName
    }
  } else {
    for (const testFieldName of possibleFieldNames) {
      const entry = resolvePageFieldEntry(
        (pageFields.value ?? {}) as Record<string, unknown>,
        testFieldName,
      )
      if (entry === undefined || !fieldEntryHasPresentableValue(entry)) continue
      backendField =
        entry && typeof entry === 'object' && !Array.isArray(entry) && 'value' in entry
          ? (entry as { value: unknown }).value
          : entry
      usedFieldName = testFieldName
      break
    }
  }

  const prefer = backendField !== undefined ? backendField : defaultValue
  const normalizeArrayValue = (value: any, fallback: any[] = []) => {
    if (Array.isArray(value)) {
      return value
    }
    if (value === undefined || value === null || value === '') {
      return fallback
    }
    return fallback
  }

  switch (type) {
    case 'DATERANGE': {
      // Пустое основное поле — пустой диапазон, без подстановки __first/__second
      // if (prefer === '' || prefer == null) {
      //   return {from: null, to: null}
      // }
      if (typeof prefer === 'object' && prefer !== null && !Array.isArray(prefer) && ('from' in prefer || 'to' in prefer)) {
        return {
          from: (prefer as {from?: unknown}).from ?? null,
          to: (prefer as {to?: unknown}).to ?? null,
        }
      }
      const parseDateRangePart = (s: unknown): Date | null => {
        if (s == null || s === '') return null
        if (s instanceof Date) {
          return Number.isNaN(s.getTime()) ? null : s
        }
        const str = String(s).trim()
        const iso = new Date(str)
        if (!Number.isNaN(iso.getTime())) return iso
        const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(str)
        if (!m) return null
        const d = Number.parseInt(m[1], 10)
        const mo = Number.parseInt(m[2], 10) - 1
        const y = Number.parseInt(m[3], 10)
        const dt = new Date(y, mo, d)
        return Number.isNaN(dt.getTime()) ? null : dt
      }
      const firstRaw = pageFields.value?.[`${fieldName}__first`]?.value
      const secondRaw = pageFields.value?.[`${fieldName}__second`]?.value
      if (firstRaw === undefined && secondRaw === undefined) {
        return {from: null, to: null}
      }
      return {
        from: parseDateRangePart(firstRaw),
        to: parseDateRangePart(secondRaw),
      }
    }
    case 'CHECKBOX': {
      const opts = getCheckboxOptions()
      if (opts.length === 0) {
        return prefer ?? ''
      }
      if (
        prefer &&
        typeof prefer === 'object' &&
        !Array.isArray(prefer) &&
        Array.isArray((prefer as {rows?: unknown}).rows) &&
        (prefer as {rows: unknown[]}).rows.length > 0
      ) {
        return prefer
      }
      return {
        columns: ['id', 'description', 'value'],
        rows: opts.map((o) => ({
          id: o.id,
          description: o.label,
          value: false,
        })),
        empty: false,
      }
    }
    case 'BOOLEAN':
      return Boolean(prefer)
    case 'LISTBOX':
    case 'COMBOBOX': {
      const pf = (pageFields.value ?? {}) as Record<string, any>
      if (usedFieldName === fieldName && prefer !== undefined) {
        let scalar: unknown = prefer
        if (prefer && typeof prefer === 'object' && !Array.isArray(prefer) && 'value' in prefer) {
          scalar = (prefer as { value?: unknown }).value ?? ''
        }
        if (
          scalar &&
          typeof scalar === 'object' &&
          !Array.isArray(scalar) &&
          Array.isArray((scalar as { rows?: unknown }).rows)
        ) {
          scalar = ''
        }
        return normalizeListboxFieldValue(scalar, pf, fieldName)
      }
      const fromPage = resolveListboxFieldValueFromPageFields(pf, fieldName)
      if (fromPage !== null && fromPage !== undefined) {
        return fromPage
      }
      return normalizeListboxFieldValue('', pf, fieldName)
    }
    case 'TABLE':
    case 'EDITABLE':
    case 'TREETABLE':
      return prefer ?? {rows: []}
    case 'MULTISELECT':
      return createMultiSelectValue(
        pageFields.value?.[fieldName]?.value ??
        pageFields.value?.[fieldName] ??
        prefer,
      )
    case 'SLIDER':
      return parseInt(prefer) || 0
    case 'PROGRESSBAR':
      return parseInt(prefer) || 0
    case 'IDENTIFICATOR':
      return prefer ?? null
    case 'STRINGRANGE':
      return prefer ?? {relation: '=', first: '', second: ''}
    case 'HTML':
      if (prefer && typeof prefer === 'object' && !Array.isArray(prefer) && 'value' in prefer) {
        return prefer.value ?? ''
      }
      return prefer ?? ''
    case 'DIRECTORY':
      return prefer ?? ''
    case 'FILEUPLOAD':
    case 'EXTFILEUPLOAD':
      return normalizeArrayValue(prefer, [])
    case 'FILEDOWNLOAD':
    case 'EXTFILEDOWNLOAD':
      return getFileDownloadModelValue()
    case 'TILES': {
      if (prefer && typeof prefer === 'object' && !Array.isArray(prefer)) {
        const r = (prefer as any).rows
        if (Array.isArray(r)) return { rows: r }
        const rv = (prefer as any).value?.rows
        if (Array.isArray(rv)) return { rows: rv }
      }
      const arr = normalizeArrayValue(prefer, [])
      return { rows: Array.isArray(arr) ? arr : [] }
    }
    case 'SVG':
      return prefer ?? ''
    case 'GROUPTABLE':
      return prefer ?? {rows: []}
    case 'FILEATTACHMENTS':
      return uniDataTableToFileItems(prefer)
    case 'TASKS':
      return normalizeArrayValue(prefer, [])
    case 'APPROVAL':
      return normalizeArrayValue(prefer, [])
    case 'MEDIAWALL':
      if (prefer) return transformMediaWallData(prefer)
      return {gridWidth: 10, gridHeight: 8, widgets: []}
    case 'VIDEOPLAYER':
      return prefer ?? ''
    case 'DOWNLOADTASK':
      return normalizeArrayValue(prefer, [])
    case 'DATE':
    case 'DATETIME': {
      const pageEntry = resolvePageFieldEntry(
        (pageFields.value ?? {}) as Record<string, unknown>,
        fieldName,
      )
      if (pageEntry !== undefined) {
        const dateProps = resolveDateTimeVisibilityProps(elementApi.format.value)
        const fromPage = parsePageFieldToDate(pageEntry, { timeOnly: dateProps.timeOnly === true })
        if (fromPage) return fromPage
      }
      const parsed = resolveDateTimeModelValue(fieldName, prefer)
      return parsed ?? null
    }
    default:
      return prefer ?? ''
  }
}

const handleReloaded = (data: any) => {
  if (elementApi.type.value !== 'FILEATTACHMENTS') return
  handleRefill(data, { mergeAttachments: true, cause: 'selection-reload' })
}

const handleRefill = (
  data: any,
  options?: { mergeAttachments?: boolean; cause?: AttachmentStoreMutationCause },
) => {
  // Data can come in multiple formats:
  // 1. {fieldName: {value: ..., type: ...}} - from GlobalProvider.refillElement
  // 2. {value: ..., type: ...} - direct UniData format
  // 3. Just the value itself
  // 4. ответ с changed_data.fields.fields

  if (data === undefined || data === null) {
    return
  }

  const tableType = elementApi.type.value
  if (
    (tableType === 'TABLE' || tableType === 'EDITABLE' || tableType === 'TREETABLE') &&
    readElementWithCheckBoxes(props?.config?.element)
  ) {
    internalSelected.value = []
    emit('update:selected', [])
    if (formProvider?.registerTableSelection && elementId.value != null) {
      const formId = (elementContext as any)?.formId ?? props.formPath?.formId
      const panelId = (elementContext as any)?.panelId ?? props.formPath?.panelId
      formProvider.registerTableSelection(
        formId,
        panelId,
        elementId.value,
        elementApi.fieldName.value,
        [],
      )
    }
  }

  if (isTextLikeElementType()) {
    textLikeEditingValue.value = null
  }

  const fieldName = elementApi.fieldName.value
  let src = data
  if (
    typeof data === 'object' &&
    !Array.isArray(data) &&
    fieldName &&
    !(fieldName in data) &&
    data.fields?.fields &&
    typeof data.fields.fields === 'object' &&
    Object.prototype.hasOwnProperty.call(data.fields.fields, fieldName)
  ) {
    const inner = (data.fields.fields as Record<string, unknown>)[fieldName]
    if (
      inner &&
      typeof inner === 'object' &&
      !Array.isArray(inner) &&
      'value' in inner &&
      'type' in inner
    ) {
      src = {[fieldName]: inner}
    }
  }
  if (
    typeof data === 'object' &&
    !Array.isArray(data) &&
    fieldName &&
    !(fieldName in data) &&
    data.changed_data?.fields?.fields &&
    typeof data.changed_data.fields.fields === 'object' &&
    Object.prototype.hasOwnProperty.call(data.changed_data.fields.fields, fieldName)
  ) {
    const inner = (data.changed_data.fields.fields as Record<string, unknown>)[fieldName]
    if (
      inner &&
      typeof inner === 'object' &&
      !Array.isArray(inner) &&
      'value' in inner &&
      'type' in inner
    ) {
      src = {[fieldName]: inner}
    }
  }

  let typedValueToSet: any = undefined

  if (typeof src === 'object' && !Array.isArray(src)) {
    // Check if data has the field name as a key (format 1)
    if (fieldName && fieldName in src) {
      const fieldData = (src as Record<string, unknown>)[fieldName]
      if (
        fieldData &&
        typeof fieldData === 'object' &&
        !Array.isArray(fieldData) &&
        'value' in fieldData &&
        'type' in fieldData
      ) {
        typedValueToSet = fieldData
      } else {
        typedValueToSet = setType(fieldData)
      }
    }
    // Check if data has 'value' property directly (format 2 - UniData format)
    else if ('value' in src && 'type' in src && typeof (src as { type: unknown }).type === 'string') {
      typedValueToSet = src
    }
      // If data is an object but not UniData format and doesn't have fieldName key,
    // try to find any key with a value property (fallback)
    else {
      const keys = Object.keys(src)
      if (keys.length > 0) {
        const firstKeyValue = (src as Record<string, unknown>)[keys[0]]
        if (
          firstKeyValue &&
          typeof firstKeyValue === 'object' &&
          !Array.isArray(firstKeyValue) &&
          'value' in firstKeyValue &&
          'type' in firstKeyValue
        ) {
          typedValueToSet = firstKeyValue
        } else {
          typedValueToSet = setType(firstKeyValue)
        }
      } else {
        typedValueToSet = setType(src)
      }
    }
  } else {
    // Data is a primitive value or array
    typedValueToSet = setType(src)
  }

  if (typedValueToSet && typeof typedValueToSet === 'object' && (typedValueToSet as { type?: string }).type === 'UniDataTable') {
    typedValueToSet = stripLeakedFormRows(typedValueToSet)
  }

  if (
    options?.mergeAttachments &&
    elementApi.type.value === 'FILEATTACHMENTS' &&
    typedValueToSet &&
    (typedValueToSet as { type?: string }).type === 'UniDataTable'
  ) {
    const current = getModelValue() as FileItem[]
    const currentItems = Array.isArray(current) ? current : []
    typedValueToSet = mergeAttachmentsUniDataTables(currentItems, typedValueToSet)
  }

  if (elementApi.type.value === 'DATE' || elementApi.type.value === 'DATETIME') {
    const dateProps = resolveDateTimeVisibilityProps(elementApi.format.value)
    const parsed = parsePageFieldToDate(typedValueToSet, { timeOnly: dateProps.timeOnly === true })
    if (parsed) {
      // Только forms registry — иначе setValue → pagesData → assign → refill → цикл.
      elementApi._input(parsed, { skipStore: true, skipOnElement: true })
      return
    }
    // Явный null-typed clear: bare null в mergePageFieldMaps не бьёт uniData (preserve),
    // и UI откатывается на предыдущую дату вместо пустого поля.
    elementApi._input({ type: 'Date', value: null }, { skipStore: false, skipOnElement: true })
    return
  }

  if (elementApi.type.value === 'FILEATTACHMENTS') {
    const refillCause = options?.cause ?? 'element-refill'
    markAttachmentStoreMutationCause({
      fieldName,
      elementId: elementId.value,
      cause: refillCause,
      detail: { mergeAttachments: options?.mergeAttachments ?? false },
    })
    logAttachmentStoreDebug('handle-refill', {
      cause: refillCause,
      fieldName,
      elementId: elementId.value,
      mergeAttachments: options?.mergeAttachments ?? false,
      payload: summarizeAttachmentFieldEntry(typedValueToSet),
    })
  }

  elementApi.refill({
    [fieldName]: typedValueToSet,
  })
}

const hasListboxPageFieldData = (
  pf: Record<string, any>,
  fieldName: string,
): boolean =>
  fieldName in pf ||
  `${fieldName}__listbox` in pf ||
  `${fieldName}__items` in pf

const isListboxCatalogLikeValue = (value: unknown): boolean => {
  const scalar = extractListboxScalarValue(value)
  return (
    scalar != null &&
    typeof scalar === 'object' &&
    !Array.isArray(scalar) &&
    Array.isArray((scalar as { rows?: unknown }).rows)
  )
}

const commitListboxScalarFromPageFields = (options?: { skipStore?: boolean }) => {
  const type = elementApi.type.value
  if (type !== 'LISTBOX' && type !== 'COMBOBOX') return
  const fieldName = elementApi.fieldName.value
  if (!fieldName) return

  const pf = (pageFields.value ?? {}) as Record<string, any>
  if (!hasListboxPageFieldData(pf, fieldName)) return

  const nextValue = resolveListboxFieldValueFromPageFields(pf, fieldName)
  if (nextValue === null || nextValue === undefined) return

  const currentValue = extractListboxScalarValue(elementApi.value.value)
  if (listboxFieldValuesEqual(currentValue, nextValue)) return

  const persistCatalogDefault =
    (isListboxEmptyValue(currentValue) || isListboxCatalogLikeValue(currentValue)) &&
    !isListboxEmptyValue(nextValue)
  const skipStore = (options?.skipStore ?? false) && !persistCatalogDefault

  elementApi._input(nextValue, {
    skipStore,
    skipOnElement: true,
  })
}

const syncListboxValueFromPageFields = () => {
  commitListboxScalarFromPageFields({ skipStore: true })
}

const syncBooleanValueFromPageFields = () => {
  if (elementApi.type.value !== 'BOOLEAN') return
  const fieldName = elementApi.fieldName.value
  if (!fieldName) return
  if (hasRecentUserInteraction()) return

  const nextValue = resolveBooleanFieldValueFromPageFields(
    (pageFields.value ?? {}) as Record<string, unknown>,
    fieldName,
  )
  if (nextValue === null) return

  const currentValue = elementApi.value.value
  const currentBool = typeof currentValue === 'boolean' ? currentValue : Boolean(currentValue)
  if (currentBool === nextValue) return

  elementApi._input(nextValue, { skipStore: true, skipOnElement: true })
}

watch(
  () => {
    const fieldName = elementApi.fieldName.value
    if (!fieldName) return null
    const type = elementApi.type.value
    if (type !== 'LISTBOX' && type !== 'COMBOBOX') return null
    const pf = (pageFields.value ?? {}) as Record<string, unknown>
    return {
      scalar: pf[fieldName],
      listbox: pf[`${fieldName}__listbox`],
      items: pf[`${fieldName}__items`],
    }
  },
  () => {
    syncListboxValueFromPageFields()
  },
  { immediate: true, deep: true },
)

watch(
  () => {
    const fieldName = elementApi.fieldName.value
    if (!fieldName) return null
    if (elementApi.type.value !== 'BOOLEAN') return null
    return resolvePageFieldEntry(
      (pageFields.value ?? {}) as Record<string, unknown>,
      fieldName,
    )
  },
  () => {
    syncBooleanValueFromPageFields()
  },
  { immediate: true, deep: true },
)

const syncDateTimeValueFromPageFields = () => {
  const type = elementApi.type.value
  if (type !== 'DATE' && type !== 'DATETIME') return
  const fieldName = elementApi.fieldName.value
  if (!fieldName) return

  const dateProps = resolveDateTimeVisibilityProps(elementApi.format.value)
  const timeOnly = dateProps.timeOnly === true
  const pageEntry = resolvePageFieldEntry(
    (pageFields.value ?? {}) as Record<string, unknown>,
    fieldName,
  )
  if (pageEntry !== undefined && fieldEntryLooksEmptyIncoming(pageEntry)) {
    const currentParsed = parsePageFieldToDate(elementApi.value.value, { timeOnly })
    if (currentParsed) {
      elementApi._input(null, { skipStore: true, skipOnElement: true })
    }
    return
  }

  const parsed = resolveDateTimeModelValue(fieldName, elementApi.value.value)
  if (!parsed) return

  const currentParsed = parsePageFieldToDate(elementApi.value.value, { timeOnly })
  if (currentParsed && currentParsed.getTime() === parsed.getTime()) {
    return
  }
  elementApi._input(parsed, { skipStore: true, skipOnElement: true })
}

watch(
  () => {
    const fieldName = elementApi.fieldName.value
    if (!fieldName) return null
    const type = elementApi.type.value
    if (type !== 'DATE' && type !== 'DATETIME') return null
    return resolvePageFieldEntry((pageFields.value ?? {}) as Record<string, unknown>, fieldName)
  },
  () => {
    syncDateTimeValueFromPageFields()
  },
  { immediate: true, deep: true },
)

/**
 * Возвращает blob содержимого вложения для превью и скачивания.
 *
 * - Строка с локальным `file: File` (ещё не на платформе) — сам `File`, без HTTP.
 * - Серверная строка (`code` задан) — `GET /platform/download` с `elementId`, `code`, `version`,
 *   `fileName` и контекстом сессии (`getPlatformFileSessionContext`, п. 16 WUI-293).
 *
 * Пробрасывается в `MFileInput` как `fetchAttachmentBlob` (ленивое превью / download).
 *
 * @returns `Blob` или `null`, если нет `sessionId` / `code` / ответа
 */
/**
 * Обработчик события `download-file` из `MFileInput`: сохранение файла и sidecar-подписей на диск.
 *
 * Локальный `File` — сразу `downloadBlobToFile`; серверная строка — через
 * `fetchAttachmentBlobForFile`, затем основной файл и записи из `signature_file_list`.
 */

/**
 * Закрытие диалога Р7: finalize lifecycle (dirty → awaiting_ws; иначе restore preOpen),
 * затем destroy DocEditor. Крестик DocEditor (`onRequestClose`) и `@close` диалога.
 */
const handleR7EditorDialogClose = () => {
  if (!r7EditorVisible.value && r7EditorTargetFile.value == null) return

  const closingFile = r7EditorTargetFile.value
  const closingCtx = r7EditorOpenContext.value
  const documentKey = r7ActiveDocumentKey.value
  const elementId =
    closingCtx?.elementId
    ?? fileAttachmentsElement.resolveR7EditorOpenContext()?.elementId
    ?? null

  const closeResult = finalizeR7AttachmentEditorClose({
    documentKey,
    item: closingFile,
    elementId,
  })
  attachmentR7Editor.finalizeEditorSession()

  if (closeResult.awaitingWs) {
    r7EditorNotification.info({
      summary: t('attachments.r7WaitingSummary'),
      detail: t('attachments.r7WaitingDetail'),
    })
  }

  r7ActiveDocumentKey.value = null
  r7EditorAwaitingShown.value = false
  r7EditorOpenContext.value = null
  r7EditorTargetFile.value = null
  if (closeResult.awaitingWs && closingFile) {
    r7InlinePreview.onEditorClosedAwaitingWs(closingFile)
  } else {
    r7InlinePreview.scheduleRestore(closingFile ?? undefined)
  }
  r7EditorVisible.value = false
}

onBeforeGetFullDataUpload = () => {
  if (fileAttachmentsFormSavePrepared) {
    fileAttachmentsFormSavePrepared = false
    return
  }
  if (r7EditorVisible.value) {
    handleR7EditorDialogClose()
  }
  r7InlinePreview.closeEditorsForFormSave()
}

onAfterGetFullDataUpload = () => {
  r7InlinePreview.endFormSaveHold()
}

prepareFileAttachmentsForFormSave = async () => {
  r7InlinePreview.beginFormSaveHold()
  try {
    if (r7EditorVisible.value) {
      const documentKey = r7ActiveDocumentKey.value
      const session = documentKey ? getEditingSession(documentKey) : undefined
      const dirtyOpen = Boolean(session?.wasDirty) && session?.editorMode === 'edit'
      let keptStaged = false
      if (dirtyOpen && documentKey) {
        const invoked = await fileAttachmentsElement.requestOfficeEditorForceSave(
          session?.documentSessionId,
        )
        if (!invoked) {
          fileAttachmentsElement.abortIfR7FormSaveWaitNotReady('error')
        }
        const waitResult = await fileAttachmentsElement.waitUntilR7NotBlockingGetFullData()
        const after = getEditingSession(documentKey)
        if (shouldDestroyR7EditorAfterFormSaveWait(waitResult, after)) {
          markEditorClosedKeepStaged(documentKey)
          attachmentR7Editor.finalizeEditorSession()
          r7ActiveDocumentKey.value = null
          r7EditorAwaitingShown.value = false
          r7EditorOpenContext.value = null
          r7EditorTargetFile.value = null
          r7EditorVisible.value = false
          keptStaged = true
        } else {
          fileAttachmentsElement.abortIfR7FormSaveWaitNotReady(waitResult)
        }
      }
      if (!keptStaged) {
        handleR7EditorDialogClose()
      }
    }
    await r7InlinePreview.prepareForFormSave()
    await fileAttachmentsElement.ensurePendingLocalFilesForUpload()
    fileAttachmentsFormSavePrepared = true
  } catch (error) {
    r7InlinePreview.endFormSaveHold()
    throw error
  }
}

/** `openAttachment` после `@shown` диалога (DOM placeholder готов). */
const runR7AttachmentEditorOpen = async (file: FileItem, ctx: NonNullable<typeof r7EditorOpenContext.value>) => {
  const snapshot = getDsAttachmentStore(ctx.elementId).getSnapshot(file)
  const stagingStorageUri = snapshot?.storageUri
  const stagingDocumentSessionId = snapshot?.documentSessionId
  const socketId = (await wsService.waitForSocketId(5000)) ?? wsService.getSocketId()
  await attachmentR7Editor.openAttachment(
    {
      item: file,
      elementId: ctx.elementId,
      elementName: ctx.elementName,
      session: ctx.session,
      mode: fileAttachmentsElement.resolveR7EditorMode(file),
      socketId,
    },
    {
      stagingStorageUri,
      stagingDocumentSessionId,
      onStagingMetaPersist: (item, meta) => {
        fileAttachmentsElement.persistStagingMetaForFile(item, meta)
      },
      onPrepared: (result) => {
        r7ActiveDocumentKey.value = result.documentKey
      },
      onEditorEvent: (name, payload) => {
        if (name === 'onDocumentStateChange' && payload === true) {
          const syncedKey = syncEditingDocumentKeyForAttachmentItem(file, ctx.elementId)
          if (syncedKey) {
            r7ActiveDocumentKey.value = syncedKey
          }
          markR7AttachmentDocumentDirty({
            item: file,
            elementId: ctx.elementId,
            documentKey: syncedKey || r7ActiveDocumentKey.value,
          })
        }
        if (name === 'r7-callback-status') {
          const syncedKey = syncEditingDocumentKeyForAttachmentItem(file, ctx.elementId)
          if (syncedKey) {
            r7ActiveDocumentKey.value = syncedKey
          }
        }
        if (name === 'onRequestClose') {
          handleR7EditorDialogClose()
          return
        }
        if (name === 'onError') {
          const code =
            payload && typeof payload === 'object' && 'errorCode' in payload
              ? String((payload as { errorCode?: unknown }).errorCode)
              : ''
          if (code === '-4' || code === '4') {
            elementApi.verificationError.value = t('attachments.r7DsLoadFailed')
          }
        }
      },
    },
  )
}

/** Инициализация DocEditor, когда контейнер отрендерен. */
const handleR7EditorDialogShown = async () => {
  if (!r7EditorAwaitingShown.value) return
  const file = r7EditorTargetFile.value
  const ctx = r7EditorOpenContext.value
  if (!file || !ctx) return
  r7EditorAwaitingShown.value = false

  try {
    await runR7AttachmentEditorOpen(file, ctx)
  } catch (error) {
    const axiosMsg = (error as { response?: { data?: { message?: string } } })?.response?.data?.message
    const message =
      axiosMsg ||
      (error instanceof Error ? error.message : t('attachments.r7OpenFailed'))
    elementApi.verificationError.value = message
    r7EditorVisible.value = false
    attachmentR7Editor.destroyEditor()
  }
}

/** Открывает модальный редактор Р7 для выбранного Office-вложения. */
const handleFileAttachmentOpenR7Editor = async (file: FileItem) => {
  r7InlinePreview.suspendForEditor()
  r7InlinePreview.previewError.value = null

  const resolvedFile = fileAttachmentsElement.resolveAttachmentItemFromStore(file)
  if (!fileAttachmentsElement.canOpenR7ForFile(resolvedFile)) {
    elementApi.verificationError.value = t('attachments.r7Unavailable')
    return
  }

  const ctx = fileAttachmentsElement.resolveR7EditorOpenContext()
  if (!ctx) {
    elementApi.verificationError.value = t('attachments.r7NoSession')
    return
  }

  const fileName = String(
    resolvedFile.fields.fileName?.value ?? resolvedFile.fields.name?.value ?? 'document.docx',
  )
  r7EditorTitle.value = fileName
  r7EditorTargetFile.value = resolvedFile
  r7EditorOpenContext.value = ctx
  elementApi.verificationError.value = null
  r7EditorAwaitingShown.value = true
  r7EditorVisible.value = true
}

const handleFileAttachmentDownload = async (file: FileItem) => {
  if (isAttachmentDownloadForbidden(file)) return
  if (file.file instanceof File) {
    downloadBlobToFile(file.file, file.file.name)
    return
  }

  try {
    const blob = await fileAttachmentsElement.fetchAttachmentBlobForFile(file)
    if (!blob) return
    const fileName = String(file.fields.fileName?.value ?? file.fields.name?.value ?? 'unknown')
    downloadBlobToFile(blob, fileName)
    downloadSignatures(file.fields, fileName)
  } catch (error) {
    console.error('[DOWNLOAD_ATTACHMENT] Download failed', error)
  }
}

const downloadBlobToFile = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.style.display = 'none'
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}

const downloadSignatures = (fields: any, fallbackName: string) => {
  const rows = fields.signature_file_list?.value?.rows || []
  for (const row of rows) {
    const sigData = row.fields?.signatureData?.value
    if (!sigData) continue
    const sigFileName = row.fields?.signatureFileName?.value || `${fallbackName}.sig`
    try {
      const bytes = Uint8Array.from(
        atob(sigData.replace(/\r?\n/g, '')),
        (c: string) => c.charCodeAt(0)
      )
      downloadBlobToFile(new Blob([bytes]), sigFileName)
    } catch (e) {
      console.error('[DOWNLOAD_ATTACHMENT] Sig download error:', e)
    }
  }
}

const handleSaveSetting = <ParamKey extends keyof ElementSettingsParams>({
                                                                           paramName,
                                                                           paramValue,
                                                                         }: {
  paramName: ParamKey
  paramValue: ElementSettingsParams[ParamKey]
}) => {
  if (paramName && paramValue !== undefined) {
    const formId = elementContext?.formId || props.formPath?.formId
    const pageId = elementContext?.pageId
    const panelId = elementContext?.panelId || props.formPath?.panelId
    const elId = elementContext?.elementId || elementId.value

    if (formId && pageId && panelId && elId) {
      setElementSetting(formId, pageId, panelId, elId, paramName, paramValue)
    }
  }
}

const getSavedSetting = <ParamKey extends keyof ElementSettingsParams>(
  paramName: ParamKey,
): ElementSettingsParams[ParamKey] | null => {
  if (paramName) {
    const formId = elementContext?.formId || props.formPath?.formId
    const pageId = elementContext?.pageId
    const panelId = elementContext?.panelId || props.formPath?.panelId
    const elId = elementContext?.elementId || elementId.value

    if (formId && pageId && panelId && elId) {
      return getElementSetting(
        formId,
        pageId,
        panelId,
        elId,
        paramName,
      ) as ElementSettingsParams[ParamKey]
    }
  }
  return null
}

watch(tableKey, (newKey, oldKey) => {
  if (elementApi.type.value !== 'TREETABLE') return
  if (!pendingTreeTableSearchTargetKey.value || newKey === oldKey) return
  const tableName = elementApi.fieldName.value
  if (!tableName) return
  // Стейл-указатель на старый focus инвалидируется, если критерий поиска
  // сменился с момента установки pending (новый ввод / Очистить / новый Найти).
  const currentLive = getTreeTableLiveCriteriaKey(
    (pageFields.value || {}) as Record<string, unknown>,
    tableName,
  )
  if (currentLive !== pendingTreeTableSearchTargetCriteriaKey.value) {
    pendingTreeTableSearchTargetKey.value = null
    return
  }
  const ticket = ++treeTableSearchRefocusTicket
  void (async () => {
    await nextTick()
    await nextTick()
    if (ticket !== treeTableSearchRefocusTicket) return
    await reassertTreeTableSearchPathInData()
    await reapplyPendingTreeTableSearchFocus('tableKey')
  })()
})

watch(treeTableSearchDataFingerprint, (fp, prevFp) => {
  if (elementApi.type.value !== 'TREETABLE') return
  if (!pendingTreeTableSearchTargetKey.value || !lastTreeTableSearchPathRows.value.length) return
  if (fp === prevFp) return
  const tableName = elementApi.fieldName.value
  if (!tableName) return
  const currentLive = getTreeTableLiveCriteriaKey(
    (pageFields.value || {}) as Record<string, unknown>,
    tableName,
  )
  if (currentLive !== pendingTreeTableSearchTargetCriteriaKey.value) {
    pendingTreeTableSearchTargetKey.value = null
    return
  }
  const ticket = ++treeTableSearchRefocusTicket
  void (async () => {
    await nextTick()
    await nextTick()
    if (ticket !== treeTableSearchRefocusTicket) return
    await reassertTreeTableSearchPathInData()
    await reapplyPendingTreeTableSearchFocus('data-fingerprint')
  })()
})

/** при возврате на вкладку реестра — снимок pagesData, без reload. */
const RESTORE_FROM_PAGE_SNAPSHOT_TYPES = new Set([
  'TABLE',
  'TREETABLE',
  'EDITABLE',
  'GROUPTABLE',
  'LISTBOX',
  'COMBOBOX',
  'MULTISELECT',
  'DATE',
  'DATETIME',
])

const restoreElementWhenPageSlotActivated = () => {
  const slot = String(pageDataSlot.value ?? '').trim()
  const cur = String(appStore.currentPageId ?? '').trim()
  if (!slot || !cur || cur !== slot) return

  const type = elementApi.type.value
  if (!RESTORE_FROM_PAGE_SNAPSHOT_TYPES.has(type)) return

  const fieldName = elementApi.fieldName.value
  const pf = pageFields.value as Record<string, unknown>
  if (!fieldName) return

  if (type === 'LISTBOX' || type === 'COMBOBOX') {
    const scalarEntry = pf[fieldName]
    const listboxEntry = pf[`${fieldName}__listbox`]
    if (listboxEntry !== undefined) {
      handleRefill({ [`${fieldName}__listbox`]: listboxEntry })
    }
    if (scalarEntry !== undefined && !isListboxCatalogLikeValue(scalarEntry)) {
      handleRefill({ [fieldName]: scalarEntry })
    } else {
      commitListboxScalarFromPageFields()
    }
    return
  }

  const keys =
    type === 'MULTISELECT'
      ? [fieldName]
      : [fieldName, `${fieldName}__listbox`, `${fieldName}__items`, `${fieldName}__action`]
  let entry: unknown
  for (const key of keys) {
    if (key in pf) {
      entry = pf[key]
      break
    }
  }
  if (entry === undefined) return
  handleRefill({ [fieldName]: entry })
}

watch(
  () => [appStore.currentPageId, pageDataSlot.value] as const,
  () => {
    void nextTick(() => restoreElementWhenPageSlotActivated())
  },
)

// Регистрация до mount дочернего MSelector — store заполняется из pageFields до автовыбора.
onBeforeMount(() => {
  initializeElement()
})

onMounted(() => {
  void reloadEmptyTableElementOnMount({
    elementType: elementApi.type.value,
    elementConfig: props.config,
    fieldName: elementApi.fieldName.value,
    pageFields: pageFields.value as Record<string, any> | undefined,
    reload: elementApi.reload,
  })
  if (elementApi.type.value === 'LISTBOX' || elementApi.type.value === 'COMBOBOX') {
    const currentValue = extractListboxScalarValue(elementApi.value.value)
    if (isListboxEmptyValue(currentValue) || isListboxCatalogLikeValue(currentValue)) {
      commitListboxScalarFromPageFields()
    }
  }
  if (elementApi.type.value === 'BOOLEAN') {
    syncBooleanValueFromPageFields()
  }
})
</script>

<style scoped>
.element-renderer {
  width: 100%;
}

.element-renderer:has(.modern-table--fill),
.element-renderer:has(.modern-tree-table--fill) {
  display: flex;
  flex-direction: column;
  flex: 1 1 0%;
  min-height: calc(var(--platform-table-fill-min-body, 120px) + 2.75rem);
  height: 100%;
  overflow: hidden;
}

.element-renderer-label {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  min-height: 24px;
  padding: 4px 0;
  color: black;
  line-height: 1.2;
  overflow-wrap: break-word;
  white-space: normal;
  word-wrap: break-word;
}

.element-renderer-label--align-left {
  justify-content: flex-start;
  text-align: left;
}

.element-renderer-label--align-center {
  justify-content: center;
  text-align: center;
}

.element-renderer-label--align-right {
  justify-content: flex-end;
  width: 100%;
  min-width: 0;
  text-align: right;
}

.element-content-wrapper:has(.element-actions-right) .element-renderer-label--align-right span {
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

label:not(:empty) {
  padding-bottom: 8px;
  display: block;
  font-weight: bold;
  color: #374151 !important;
}

.element-renderer.has-custom-styles {
  font-family: var(--element-font-family, inherit) !important;
  font-size: var(--element-font-size, inherit) !important;
  font-weight: var(--element-font-weight, inherit) !important;
  font-style: var(--element-font-style, inherit) !important;
  color: var(--element-color, inherit) !important;
  background-color: var(--element-bg-color, transparent) !important;
}

.element-renderer.has-custom-styles :deep(input),
.element-renderer.has-custom-styles :deep(textarea),
.element-renderer.has-custom-styles :deep(select),
.element-renderer.has-custom-styles :deep(.p-inputtext),
.element-renderer.has-custom-styles :deep(.p-textarea),
.element-renderer.has-custom-styles :deep(.p-select),
.element-renderer.has-custom-styles :deep(.p-autocomplete-input),
.element-renderer.has-custom-styles :deep(.p-autocomplete-input-multiple),
.element-renderer.has-custom-styles :deep(.p-dropdown),
.element-renderer.has-custom-styles :deep(.p-multiselect) {
  font-family: var(--element-font-family, inherit) !important;
  font-size: var(--element-font-size, inherit) !important;
  font-weight: var(--element-font-weight, inherit) !important;
  font-style: var(--element-font-style, inherit) !important;
  color: var(--element-color, inherit) !important;
  background-color: var(--element-bg-color, #ffffff) !important;
}

.element-renderer.has-custom-styles :deep(label),
.element-renderer.has-custom-styles :deep(.p-floatlabel label),
.element-renderer.has-custom-styles :deep(.m-label) {
  font-family: var(--element-font-family, inherit) !important;
  font-size: var(--element-font-size, inherit) !important;
  font-weight: var(--element-font-weight, inherit) !important;
  font-style: var(--element-font-style, inherit) !important;
  color: var(--element-color, #374151) !important;
}

.element-renderer.has-custom-styles :deep(.p-checkbox-label),
.element-renderer.has-custom-styles :deep(.p-radiobutton-label) {
  font-family: var(--element-font-family, inherit) !important;
  font-size: var(--element-font-size, inherit) !important;
  font-weight: var(--element-font-weight, inherit) !important;
  font-style: var(--element-font-style, inherit) !important;
  color: var(--element-color, inherit) !important;
}

.element-renderer.remove-text-field-border :deep(input),
.element-renderer.remove-text-field-border :deep(textarea),
.element-renderer.remove-text-field-border :deep(select),
.element-renderer.remove-text-field-border :deep(.p-inputtext),
.element-renderer.remove-text-field-border :deep(.p-inputtextarea),
.element-renderer.remove-text-field-border :deep(.p-textarea),
.element-renderer.remove-text-field-border :deep(.p-select),
.element-renderer.remove-text-field-border :deep(.p-dropdown),
.element-renderer.remove-text-field-border :deep(.p-multiselect),
.element-renderer.remove-text-field-border :deep(.p-autocomplete-input),
.element-renderer.remove-text-field-border :deep(.p-autocomplete-input-multiple) {
  border: none !important;
  box-shadow: none !important;
}
</style>
